// Characters — route /characters
//
// Characters domain (style/look catalog + async GPU generation). The page
// exposes model/LoRA browsers, a generated-character gallery, and one editor
// panel for both new character creation and selected-character revision.

import { useMemo, useRef, useState } from 'react'
import {
  Camera,
  Check,
  ChevronDown,
  Clapperboard,
  Dice5,
  ExternalLink,
  Eye,
  EyeOff,
  Flame,
  ImageOff,
  Loader2,
  Move,
  Package,
  Palette,
  RefreshCw,
  Shirt,
  Smile,
  Trash2,
  UserRound,
  Users,
  Wand2,
  X,
} from 'lucide-react'
import { ApiError } from '@/api/client'
import type {
  Character,
  CharacterAspect,
  CharacterStatus,
  KreaLoraCatalog,
  KreaLoraStyle,
  Style,
} from '@/api/types'
import {
  useCharacters,
  useCreateCharacter,
  useDeleteCharacter,
  useKreaLoras,
  useRetryCharacter,
  useStyles,
  useUpdateCharacter,
} from '@/hooks/useCharacters'
import { cn, formatTime } from '@/lib/utils'
import type { BadgeVariant } from '@/admin-design/_common'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormRow,
  Input,
  Pagination,
  Select,
  Textarea,
} from '@/admin-design/_common'

const STATUS_VARIANT: Record<CharacterStatus, BadgeVariant> = {
  draft: 'muted',
  generating: 'info',
  ready: 'success',
  error: 'destructive',
}

const ASPECT_OPTIONS: { label: string; value: CharacterAspect }[] = [
  { label: 'Portrait (3:4)', value: 'portrait' },
  { label: 'Landscape (4:3)', value: 'landscape' },
  { label: 'Square (1:1)', value: 'square' },
]

const GALLERY_STATUS_OPTIONS: { label: string; value: 'all' | CharacterStatus }[] = [
  { label: '전체 상태', value: 'all' },
  { label: '실패만', value: 'error' },
  { label: '생성 중', value: 'generating' },
  { label: '완료', value: 'ready' },
  { label: '초안', value: 'draft' },
]

const GALLERY_PAGE_SIZE_OPTIONS = [
  { label: '12개씩', value: '12' },
  { label: '24개씩', value: '24' },
  { label: '48개씩', value: '48' },
  { label: '96개씩', value: '96' },
]

type PromptGenderLock = 'auto' | 'female' | 'male'

type PromptLocks = {
  gender: PromptGenderLock
  age: string
}

const PROMPT_GENDER_OPTIONS: { label: string; value: PromptGenderLock }[] = [
  { label: '자동', value: 'auto' },
  { label: '여성', value: 'female' },
  { label: '남성', value: 'male' },
]

type VisualFeatureSource = {
  id: string
  label: string
  href: string
  note: string
  modes: ('realistic' | 'anime')[]
  options: { label: string; prompt: string }[]
}

const VISUAL_FEATURE_SOURCES: VisualFeatureSource[] = [
  {
    id: 'synthetic-characters',
    label: 'Synthetic Characters',
    href: 'https://huggingface.co/datasets/AbstractPhil/synthetic-characters',
    note: '완결형 외형/헤어/복장 프리셋',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Editorial heroine',
        prompt:
          'sleek dark chocolate hair swept into a low chignon, luminous olive skin with sculpted cheekbones, confident poised expression, structured ivory blazer over a silk camisole, gold statement earrings, chin lifted with editorial poise, soft directional studio key light with a gentle rim glow',
      },
      {
        label: 'Street casual',
        prompt:
          'tousled honey-blonde shoulder-length waves, sun-kissed freckled complexion, easy relaxed half-smile, oversized washed denim jacket over a graphic tee, a canvas tote on the shoulder, hands in pockets leaning against a brick wall, bright overcast daylight',
      },
      {
        label: 'Studio profile',
        prompt:
          'cropped jet-black pixie cut with a soft undercut, cool porcelain skin, calm neutral gaze in three-quarter profile, minimalist charcoal turtleneck, a subtle silver stud earring, still upright posture, clean seamless grey backdrop with soft even lighting',
      },
      {
        label: 'Fantasy traveler',
        prompt:
          'windswept auburn braid threaded with leather cord, weathered freckled skin, watchful adventurous expression, layered wool cloak over a linen tunic and leather bracers, a worn satchel across the chest, standing on a windswept ridge, cool golden dusk light',
      },
      {
        label: 'Creative worker',
        prompt:
          'messy chestnut top-knot with loose strands, warm tan complexion, focused thoughtful look, paint-flecked denim apron over a mustard sweater, sleeves pushed up, one hand resting on a worktable, warm tungsten workshop glow',
      },
      {
        label: 'Curator',
        prompt:
          'silver-streaked slicked-back hair, refined fair skin with fine laugh lines, serene composed expression, tailored slate wool suit with a thin lapel pin, round tortoiseshell glasses, hands clasped behind the back, soft museum spotlight',
      },
      {
        label: 'Night-shift lead',
        prompt:
          'a dark low ponytail with a few flyaways, steady tired eyes with subtle under-eye shadow, matte olive skin, navy utility jumpsuit with rolled cuffs, an id badge clipped to the chest, arms crossed, cool fluorescent overhead light with a teal cast',
      },
      {
        label: 'Documentarian',
        prompt:
          'short salt-and-pepper curls, sun-worn tanned skin with crinkled squint lines, attentive curious expression, faded field vest over a chambray shirt, a camera strap slung across the body, crouched slightly with alert posture, harsh midday sun',
      },
      {
        label: 'Academic',
        prompt:
          'neat side-parted ash-brown hair, pale freckled complexion, quietly earnest expression, corduroy blazer with elbow patches over a knit vest, wire-rimmed glasses, holding a leather-bound notebook, warm library reading-lamp light',
      },
      {
        label: 'Athlete',
        prompt:
          'sweat-damp dark hair pulled back tight, deep bronze skin with a defined muscular build, determined intense gaze, moisture-wicking performance tank and fitted shorts, a towel over one shoulder, dynamic athletic stance, crisp bright stadium light',
      },
      {
        label: 'Musician',
        prompt:
          'long wavy platinum hair with dark roots, cool pale skin with a dusting of glitter, moody smoldering expression, distressed leather jacket over a mesh top, layered silver chains, leaning back cradling a guitar, saturated magenta stage light',
      },
      {
        label: 'Chef',
        prompt:
          'a close-cropped dark fade, warm brown skin beaded with kitchen heat, focused steady expression, crisp double-breasted white chef jacket with rolled sleeves, a navy apron and side towel, plating with precise hands, warm pass-line kitchen light',
      },
      {
        label: 'Florist',
        prompt:
          'a soft strawberry-blonde braid crowned with tiny blooms, rosy fair skin with light freckles, gentle warm smile, linen pinafore over a floral blouse, hands cradling a bundle of eucalyptus and ranunculus, dappled greenhouse daylight',
      },
      {
        label: 'Architect',
        prompt:
          'a sharp asymmetric bob in ink black, cool medium skin, precise analytical expression, monochrome minimalist tunic with wide trousers, a slim drafting pen tucked at the collar, standing beside a scale model, bright diffused north-facing studio light',
      },
    ],
  },
  {
    id: 'complete-persona-realistic',
    label: 'Complete Personas',
    href: 'https://huggingface.co/datasets/AbstractPhil/synthetic-characters',
    note: '한 줄로 완성되는 인물 콘셉트',
    modes: ['realistic'],
    options: [
      {
        label: 'Vinyl shop owner',
        prompt:
          'a seasoned vinyl record shop owner, unruly grey-flecked curls and a trimmed beard, deep laugh lines, a vintage band tee under a worn corduroy jacket, thumbing through dusty record crates, warm amber shop light over floor-to-ceiling record shelves, shallow depth of field',
      },
      {
        label: 'Rooftop mixologist',
        prompt:
          'a confident rooftop-bar mixologist, slicked dark undercut and a neat mustache, rolled white shirtsleeves with a leather apron and a pocket of bar tools, shaking a cocktail with a practiced flourish, city-skyline bokeh at blue hour behind lit bottles, cinematic teal-and-amber glow',
      },
      {
        label: 'Marine biologist',
        prompt:
          'a weathered marine biologist, salt-crusted sandy hair and a sunburnt nose, a half-zipped wetsuit over a rash guard, a dive slate and mask in hand, crouched on a research-boat deck against the open sea, bright coastal daylight with sea spray',
      },
      {
        label: 'Tattoo artist',
        prompt:
          'a heavily tattooed tattoo artist, jet-black hair shaved on one side, a septum ring and bold sleeve ink, a black tank and nitrile gloves gripping a tattoo machine, focused under a bright work lamp in a studio of flash art, moody directional light',
      },
      {
        label: 'Ballet instructor',
        prompt:
          'a poised ballet instructor, a sleek dark bun and a long elegant neck, a fitted wrap cardigan over a leotard and soft warmers, one hand resting on the barre in a graceful stance, a sunlit dance studio with tall mirrors and a sprung wood floor, soft window light',
      },
      {
        label: 'Motorcycle courier',
        prompt:
          'an urban motorcycle courier, wind-mussed dark hair and a scar through one brow, a scuffed leather jacket with a crossbody messenger bag, helmet tucked under an arm beside a parked cafe-racer, a rain-slick neon-lit street at night, reflective wet-asphalt highlights',
      },
      {
        label: 'Perfumer',
        prompt:
          'a refined perfumer, silver hair in a low twist and a serene focused gaze, an ivory lab coat over a silk blouse, holding a smelling strip to the nose amid amber glass bottles on a marble counter, soft diffused atelier light, a delicate warm palette',
      },
      {
        label: 'Bookshop keeper',
        prompt:
          'a gentle vintage bookshop keeper, soft grey waves and half-moon reading glasses on the nose tip, a knit cardigan over a linen shirt, cradling an old clothbound book among towering wooden stacks, dusty golden afternoon light through a shop window',
      },
      {
        label: 'E-sports pro',
        prompt:
          'a sharp e-sports pro, a dyed teal undercut and an intense competitive stare, a team jersey with a headset around the neck, hands poised over a mechanical keyboard, an rgb-lit gaming arena glowing behind, cool blue-and-violet ambient light',
      },
      {
        label: 'Jazz vocalist',
        prompt:
          'a sultry jazz vocalist, finger-waved vintage hair and bold red lips, a shimmering satin gown with long gloves, leaning into a vintage ribbon microphone with eyes half-closed, a smoky dim club with a single warm spotlight, deep noir shadows',
      },
      {
        label: 'Ceramicist',
        prompt:
          'an earthy ceramicist, a clay-dusted chestnut bun and rolled sleeves, a mud-streaked linen apron over a simple tee, hands cupped around a spinning pot on the wheel, a sunlit studio lined with drying bowls, soft warm natural light',
      },
      {
        label: 'Storm chaser',
        prompt:
          'a rugged storm chaser, windblown dark hair and squinting alert eyes, a weathered field jacket over a flannel with a radio clipped on, standing beside an instrument-topped truck under a churning supercell sky, dramatic stormy backlight and ominous grey clouds',
      },
      {
        label: 'Sommelier',
        prompt:
          'an elegant sommelier, neat swept-back hair and a discerning calm expression, a crisp charcoal suit with a tastevin on a chain, swirling a glass of red against candlelit wine-cellar racks, warm low cellar light and rich burgundy tones',
      },
      {
        label: 'Indie director',
        prompt:
          'a thoughtful indie film director, tousled hair under a knit beanie and wire-frame glasses, an oversized flannel over a worn tee with a viewfinder around the neck, framing a shot with raised hands on a scrappy set, moody practical lighting and a hazy atmosphere',
      },
    ],
  },
  {
    id: 'face-caption',
    label: 'FaceCaption / HumanCaption',
    href: 'https://huggingface.co/OpenFace-CQUPT',
    note: '얼굴/피부/헤어 디테일',
    modes: ['realistic'],
    options: [
      {
        label: 'Double-lidded almond',
        prompt:
          'close-up beauty portrait, an oval face with defined double eyelids and large almond eyes, smooth even fair skin, softly arched brows, a delicate straight nose, rosy tinted lips, subtle glowing dewy makeup, loose face-framing chestnut layers, soft frontal beauty light',
      },
      {
        label: 'Monolid soft',
        prompt:
          'a serene portrait, a round face with smooth monolid eyes and a gentle upturned gaze, a warm ivory complexion with soft cheeks, straight tapered brows, a low button nose, muted mauve lips, minimal natural makeup, sleek center-parted black hair, even soft daylight',
      },
      {
        label: 'Hooded smoky',
        prompt:
          'a striking portrait, an angular face with deep-set hooded eyes, cool fair skin, strong straight brows, defined cheekbones, a bronzed smoky eye and a nude lip, a sharp contoured nose, dark tousled hair swept to one side, moody low-key side light',
      },
      {
        label: 'Upturned fox',
        prompt:
          'a sleek portrait, a heart-shaped face with upturned fox-like eyes and a sly expression, luminous skin, thin high-arched brows, a slender nose, glossy coral lips, sharp winged liner, a slicked-back dark ponytail, crisp editorial light',
      },
      {
        label: 'Freckled sun',
        prompt:
          'a sun-kissed portrait, an oval face scattered with warm freckles across the nose and cheeks, golden tan skin, soft feathered brows, bright wide eyes, a small upturned nose, bare glossy lips, tousled copper waves, warm natural sunlight',
      },
      {
        label: 'Beauty mark',
        prompt:
          'an elegant portrait, a diamond face with a single beauty mark beside the lip, smooth pale skin, softly defined brows, calm hooded eyes, high cheekbones, matte rose lips, classic understated makeup, sleek dark hair in a low twist, soft glamour light',
      },
      {
        label: 'Dimpled smile',
        prompt:
          'a warm portrait, a round face with deep cheek dimples and a bright genuine smile, healthy tan skin, natural full brows, crinkled smiling eyes, a soft nose, glossy lips revealing an easy grin, bouncy shoulder-length curls, cheerful bright light',
      },
      {
        label: 'Sharp jawline',
        prompt:
          'a sculptural portrait, a square face with a sharp defined jawline and chiseled cheekbones, cool medium skin, straight bold brows, intense narrow eyes, a strong straight nose, neutral matte lips, short slicked dark hair, dramatic contour light',
      },
      {
        label: 'Aegyo-sal glow',
        prompt:
          'a youthful glowing portrait, a soft oval face with pronounced aegyo-sal under bright rounded eyes, dewy translucent skin, straight soft brows, a petite nose, gradient tinted lips, glass-skin makeup, airy layered brown hair, bright clean beauty light',
      },
      {
        label: 'High cheekbones',
        prompt:
          'a refined portrait, a long face with high prominent cheekbones and a slender straight nose, porcelain skin, elegantly arched brows, cool almond eyes, a subtle rosy blush, satin nude lips, hair pulled back sleek, soft sculpting light',
      },
      {
        label: 'Full matte lips',
        prompt:
          'a bold portrait, an oval face with full pouty lips in a matte terracotta finish, warm bronze skin, feathered brushed-up brows, deep expressive eyes, defined cheekbones, minimal eye makeup, voluminous dark curls framing the face, soft directional light',
      },
      {
        label: 'Dewy porcelain',
        prompt:
          'a luminous portrait, a soft round face with flawless dewy porcelain skin, a faint natural blush, gently arched brows, clear bright eyes, a delicate nose, sheer glossy lips, a bare-skin glow, silky straight black hair, bright diffused light',
      },
      {
        label: 'Bold brows',
        prompt:
          'a confident portrait, a heart-shaped face with thick bold straight brows framing the eyes, warm olive skin, deep-set expressive eyes, a defined nose, matte berry lips, subtle contour, dark hair parted in the middle, clean studio light',
      },
      {
        label: 'Soft round',
        prompt:
          'a gentle portrait, a soft round face with rounded cheeks and a warm approachable expression, fair rosy skin, soft rounded brows, wide friendly eyes, a small nose, natural pink lips, a fluffy fringe and soft waves, tender window light',
      },
    ],
  },
  {
    id: 'reid-attributes',
    label: 'ReID Attributes',
    href: 'https://huggingface.co/datasets/adonaivera/fiftyone-multiview-reid-attributes',
    note: '전신/복장/소품 조합',
    modes: ['realistic'],
    options: [
      {
        label: 'Beige coat combo',
        prompt:
          'full-body view, a long beige wool coat over a cream turtleneck, tapered brown trousers, tan leather ankle boots, a structured camel handbag, a thin gold necklace, straight shoulder-length brown hair, a relaxed standing stance, soft daylight',
      },
      {
        label: 'Red hoodie set',
        prompt:
          'full-body view, an oversized red pullover hoodie, black jogger sweatpants, white chunky sneakers, a grey nylon backpack, a black baseball cap, a dark low ponytail, weight resting on one leg, bright overcast light',
      },
      {
        label: 'Navy blazer look',
        prompt:
          'full-body view, a fitted navy blazer over a white blouse, high-waisted charcoal slacks, black pointed loafers, a slim leather crossbody, small silver hoop earrings, a sleek center-parted black bob, upright confident posture, clean even light',
      },
      {
        label: 'Floral dress walk',
        prompt:
          'full-body view, a flowing knee-length floral midi dress, strappy tan sandals, a woven straw tote, a wide-brim sun hat, delicate layered bracelets, loose beachy blonde waves, a mid-stride walking pose, warm golden sunlight',
      },
      {
        label: 'Denim double',
        prompt:
          'full-body view, a light-wash denim jacket over a white ribbed tank, dark straight-leg jeans, white canvas low-tops, a small tan belt bag, round tortoiseshell sunglasses, a tousled auburn shag cut, hands tucked in pockets, bright street light',
      },
      {
        label: 'Grey sportswear',
        prompt:
          'full-body view, a heather-grey zip-up track jacket, matching jogger pants, neon running shoes, a slim crossbody phone pouch, a fitness band on the wrist, a high sporty ponytail, a dynamic stretching stance, crisp morning light',
      },
      {
        label: 'Trench monochrome',
        prompt:
          'full-body view, a belted stone-grey trench coat over an all-black outfit, sleek black trousers, black chelsea boots, a structured top-handle bag, a silk neck scarf, slicked-back dark hair, a poised standing pose, cool urban light',
      },
      {
        label: 'Yellow raincoat',
        prompt:
          'full-body view, a glossy bright-yellow hooded raincoat, dark cropped rain trousers, black rubber rain boots, a waterproof backpack, a clear umbrella in hand, a damp fringe peeking from the hood, standing under drizzle, muted rainy light',
      },
      {
        label: 'Plaid shirt jeans',
        prompt:
          'full-body view, an open red-and-black plaid flannel over a plain grey tee, faded blue jeans, brown lace-up work boots, a canvas duffel over the shoulder, a leather cuff bracelet, wavy shoulder-length brown hair, an easygoing lean, warm afternoon light',
      },
      {
        label: 'Business grey',
        prompt:
          'full-body view, a tailored light-grey suit with a pale blue shirt and no tie, brown oxford shoes, a slim leather briefcase, a minimalist watch, neatly combed dark hair, a professional upright stance, bright office light',
      },
      {
        label: 'Black leather set',
        prompt:
          'full-body view, a cropped black leather biker jacket over a black tee, skinny black jeans, studded ankle boots, a small quilted chain bag, chunky silver rings, jet-black blunt-cut hair, a confident hip-cocked pose, dramatic side light',
      },
      {
        label: 'Pastel knit skirt',
        prompt:
          'full-body view, a soft lilac cropped cardigan over a white cami, a pleated mint midi skirt, white mary-jane flats, a small pastel shoulder bag, a pearl hair clip, soft wavy light-brown hair, a gentle demure stance, airy soft light',
      },
      {
        label: 'Cargo utility',
        prompt:
          'full-body view, an olive oversized utility shirt-jacket over a black tank, wide cargo pants with pockets, chunky trail sneakers, a tactical sling bag, a beanie and a thin chain, a dark shag mullet, a relaxed wide stance, cool overcast light',
      },
      {
        label: 'White summer set',
        prompt:
          'full-body view, a breezy white linen short-sleeve shirt, cream tailored shorts, tan espadrilles, a rattan crossbody, round gold sunglasses, sun-lightened tousled hair, a casual summer stroll pose, bright warm sunlight',
      },
    ],
  },
  {
    id: 'fashionpedia-deepfashion',
    label: 'Fashionpedia / DeepFashion',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '의상 category/attribute 조합',
    modes: ['realistic'],
    options: [
      {
        label: 'Tailored suit',
        prompt:
          'a sharply tailored double-breasted suit in charcoal wool, structured shoulders and peak lapels, a crisp white cotton shirt, slim tapered trousers with a clean break, polished leather oxfords, a monochrome tonal palette, refined confident posture, soft studio light',
      },
      {
        label: 'Trench coat',
        prompt:
          'a classic belted trench coat in stone gabardine with a storm flap and buckled cuffs, worn open over a fine-knit ecru sweater and tailored trousers, tan leather boots, a warm neutral palette, hands resting in the coat pockets, moody overcast light',
      },
      {
        label: 'Slip dress',
        prompt:
          'a bias-cut satin slip dress in dusty rose, delicate spaghetti straps and a cowl neckline, sheer draped fabric catching the light, layered fine gold chains, strappy heels, a soft romantic palette, a graceful relaxed stance, warm ambient glow',
      },
      {
        label: 'Oversized knit',
        prompt:
          'a chunky oversized cable-knit sweater in oatmeal wool, slouchy dropped shoulders, layered over faded relaxed-fit jeans, cozy wool socks and suede boots, a soft earthy palette, a curled-up comfortable pose, warm cozy light',
      },
      {
        label: 'Leather biker',
        prompt:
          'a cropped black leather biker jacket with asymmetric zips and quilted panels, over a graphic band tee, ripped black skinny jeans, buckled combat boots, a hardware-heavy edgy palette, a hip-cocked attitude stance, dramatic contrasty light',
      },
      {
        label: 'Cocktail dress',
        prompt:
          'a fitted little black cocktail dress in structured crepe, a sweetheart neckline and a nipped waist, sheer opera gloves, sparkling drop earrings, sleek stiletto heels, an elegant monochrome palette, a poised evening pose, soft glamour spotlight',
      },
      {
        label: 'Modern hanbok',
        prompt:
          'a modernized hanbok with a cropped jeogori in blush silk and a voluminous high-waisted skirt in deep jade, subtle floral embroidery and a delicate goreum ribbon, clean minimal lines, a soft pastel palette, graceful upright posture, gentle diffused light',
      },
      {
        label: 'Modern qipao',
        prompt:
          'a contemporary qipao in wine-red silk brocade, a mandarin collar and a side slit, subtle metallic floral jacquard, a sleek tailored fit, a small clutch, a rich jewel-tone palette, an elegant composed stance, warm directional light',
      },
      {
        label: 'Techwear street',
        prompt:
          'layered urban techwear, a matte black waterproof shell with taped seams and utility straps, a tactical harness vest, tapered jogger cargos with buckles, chunky trail sneakers, an all-black functional palette, a dynamic guarded stance, cool blue-lit night',
      },
      {
        label: 'Athleisure set',
        prompt:
          'a coordinated athleisure set, a fitted seamless sports top and high-waist leggings in sage, an unzipped lightweight windbreaker, minimalist running shoes, a slim crossbody, a fresh sporty palette, a mid-stretch active pose, bright morning light',
      },
      {
        label: 'Cottagecore dress',
        prompt:
          'a romantic cottagecore prairie dress in cream cotton with a tiny floral print, puff sleeves and a ruffled square neckline, a knit shawl and a woven basket, a delicate soft palette, standing among tall grass, dreamy golden-hour light',
      },
      {
        label: 'Punk layered',
        prompt:
          'layered punk streetwear, a studded black leather jacket over a torn fishnet and a slogan tee, a tartan mini kilt with chains, ripped tights and heavy platform boots, a chaotic monochrome-and-red palette, a defiant stance, harsh flash light',
      },
      {
        label: 'Bohemian flow',
        prompt:
          'flowing bohemian layers, a tiered maxi skirt in earthy paisley, a crochet crop top and a fringed suede vest, stacked beaded bracelets and a floppy felt hat, a warm terracotta palette, a free-spirited swaying pose, hazy sunset light',
      },
      {
        label: 'Avant runway',
        prompt:
          'an avant-garde runway look, a sculptural asymmetric gown in structured metallic organza, exaggerated architectural draping and bold volume, minimal sleek styling, a futuristic monochrome palette, a dramatic editorial pose, hard directional light',
      },
      {
        label: 'Vintage seventies',
        prompt:
          'seventies vintage style, a rust corduroy flared jumpsuit with a wide pointed collar, a suede fringe jacket, platform boots and round tinted sunglasses, a warm autumnal palette, a relaxed retro stance, sun-faded film-grain light',
      },
      {
        label: 'Y2k revival',
        prompt:
          'y2k revival styling, a fitted baby-tee and low-rise flared cargos, a shiny quilted mini bag and butterfly hair clips, tinted rectangular sunglasses and layered chokers, a candy pastel palette, a playful pose, bright flash-lit glow',
      },
    ],
  },
  {
    id: 'human-caption-hq',
    label: 'HumanCaption HQ',
    href: 'https://huggingface.co/datasets/OpenFace-CQUPT/HumanCaption-HQ',
    note: '인물 인상/환경 묘사',
    modes: ['realistic'],
    options: [
      {
        label: 'Cafe window',
        prompt:
          'a contemplative person sitting by a rain-streaked cafe window, a warm knit sweater and a steaming mug cradled in both hands, a soft wistful expression gazing outside, a cozy wooden interior with hanging plants, warm amber interior light against grey daylight',
      },
      {
        label: 'Rainy crosswalk',
        prompt:
          'a composed figure pausing at a busy crosswalk in the rain, a dark trench and a clear umbrella overhead, a calm distant expression, blurred neon reflections shimmering on wet asphalt, a cool cinematic city-night mood',
      },
      {
        label: 'Golden field',
        prompt:
          'a serene person standing in a swaying golden wheat field at sunset, a light linen shirt fluttering in the breeze, eyes closed with a peaceful smile, an endless warm horizon behind, glowing backlit golden-hour light',
      },
      {
        label: 'Neon alley',
        prompt:
          'a cool confident figure leaning in a narrow neon-soaked alley, a slick jacket with reflective accents, an intense sidelong glance, glowing pink and cyan signage overhead, a moody rain-damp cyberpunk atmosphere',
      },
      {
        label: 'Library corner',
        prompt:
          'a quiet studious person curled in a corner of an old library, a soft cardigan and an open book, an absorbed focused expression, towering wooden shelves and dust motes in the air, a warm reading-lamp glow',
      },
      {
        label: 'Seaside pier',
        prompt:
          'a relaxed person walking along a weathered wooden pier at the coast, a breezy shirt and rolled trousers, a carefree windswept look, a calm turquoise sea and a pale sky beyond, bright airy seaside light',
      },
      {
        label: 'Rooftop dusk',
        prompt:
          'a thoughtful figure standing at a rooftop railing at dusk, a casual jacket over a tee, gazing at the glittering city skyline, hair lifting in the evening wind, a soft purple-and-orange twilight glow',
      },
      {
        label: 'Snowy street',
        prompt:
          'a cozy bundled person walking a quiet snow-covered street, a wool coat, scarf and mittens, cheeks flushed pink with breath fogging the air, soft falling snow and blurred warm shop lights, cool blue winter light',
      },
      {
        label: 'Subway platform',
        prompt:
          'a pensive person waiting alone on an empty subway platform, a worn backpack and earbuds in, a tired faraway gaze, tiled walls and a lit tunnel receding behind, cool fluorescent underground light',
      },
      {
        label: 'Autumn park',
        prompt:
          'a gentle person strolling through an autumn park, an oversized knit and a light scarf, a soft content smile amid falling leaves, golden and crimson trees lining the path, warm dappled afternoon light',
      },
      {
        label: 'Studio loft',
        prompt:
          'a creative person perched on a stool in a sunlit studio loft, paint-splattered overalls and rolled sleeves, a candid mid-thought expression, canvases and brushes scattered around exposed brick, bright soft north-window light',
      },
      {
        label: 'Market morning',
        prompt:
          'a lively person browsing an open-air morning market, a canvas tote and a casual jacket, a warm curious smile amid stalls of produce and flowers, bustling colorful surroundings softly blurred, fresh bright morning light',
      },
    ],
  },
  {
    id: 'pedestrian-attributes',
    label: 'PETA / RAP Attributes',
    href: 'https://github.com/dangweili/pedestrian-attribute-recognition-pytorch',
    note: '보행자 attribute/소지품',
    modes: ['realistic'],
    options: [
      {
        label: 'Backpack commuter',
        prompt:
          'a candid full-body street shot, a fitted grey jacket over a navy top, dark slim jeans, white sneakers, a black backpack on both shoulders, earbuds in, walking briskly along a sidewalk, natural daytime street light',
      },
      {
        label: 'Umbrella walker',
        prompt:
          'a candid full-body street shot, a beige raincoat over a maroon sweater, cropped trousers, waterproof boots, a folded tote under one arm, holding a navy umbrella overhead, striding through light rain, soft grey overcast light',
      },
      {
        label: 'Handbag waiting',
        prompt:
          'a candid full-body street shot, a camel coat over a cream blouse, black tailored pants, low heels, a structured brown handbag on the forearm, standing still and checking a watch, waiting on a corner, muted afternoon light',
      },
      {
        label: 'Shopping bags',
        prompt:
          'a candid full-body street shot, a denim jacket over a white tee, a floral skirt, flat sandals, several paper shopping bags in both hands, a small crossbody, walking home laden with purchases, bright late-day light',
      },
      {
        label: 'Phone checker',
        prompt:
          'a candid full-body street shot, an olive hoodie over joggers, chunky sneakers, a sling bag across the chest, a cap pulled low, paused mid-step looking down at a phone, a casual urban setting, flat daylight',
      },
      {
        label: 'Jogging pass',
        prompt:
          'a candid full-body street shot, a bright performance tee and black running shorts, cushioned running shoes, a slim armband and earbuds, a water bottle in hand, jogging along a park path, crisp clear morning light',
      },
      {
        label: 'Coffee stroll',
        prompt:
          'a candid full-body street shot, a soft grey knit over dark jeans, ankle boots, a shoulder bag, a takeaway coffee cup in hand, strolling unhurried down a tree-lined street, warm gentle daylight',
      },
      {
        label: 'Bus stop wait',
        prompt:
          'a candid full-body street shot, a puffer jacket over a plaid shirt, straight-leg jeans, worn sneakers, a duffel bag by the feet, leaning against a bus-stop post looking up the road, cool cloudy light',
      },
      {
        label: 'Crosswalk stride',
        prompt:
          'a candid full-body street shot, a tan trench over a black turtleneck, slim trousers, loafers, a slim briefcase in one hand, striding across a marked crosswalk mid-step, bright midday city light',
      },
      {
        label: 'Window browse',
        prompt:
          'a candid full-body street shot, a pastel cardigan over a white dress, mary-jane flats, a small handbag, pausing to look into a shop window with a curious tilt, a hand near the glass, soft warm storefront light',
      },
      {
        label: 'Bench sitter',
        prompt:
          'a candid full-body street shot, a checked overshirt over a tee, relaxed chinos, canvas shoes, a backpack set beside on the bench, sitting and glancing to the side, a park bench setting, dappled afternoon light',
      },
      {
        label: 'Suitcase traveler',
        prompt:
          'a candid full-body street shot, a comfortable oversized sweater over leggings, slip-on sneakers, a crossbody travel pouch, wheeling a hard-shell suitcase, a neck pillow looped on the handle, walking through a transit hall, bright indoor light',
      },
    ],
  },
  {
    id: 'cinematic-archetypes',
    label: 'Cinematic Archetypes',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '역할/장면성이 강한 캐릭터 hook',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Neon Hacker',
        prompt:
          'neon-lit hacker in a cramped server den, hooded techwear jacket with glowing circuit trim, face washed in cyan and magenta screen glow, fingerless gloves, intense focused stare, reflected code across a visor, moody cyberpunk atmosphere',
      },
      {
        label: 'Rain-soaked Detective',
        prompt:
          'rain-soaked noir detective under a flickering streetlamp, damp trench coat and loosened tie, dripping fedora brim, curling cigarette smoke, weary knowing gaze, wet asphalt reflections, chiaroscuro amber and blue',
      },
      {
        label: 'Desert Wanderer',
        prompt:
          'sun-scorched desert wanderer wrapped in dusty layered robes and a weathered face scarf, sand-dusted skin, squinting against harsh light, distant dunes and shimmering heat haze, canteen at the hip, stoic resilient expression, warm ochre palette',
      },
      {
        label: 'Corporate Assassin',
        prompt:
          'sleek corporate assassin in a tailored charcoal suit, concealed shoulder holster, cold composed expression, glass skyscraper skyline at dusk, slicked-back hair, precise controlled posture, low-key steel-blue lighting',
      },
      {
        label: 'Cottage Witch',
        prompt:
          'cottage witch among hanging dried herbs and candlelight, layered linen shawl and an amulet pendant, tousled hair with small braids, gentle mysterious half-smile, warm hearth glow, drifting dust motes, cozy earthy tones',
      },
      {
        label: 'Retro Astronaut',
        prompt:
          'retro-futurist astronaut in a cream analog spacesuit with chunky toggles and mission patches, bubble helmet tucked under one arm, hopeful upward gaze, vintage control-panel glow, sixties sci-fi palette of orange and teal',
      },
      {
        label: 'Street Racer',
        prompt:
          'street racer leaning against a tuned import car under sodium garage lights, cropped bomber jacket and racing gloves, keys spinning on one finger, cocky grin, drifting tire-smoke haze, saturated night-neon reflections',
      },
      {
        label: 'Cyberpunk Medic',
        prompt:
          'cyberpunk field medic in a reinforced utility vest with glowing red-cross patches, satchel of vials, augmented forearm interface, alert compassionate look, rain-slick alley backdrop, teal and crimson lighting',
      },
      {
        label: 'Noir Chanteuse',
        prompt:
          'noir chanteuse at a smoky jazz club, satin evening gown and long gloves, vintage microphone close to the lips, soft spotlight on the face, half-lidded sultry gaze, deep velvet shadows, warm golden and burgundy tones',
      },
      {
        label: 'Post-apoc Scavenger',
        prompt:
          'post-apocalyptic scavenger in patched layered gear with a gas mask pushed up and goggles on the forehead, dust-streaked face, scavenged pack and improvised tools, wary determined eyes, desaturated wasteland palette, harsh overcast light',
      },
      {
        label: 'Festival Dancer',
        prompt:
          'festival dancer mid-celebration in flowing embroidered garments and jangling ornaments, flower crown and streaked face paint, joyful open-mouthed laugh, drifting confetti and string lights, vibrant saturated colors, dynamic golden dusk',
      },
      {
        label: 'Samurai Duelist',
        prompt:
          'samurai duelist in a windblown layered kimono and hakama, one hand resting on a sheathed katana, calm razor focus, falling cherry blossoms, misty dojo courtyard, cinematic side light, muted crimson and ink tones',
      },
      {
        label: 'Gallery Thief',
        prompt:
          'elegant gallery thief in a fitted black catsuit and slim harness, tiny earpiece, poised on a marble staircase, sly confident smirk, laser-grid glow and moonlight, cool blue museum shadows',
      },
      {
        label: 'Storm Mage',
        prompt:
          'storm mage on a cliff edge with crackling arcs of lightning swirling around raised hands, wind-lashed robes and glowing runic sigils, hair whipping wildly, fierce commanding gaze, roiling stormclouds, electric violet and slate palette',
      },
    ],
  },
  {
    id: 'subculture-wardrobe',
    label: 'Subculture Wardrobe',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '개성 강한 옷차림/무드',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Dark Academia',
        prompt:
          'dark academia styling, tweed blazer over a knit vest and oxford shirt, pleated wool trousers, leather satchel, muted browns and forest green, pensive scholarly composure, warm library light',
      },
      {
        label: 'Techwear',
        prompt:
          'urban techwear layering, matte black waterproof shell with taped seams and utility straps, cargo joggers and chunky sneakers, chest harness rig, monochrome black palette, cool aloof attitude, overcast city backdrop',
      },
      {
        label: 'Gorpcore',
        prompt:
          'gorpcore outdoor styling, boxy fleece under a colorful nylon shell jacket, hiking pants and trail runners, carabiner accents, earthy greens and burnt orange, easygoing adventurous vibe, crisp alpine daylight',
      },
      {
        label: 'Y2K',
        prompt:
          'y2k throwback outfit, low-rise flared jeans and a baby tee, tinted wraparound sunglasses, butterfly clips and chrome accessories, glossy pink and silver palette, bubbly playful confidence, mall photo-booth energy',
      },
      {
        label: 'Cottagecore',
        prompt:
          'cottagecore ensemble, floral prairie dress with puff sleeves and a linen apron, straw hat and woven basket, soft pastel and cream tones, gentle wholesome warmth, sun-dappled meadow light',
      },
      {
        label: 'Punk',
        prompt:
          'punk styling, studded leather jacket over a torn band tee, ripped tartan trousers and scuffed combat boots, safety-pin details, bold liberty-spike hair, black and blood-red palette, defiant snarling attitude',
      },
      {
        label: 'Goth',
        prompt:
          'goth aesthetic, layered black lace and velvet, silver ankh and choker, dark lipstick and heavy eyeliner, corseted silhouette, cool moonlit palette, brooding elegant melancholy',
      },
      {
        label: 'Mod',
        prompt:
          'sixties mod styling, sharp geometric mini shift dress or a slim tailored suit, bold op-art patterns, sleek bob or crisp side part, monochrome with a single primary-color pop, confident poise, retro studio light',
      },
      {
        label: 'Biker',
        prompt:
          'biker styling, cropped leather moto jacket with zips and patches, distressed denim and heavy boots, bandana and aviators, oil-slick black and chrome palette, rugged rebellious swagger, dusty roadside light',
      },
      {
        label: 'Preppy',
        prompt:
          'preppy country-club look, crisp polo with a sweater tied over the shoulders, chinos or a pleated skirt, boat shoes, navy pastel and white palette, poised cheerful confidence, bright coastal daylight',
      },
      {
        label: 'Harajuku',
        prompt:
          'harajuku street fashion, clashing layered pastels and bold graphic prints, oversized bows and platform shoes, candy-colored accessories, kawaii maximalist palette, bubbly expressive energy, colorful Tokyo lane',
      },
      {
        label: 'Western Cowboy',
        prompt:
          'western cowboy styling, fringed suede jacket and pearl-snap shirt, wide-brim hat and worn boots, tooled leather belt, dusty tan and denim tones, easy rugged confidence, golden prairie light',
      },
      {
        label: 'Military Surplus',
        prompt:
          'military surplus styling, olive field jacket with rolled sleeves and dog tags, cargo utility trousers and lace-up boots, canvas webbing, faded khaki and drab palette, stoic no-nonsense demeanor, gritty overcast light',
      },
      {
        label: 'Rave Cyber',
        prompt:
          'cyber rave outfit, holographic crop top and neon mesh sleeves, chunky platform boots and UV accessories, goggles and glowing kandi cuffs, electric neon palette, ecstatic high-energy vibe, blacklight glow',
      },
      {
        label: 'Vintage 50s',
        prompt:
          'vintage fifties styling, swing dress with a cinched waist and petticoat, cat-eye sunglasses and a silk headscarf, red lipstick, pastel polka-dot palette, sweet retro charm, sunny diner backdrop',
      },
      {
        label: 'Avant-garde',
        prompt:
          'avant-garde couture, sculptural asymmetric silhouette with dramatic folds and exaggerated shoulders, deconstructed monochrome fabric, bold architectural lines, editorial palette, striking aloof intensity, stark studio light',
      },
    ],
  },
  {
    id: 'scene-energy',
    label: 'Scene Energy',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '배경/감정/빛을 한 번에 바꾸는 scene hook',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Neon Night Market',
        prompt:
          'neon night market glow, tangled signage and steam rising from food stalls, reflections in wet pavement, curious wandering mood, saturated magenta and cyan light',
      },
      {
        label: 'Golden-hour Rooftop',
        prompt:
          'golden-hour city rooftop, warm sun grazing skin and hair, distant skyline haze, breezy carefree calm, honeyed amber backlight and soft lens flare',
      },
      {
        label: 'Rainy Window Cafe',
        prompt:
          'rainy-day window cafe, streaked raindrops on the glass and soft interior lamplight, a steaming cup nearby, cozy wistful mood, muted grey-blue tones with warm accents',
      },
      {
        label: 'Snowfield Dusk',
        prompt:
          'open snowfield at dusk, pale drifting flakes and long blue shadows, breath fogging in the cold air, serene quiet solitude, cool lavender and white palette',
      },
      {
        label: 'Sunlit Studio',
        prompt:
          'bright minimalist studio, clean seamless backdrop and airy diffused daylight, fresh confident calm, crisp high-key white and soft pastel tones',
      },
      {
        label: 'Moody Warehouse',
        prompt:
          'abandoned warehouse interior, dusty shafts of light through high windows, weathered concrete and steel, brooding introspective mood, dramatic chiaroscuro grey palette',
      },
      {
        label: 'Seaside Cliff',
        prompt:
          'windswept seaside cliff, crashing waves and salt spray below, wide open horizon, free adventurous spirit, cool ocean blues under bright overcast light',
      },
      {
        label: 'Autumn Forest Path',
        prompt:
          'autumn forest path, drifting golden leaves and dappled sunlight through branches, crisp still air, nostalgic peaceful mood, warm amber and russet tones',
      },
      {
        label: 'Midnight Subway',
        prompt:
          'empty midnight subway platform, cold fluorescent tubes and tiled walls, faint motion blur of a passing train, lonely urban stillness, teal and sodium-yellow contrast',
      },
      {
        label: 'Festival Crowd Bokeh',
        prompt:
          'night festival crowd, blurred string lights and glowing lanterns behind, warm celebratory energy, joyful lively mood, golden bokeh and soft haze',
      },
      {
        label: 'Foggy Pier',
        prompt:
          'misty morning pier over still water, weathered wooden posts fading into fog, muffled quiet atmosphere, contemplative lonely mood, desaturated cool grey palette',
      },
      {
        label: 'Candlelit Interior',
        prompt:
          'candlelit interior at night, flickering warm flames and deep soft shadows, intimate hushed mood, glowing amber and umber tones',
      },
    ],
  },
  {
    id: 'motion-beats',
    label: 'Motion Beats',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '멈춰 선 포즈가 아니라 행동 중인 순간',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Sprint Start',
        prompt:
          'explosive sprint start, weight driving forward off the back foot, arms pumping and torso low, hair streaming back, tense focused drive, frozen split-second of motion',
      },
      {
        label: 'Mid-spin Twirl',
        prompt:
          'mid-spin twirl, skirt and hair flaring outward with centrifugal pull, one arm sweeping wide, delighted open expression, dynamic rotational blur, caught mid-turn',
      },
      {
        label: 'Jacket Flip',
        prompt:
          'flipping a jacket over one shoulder mid-stride, fabric snapping through the air, confident glance to the side, loose relaxed swagger, frozen instant of the toss',
      },
      {
        label: 'Hair Toss',
        prompt:
          'sharp hair toss, strands fanning up and outward in an arc, head tilted back, playful liberated grin, energetic flick captured in motion',
      },
      {
        label: 'Jumping Mid-air',
        prompt:
          'suspended mid-jump, both feet off the ground and knees tucked, arms flung upward, exhilarated laughing face, weightless frozen leap',
      },
      {
        label: 'Sitting Onto Stool',
        prompt:
          'caught in the act of sitting down onto a tall stool, weight shifting back and one hand steadying, relaxed casual glance, natural candid mid-motion pose',
      },
      {
        label: 'Reaching Upward',
        prompt:
          'reaching upward on tiptoe toward something just out of frame, spine and arm fully extended, hopeful focused expression, elongated dynamic line, frozen stretch',
      },
      {
        label: 'Turning To Look Back',
        prompt:
          'turning to glance back over one shoulder mid-walk, torso twisting and hair swinging, curious inviting look, caught mid-rotation',
      },
      {
        label: 'Laughing Mid-step',
        prompt:
          'laughing mid-step, head thrown back and one foot lifting off the ground, a hand rising toward the face, genuine unguarded joy, candid walking motion',
      },
      {
        label: 'Throwing Punch',
        prompt:
          'throwing a straight punch toward the camera, fist and shoulder driving forward with the body rotating, fierce focused snarl, dynamic combat instant frozen',
      },
      {
        label: 'Dancing Sway',
        prompt:
          'swaying mid-dance, hips and shoulders counter-rotating, arms flowing loosely overhead, blissful eyes-closed expression, fluid rhythmic motion captured',
      },
      {
        label: 'Catching Object',
        prompt:
          'lunging to catch a falling object, body stretched and hands cupped ready, wide alert eyes, split-second anticipation frozen',
      },
      {
        label: 'Wind-blown Stride',
        prompt:
          'striding into a strong wind, coat and hair streaming back and one hand shielding the face, leaning purposefully forward, resolute expression, gusting motion frozen',
      },
      {
        label: 'Crouched Ready Stance',
        prompt:
          'low crouched ready stance, weight coiled on the balls of the feet and one hand grazing the ground, sharp alert focus, poised tension before a spring',
      },
      {
        label: 'Arms Overhead Stretch',
        prompt:
          'stretching both arms high overhead, spine arching and chest lifting, relaxed contented eyes-closed expression, languid waking-up motion captured',
      },
      {
        label: 'Leaning Off Balance',
        prompt:
          'leaning far off balance to one side, arms flung out to counterweight and one foot lifting, surprised laughing expression, playful precarious instant frozen',
      },
    ],
  },
  {
    id: 'camera-drama',
    label: 'Camera Drama',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '렌즈/프레이밍으로 분위기 전환',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Mirror Shot',
        prompt:
          'reflection composition in a mirror, subject and mirrored self framed together, layered depth and a doubled gaze, introspective mood, soft even light',
      },
      {
        label: 'Shallow 85mm',
        prompt:
          'eighty-five millimeter portrait with razor-shallow depth of field, tack-sharp eyes and a creamy melted background, flattering compression, intimate focused framing, soft natural light',
      },
      {
        label: 'Wide 24mm Environmental',
        prompt:
          'wide twenty-four millimeter environmental portrait, subject set within an expansive scene, sweeping context and dramatic perspective, immersive storytelling framing, natural depth',
      },
      {
        label: 'Backlit Rim Silhouette',
        prompt:
          'strong backlight carving a glowing rim around the silhouette, face in gentle shadow, hair haloed in light, dreamy atmospheric haze, moody contrast',
      },
      {
        label: 'Top-down Flat Lay',
        prompt:
          'top-down overhead portrait lying flat, face centered and gaze up into the lens, symmetric graphic composition, surrounding props framing the head, even soft light',
      },
      {
        label: 'Through-glass Reflection',
        prompt:
          'shot through a rain-streaked window, subject behind the glass with overlaid reflections and droplets, layered dreamy separation, pensive mood, diffused light',
      },
      {
        label: 'Split Lighting',
        prompt:
          'hard split lighting cutting the face into equal light and shadow halves, a sharp edge along the nose, intense enigmatic mood, high-contrast monochrome feel',
      },
      {
        label: 'Neon Color Gel',
        prompt:
          'dual neon color gels washing the face in clashing magenta and cyan, glossy highlights and colored shadows, electric nightlife mood, bold saturated contrast',
      },
      {
        label: 'Chiaroscuro',
        prompt:
          'renaissance chiaroscuro, a single soft key light emerging from deep darkness, luminous face against black, sculpted shadow and painterly gradation, solemn mood',
      },
      {
        label: 'Golden Lens Flare',
        prompt:
          'sun flaring into the lens at golden hour, streaking blooms and warm haze across the frame, glowing backlit skin, nostalgic radiant mood, soft focus edges',
      },
      {
        label: 'Macro Detail',
        prompt:
          'extreme macro framing on the eyes and lashes, ultra-fine skin and iris texture, intimate abstract crop, delicate soft light and tight focus',
      },
      {
        label: 'Dutch Tilt Tension',
        prompt:
          'canted dutch-angle framing tilting the horizon, unsettling dynamic tension, subject pressed into a corner of the frame, edgy dramatic mood, hard directional light',
      },
      {
        label: 'Telephoto Compression',
        prompt:
          'long telephoto compression flattening the layers, subject stacked tight against a blurred background, isolated and monumental, cinematic distance, soft even light',
      },
      {
        label: 'Soft Window Bokeh',
        prompt:
          'seated by a large window with soft directional daylight, gentle bokeh sparkle behind, delicate falloff across the face, calm airy mood, natural pastel tones',
      },
    ],
  },
  {
    id: 'camera-angles',
    label: 'Camera Angles / Viewpoint',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '카메라 앵글·시점 라이브러리',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Front Eye-level',
        prompt:
          'camera dead front at eye level, symmetric head-on composition, direct engaged gaze into the lens, balanced neutral framing',
      },
      {
        label: 'Three-quarter View',
        prompt:
          'camera at a three-quarter angle, face and body turned partly away, dimensional flattering planes and a visible cheekbone line, dynamic balanced composition',
      },
      {
        label: 'Profile Side View',
        prompt:
          'pure side profile, camera perpendicular to the face, clean silhouette of nose brow and jaw, graphic edge-lit composition',
      },
      {
        label: 'Over-shoulder Back',
        prompt:
          'camera behind the subject looking over one shoulder, back and shoulder blades filling the frame, face turned partly toward the lens, mysterious layered composition',
      },
      {
        label: 'Low Hero Angle',
        prompt:
          'low hero angle shooting upward, subject towering against the sky, powerful dominant stance, dramatic converging lines, imposing composition',
      },
      {
        label: 'High Overhead Angle',
        prompt:
          'high angle looking down at the subject, foreshortened body and an upturned face, vulnerable intimate feel, sweeping downward composition',
      },
      {
        label: "Worm's-eye Upward",
        prompt:
          'extreme worm-eye view from the ground looking straight up, exaggerated towering perspective and a looming figure, dramatic sky backdrop, vertiginous composition',
      },
      {
        label: "Bird's-eye Downward",
        prompt:
          'top-down bird-eye view from straight overhead, subject flattened into a graphic shape below, surrounding floor negative space, striking map-like composition',
      },
      {
        label: 'Dutch Angle',
        prompt:
          'tilted dutch camera angle, canted horizon and diagonal framing, off-kilter energetic tension, dynamic unstable composition',
      },
      {
        label: 'Extreme Close-up',
        prompt:
          'extreme close-up filling the frame with the face, dominant eyes and lips, every micro-expression readable, intense intimate crop',
      },
      {
        label: 'Waist-up Medium',
        prompt:
          'waist-up medium shot, head and torso framed with room above, natural conversational distance, balanced portrait composition',
      },
      {
        label: 'Full-body Wide',
        prompt:
          'full-body wide shot from head to toe with surrounding space, complete silhouette and pose visible, environmental context, spacious composition',
      },
      {
        label: 'Cowboy Shot',
        prompt:
          'cowboy shot framed from mid-thigh up, confident stance with room for gesture, classic cinematic western framing, grounded balanced composition',
      },
      {
        label: 'First-person POV Hands',
        prompt:
          'first-person point of view looking down at one own outstretched hands and forearms, immersive subjective framing, foreground hands and receding perspective',
      },
      {
        label: 'From Below Looking Up',
        prompt:
          'camera low near the ground angled up at the face, chin and jaw emphasized against a bright sky, heroic upward composition, dramatic perspective',
      },
      {
        label: 'From Behind Forward',
        prompt:
          'camera positioned directly behind the subject facing the same direction, back of the head and shoulders in the foreground, gaze leading into the scene ahead, immersive composition',
      },
    ],
  },
  {
    id: 'pose-composition',
    label: 'Pose / Composition',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '포즈/프레이밍',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Full-body Front',
        prompt:
          'full-body frontal pose, weight evenly planted and shoulders squared, open confident stance, head-to-toe framing with balanced symmetry',
      },
      {
        label: 'Three-quarter Bust',
        prompt:
          'three-quarter bust framing, torso angled and shoulders staggered, chin slightly turned, elegant tapering neckline, tight flattering crop',
      },
      {
        label: 'Over-shoulder Glance',
        prompt:
          'body angled away with the head turning back over the shoulder, curved spine line, inviting glance to the lens, dynamic diagonal framing',
      },
      {
        label: 'Seated Editorial',
        prompt:
          'seated editorial pose on the floor, knees drawn up and one arm draped, elongated relaxed limbs, sculptural body lines, magazine-style framing',
      },
      {
        label: 'Walking Motion',
        prompt:
          'captured mid-walk with one leg forward and arms loosely swinging, natural forward lean, candid stride, full-length motion framing',
      },
      {
        label: 'Hands At Waist',
        prompt:
          'confident stance with both hands on the waist, elbows out and shoulders back, strong triangular silhouette, assertive waist-up framing',
      },
      {
        label: 'Leaning Wall',
        prompt:
          'casually leaning back against a wall, one shoulder to the surface and ankles crossed, relaxed diagonal body line, easygoing three-quarter framing',
      },
      {
        label: 'Hair Touch',
        prompt:
          'one hand lifted to sweep through the hair, elbow raised and neck exposed, soft graceful arm curve, intimate flattering close framing',
      },
      {
        label: 'Crossed Arms',
        prompt:
          'arms folded across the chest, weight on one hip and chin lowered slightly, grounded self-assured silhouette, sturdy waist-up framing',
      },
      {
        label: 'Contrapposto',
        prompt:
          'classical contrapposto stance, weight on one leg with hips and shoulders counter-tilted, graceful s-curve body line, sculptural full-body framing',
      },
      {
        label: 'Kneeling',
        prompt:
          'upright kneeling pose, back straight and hands resting on the thighs, calm composed vertical line, centered grounded framing',
      },
      {
        label: 'Crouching',
        prompt:
          'low crouch balanced on the balls of the feet, forearms resting on the knees, compact coiled silhouette, intimate ground-level framing',
      },
      {
        label: 'Reclining On Side',
        prompt:
          'reclining on one side propped on an elbow, long horizontal body line and gently bent legs, languid relaxed pose, sweeping wide framing',
      },
      {
        label: 'Hands Behind Head',
        prompt:
          'arms raised and crossed behind the head, chest open and elbows flared, relaxed dominant silhouette, dynamic upper-body framing',
      },
      {
        label: 'Hands In Pockets',
        prompt:
          'both hands tucked into pockets, shoulders loose and weight cocked to one side, effortless casual line, laid-back three-quarter framing',
      },
      {
        label: 'Twirl With Fabric',
        prompt:
          'mid-twirl with fabric swirling outward in a wide arc, spiraling dynamic body line, joyful movement, sweeping full-length framing',
      },
      {
        label: 'Back Arch Stretch',
        prompt:
          'gentle back arch with the chest lifted and arms reaching back, elongated graceful spine curve, expressive open line, dramatic full-body framing',
      },
      {
        label: 'Looking Up Dreamy',
        prompt:
          'head tilted up with a soft dreamy upward gaze, exposed throat and lifted chin, serene vertical line, delicate close framing',
      },
    ],
  },
  {
    id: 'gesture-framing',
    label: 'Gesture / Framing',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '손동작/카메라 구도',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Hand On Chin',
        prompt:
          'one hand cradling the chin with a curled finger along the jaw, thoughtful tilt of the head, close waist-up framing, contemplative gesture',
      },
      {
        label: 'Peace Sign',
        prompt:
          'flashing a peace sign beside the cheek, bright playful grin, hand held close to the face, cheerful tight portrait framing',
      },
      {
        label: 'Blowing Kiss',
        prompt:
          'blowing a kiss toward the lens, fingertips at pursed lips, a flirtatious sparkle in the eyes, intimate close framing',
      },
      {
        label: 'Adjusting Collar',
        prompt:
          'both hands adjusting the jacket collar, chin lifted with a self-assured look, polished editorial gesture, sharp waist-up framing',
      },
      {
        label: 'Holding Hat Brim',
        prompt:
          'one hand tilting the brim of a hat downward, shadow grazing the eyes, mysterious cool poise, cinematic close framing',
      },
      {
        label: 'Fingers In Hair',
        prompt:
          'fingers combing back through the hair, elbow raised and neck exposed, relaxed sensual gesture, soft close-up framing',
      },
      {
        label: 'Shading Eyes',
        prompt:
          'one hand raised to shade the eyes as if gazing into the distance, hopeful searching expression, dynamic upward framing',
      },
      {
        label: 'Waving',
        prompt:
          'waving warmly toward the lens with an open hand, bright welcoming smile, friendly candid framing with the gesture leading the composition',
      },
      {
        label: 'Pointing At Camera',
        prompt:
          'pointing directly at the lens with a confident smirk, arm extended into the foreground, bold engaging framing with forced perspective',
      },
      {
        label: 'Cupping Face',
        prompt:
          'one hand gently cupping the own cheek, head leaning into the palm, soft tender expression, cozy close framing',
      },
      {
        label: 'Hands Framing Face',
        prompt:
          'both hands framing the face along the jawline, wide bright eyes peeking between, playful graphic gesture, tight symmetric framing',
      },
      {
        label: 'Reaching Toward Lens',
        prompt:
          'one hand reaching out toward the lens with fingers spread, dramatic foreground hand and a receding face, immersive depth, dynamic framing',
      },
      {
        label: 'Finger On Lips',
        prompt:
          'a single finger pressed softly to the lips in a hush gesture, secretive twinkle in the eyes, intimate close framing',
      },
      {
        label: 'Clasped Hands',
        prompt:
          'hands clasped together near the chest, gentle hopeful expression, demure gathered posture, warm waist-up framing',
      },
    ],
  },
  {
    id: 'signature-props',
    label: 'Signature Props',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '캐릭터를 기억시키는 소품',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Vintage Camera',
        prompt:
          'holding a vintage film camera raised toward the eye, hands cradling the lens, curious creative focus, retro photographer vibe, warm nostalgic tones',
      },
      {
        label: 'Coffee Cup',
        prompt:
          'cradling a steaming coffee cup in both hands near the face, soft rising steam, cozy relaxed warmth, gentle morning light',
      },
      {
        label: 'Umbrella',
        prompt:
          'holding a translucent umbrella overhead in the rain, droplets sliding off the canopy, serene sheltered mood, cool rainy palette with soft highlights',
      },
      {
        label: 'Bouquet',
        prompt:
          'carrying a lush bouquet of wildflowers against the chest, petals brushing the chin, tender romantic softness, blush and green pastel tones',
      },
      {
        label: 'Headphones',
        prompt:
          'wearing large over-ear headphones with eyes half-closed, lost in the music, absorbed blissful mood, moody colored ambient light',
      },
      {
        label: 'Skateboard',
        prompt:
          'resting a skateboard upright with one hand on the deck, casual streetwear cool, laid-back urban attitude, gritty daylight backdrop',
      },
      {
        label: 'Katana',
        prompt:
          'holding a sheathed katana across the body with both hands, calm disciplined focus, poised warrior stillness, muted cinematic side light',
      },
      {
        label: 'Book Stack',
        prompt:
          'balancing a tall stack of worn books in the arms, peeking over the top, bookish curious charm, warm scholarly library tones',
      },
      {
        label: 'Guitar',
        prompt:
          'cradling an acoustic guitar with one hand on the neck, easy soulful expression, caught mid-strum, golden intimate light',
      },
      {
        label: 'Neon Sign',
        prompt:
          'leaning beside a glowing neon sign, face bathed in its colored glow, nonchalant nightlife cool, saturated magenta and blue reflections',
      },
      {
        label: 'Balloon Bunch',
        prompt:
          'clutching a bunch of pastel balloons drifting overhead, strings gathered in one hand, whimsical buoyant joy, bright airy palette',
      },
      {
        label: 'Polaroid Photos',
        prompt:
          'fanning out a handful of polaroid photos toward the lens, playful nostalgic grin, scattered instant snapshots, warm candid tones',
      },
    ],
  },
  {
    id: 'expression-drama',
    label: 'Expression Drama',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '단순 미소 대신 감정이 있는 표정',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Playful Smirk',
        prompt:
          'one eyebrow cocked, eyes glinting with amusement, lips curled into a lopsided smirk, teasing playful mood',
      },
      {
        label: 'Wistful Gaze',
        prompt:
          'softly unfocused eyes gazing past the lens, a faint downturned mouth, relaxed brow, gentle wistful longing',
      },
      {
        label: 'Fierce Glare',
        prompt:
          'narrowed intense eyes locked on the lens, jaw set and lips pressed thin, lowered furrowed brow, fierce defiant mood',
      },
      {
        label: 'Soft Laughter',
        prompt:
          'eyes crinkled and half-closed with warmth, mouth open in easy laughter, lifted cheeks and a relaxed brow, joyful genuine mood',
      },
      {
        label: 'Quiet Melancholy',
        prompt:
          'heavy-lidded downcast eyes, lips gently parted and drooping, faintly knitted brow, quiet aching melancholy',
      },
      {
        label: 'Surprised Wide Eyes',
        prompt:
          'eyes flung wide and round, mouth dropping open in a small gasp, raised arched brows, startled surprised mood',
      },
      {
        label: 'Confident Half-smile',
        prompt:
          'steady assured eyes meeting the lens, one corner of the mouth lifted in a subtle half-smile, calm even brow, self-possessed confident mood',
      },
      {
        label: 'Teary-eyed',
        prompt:
          'glistening welling eyes on the verge of tears, trembling pressed lips, delicately raised inner brows, tender vulnerable emotion',
      },
      {
        label: 'Sultry Look',
        prompt:
          'half-lowered smoldering eyes, lips slightly parted and soft, a subtly arched brow, alluring sultry mood',
      },
      {
        label: 'Mischievous Grin',
        prompt:
          'eyes sparkling with scheming delight, a wide crooked grin baring a hint of teeth, one raised brow, impish mischievous mood',
      },
      {
        label: 'Serene Calm',
        prompt:
          'softly closed or gently lowered eyes, lips resting in a faint peaceful curve, smooth untroubled brow, serene meditative calm',
      },
      {
        label: 'Determined Jaw',
        prompt:
          'focused unwavering eyes fixed ahead, mouth firm and jaw clenched with resolve, a level tensed brow, determined driven mood',
      },
      {
        label: 'Dreamy Faraway',
        prompt:
          'eyes drifting upward into a faraway dream, lips softly parted in a dreamy half-smile, relaxed lifted brow, whimsical wandering mood',
      },
      {
        label: 'Pouting',
        prompt:
          'eyes glancing up with a sulky flicker, lower lip pushed out in a pout, a slightly bunched brow, petulant charming mood',
      },
      {
        label: 'Cold Contempt',
        prompt:
          'eyes half-lidded with icy disdain, mouth curled into a faint sneer, one arched brow, cold contemptuous mood',
      },
      {
        label: 'Warm Tender',
        prompt:
          'soft glowing eyes full of affection, lips curved in a gentle tender smile, relaxed lifted brow, warm loving mood',
      },
    ],
  },
  {
    id: 'anigame-persona',
    label: 'AniGame PersonaCaps',
    href: 'https://huggingface.co/datasets/AbstractPhil/synthetic-characters',
    note: 'anime 캐릭터 외형',
    modes: ['anime'],
    options: [
      {
        label: 'magical girl',
        prompt:
          '1girl, magical girl, twin-tailed pink hair with ribbons, aqua eyes, frilly pastel dress, glowing star wand, crescent moon brooch, sparkling aura',
      },
      {
        label: 'armored knight',
        prompt:
          '1girl, knight, silver braided hair, steel-blue eyes, ornate plate armor, crested tabard, gauntlets, longsword at hip, resolute expression',
      },
      {
        label: 'pop idol',
        prompt:
          '1girl, pop idol, gradient purple bob, star-shaped pupils, glittering stage costume, wireless mic, glowstick, cheerful wink, spotlight glow',
      },
      {
        label: 'arcane mage',
        prompt:
          '1boy, mage, long white hair, violet eyes, star-embroidered robe, wide-brim pointed hat, wooden staff, floating spellbook, faint arcane glow',
      },
      {
        label: 'shrine maiden',
        prompt:
          '1girl, shrine maiden, long black hair, red eyes, white and red miko outfit, paper talismans, purification wand, serene calm expression',
      },
      {
        label: 'catgirl',
        prompt:
          '1girl, catgirl, orange bob hair, cat ears, cat tail, golden eyes, casual hoodie, bell collar, paw gloves, playful smirk',
      },
      {
        label: 'gothic princess',
        prompt:
          '1girl, gothic princess, long silver curls, crimson eyes, black lace gown, lace parasol, choker, elegant tiara, melancholy gaze',
      },
      {
        label: 'mecha pilot',
        prompt:
          '1boy, mecha pilot, spiky navy hair, sharp cyan eyes, sleek pilot flight suit, headset, glowing visor, cockpit harness, determined look',
      },
      {
        label: 'wandering swordsman',
        prompt:
          '1boy, swordsman, messy crimson hair, amber eyes, dark haori over kimono, drawn katana, battle scar, focused intense stare',
      },
      {
        label: 'alchemist',
        prompt:
          '1girl, alchemist, wavy chestnut hair, green eyes, leather apron over dress, goggles on forehead, glass vials, bubbling potion, curious grin',
      },
      {
        label: 'vampire noble',
        prompt:
          '1boy, vampire noble, sleek black hair, glowing red eyes, victorian tailcoat, high-collar cape, fangs, wine goblet, aristocratic smirk',
      },
      {
        label: 'fox spirit',
        prompt:
          '1girl, fox spirit, long golden hair, fox ears, nine tails, amber eyes, flowing kimono, floating fox fire, mischievous smile',
      },
      {
        label: 'star sailor',
        prompt:
          '1girl, star sailor, cosmic blue hair with star clips, glittering violet eyes, celestial sailor uniform, star staff, floating stardust, dreamy expression',
      },
      {
        label: 'elf archer',
        prompt:
          '1girl, elf archer, long emerald braid, pointed ears, forest-green eyes, leather ranger tunic, longbow, quiver of arrows, feather earring, keen gaze',
      },
    ],
  },
  {
    id: 'complete-persona-anime',
    label: 'Complete Anime Personas',
    href: 'https://huggingface.co/datasets/AbstractPhil/synthetic-characters',
    note: '한 줄로 완성되는 anime 콘셉트',
    modes: ['anime'],
    options: [
      {
        label: 'spring schoolgirl',
        prompt:
          '1girl, long sky-blue hair with white ribbon, teal eyes, sailor school uniform, star-shaped hairpin, gentle smile, cherry blossom courtyard, soft spring light',
      },
      {
        label: 'shonen brawler',
        prompt:
          '1boy, spiky orange hair, golden eyes, torn martial arts gi, bandaged fists, fierce grin, crackling battle aura, rocky arena background',
      },
      {
        label: 'battle mage',
        prompt:
          '1girl, silver twin braids, purple eyes, ornate mage robe, crystal pendant, confident smirk, floating magic circles, ancient library background',
      },
      {
        label: 'cyberpunk runner',
        prompt:
          '1boy, sleek black undercut, cybernetic red eye, neon-lined jacket, holographic visor, cool stare, rain-soaked neon city background',
      },
      {
        label: 'gothic lolita',
        prompt:
          '1girl, flowing lavender hair, pink eyes, frilly gothic lolita dress, black bonnet, shy expression, rose garden background, dreamy bokeh',
      },
      {
        label: 'cafe maid',
        prompt:
          '1girl, short teal bob, amber eyes, pastel maid outfit, lace headband, cheerful wink, cozy cafe interior, warm afternoon light',
      },
      {
        label: 'samurai wanderer',
        prompt:
          '1boy, long white ponytail, blue eyes, samurai armor over kimono, sheathed katana, calm resolve, misty bamboo forest background',
      },
      {
        label: 'adventurer heroine',
        prompt:
          '1girl, wavy crimson hair, emerald eyes, adventurer leather armor, worn map, adventurous grin, sunlit cliffside overlooking the ocean',
      },
      {
        label: 'magical heroine',
        prompt:
          '1girl, glowing pink twin-tails, cyan eyes, magical girl frilled dress, heart-topped wand, determined pose, sparkling starry sky background',
      },
      {
        label: 'everyday teen',
        prompt:
          '1boy, messy brown hair, hazel eyes, casual hoodie and backpack, headphones around neck, relaxed smile, city rooftop at sunset',
      },
      {
        label: 'ace pilot',
        prompt:
          '1girl, ash-grey long hair, red eyes, sleek pilot suit, glowing wristband, focused expression, futuristic hangar with mech silhouette',
      },
      {
        label: 'kimono maiden',
        prompt:
          '1girl, dark navy hair in a bun, golden eyes, elegant floral kimono, ornate hair comb, serene smile, autumn shrine steps background',
      },
      {
        label: 'royal prince',
        prompt:
          '1boy, platinum blond hair, violet eyes, ornate royal prince coat, jeweled brooch, poised confidence, grand palace ballroom background',
      },
      {
        label: 'track athlete',
        prompt:
          '1girl, mint-green short hair, orange eyes, sporty track uniform, whistle, energetic grin, sunny stadium track background',
      },
    ],
  },
  {
    id: 'danbooru-sfw',
    label: 'Danbooru SFW Tags',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: 'anime tag 조합 (SFW)',
    modes: ['anime'],
    options: [
      {
        label: 'blonde schoolgirl',
        prompt:
          '1girl, long hair, blonde hair, blue eyes, sailor collar, school uniform, hair ribbon, smile, blush, simple background',
      },
      {
        label: 'pink catgirl',
        prompt:
          '1girl, short hair, pink hair, green eyes, hoodie, cat ears, choker, closed mouth, white background',
      },
      {
        label: 'spiky delinquent',
        prompt:
          '1boy, spiky hair, black hair, red eyes, jacket, fingerless gloves, serious, grin, simple background',
      },
      {
        label: 'silver twintails',
        prompt:
          '1girl, twintails, silver hair, purple eyes, frilled dress, ribbon, hair bow, open mouth, gradient background',
      },
      {
        label: 'casual bookworm',
        prompt:
          '1girl, medium hair, brown hair, amber eyes, hoodie, glasses, headphones, smile, simple background',
      },
      {
        label: 'kimono redhead',
        prompt:
          '1girl, long hair, red hair, green eyes, kimono, floral print, hair flower, gentle smile, white background',
      },
      {
        label: 'white-haired boy',
        prompt:
          '1boy, medium hair, white hair, blue eyes, coat, scarf, closed eyes, calm, gradient background',
      },
      {
        label: 'orange ponytail',
        prompt:
          '1girl, ponytail, orange hair, yellow eyes, sportswear, wristband, headband, open mouth, smile, simple background',
      },
      {
        label: 'purple gothic',
        prompt:
          '1girl, wavy hair, purple hair, pink eyes, gothic lolita, bonnet, lace, frown, dark background',
      },
      {
        label: 'blue sailor',
        prompt:
          '1girl, short hair, blue hair, red eyes, sailor uniform, neckerchief, hairclip, blush, closed mouth, simple background',
      },
      {
        label: 'green mage boy',
        prompt:
          '1boy, long hair, green hair, gold eyes, robe, hood, earrings, smirk, simple background',
      },
      {
        label: 'braided student',
        prompt:
          '1girl, twin braids, black hair, brown eyes, cardigan, glasses, ribbon, shy, blush, white background',
      },
      {
        label: 'idol gradient',
        prompt:
          '1girl, long hair, gradient hair, aqua eyes, idol costume, gloves, star hairpin, wink, colorful background',
      },
      {
        label: 'blond casual',
        prompt:
          '1boy, short hair, blond hair, blue eyes, hoodie, headphones, backpack, neutral expression, simple background',
      },
    ],
  },
  {
    id: 'anime-caption-danbooru',
    label: 'Anime Caption Danbooru SFW',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: 'caption형 anime 묘사',
    modes: ['anime'],
    options: [
      {
        label: 'cheerful schoolgirl',
        prompt:
          'a cheerful anime girl with long flowing blonde hair, bright blue eyes, wearing a crisp school uniform, smiling warmly at the viewer, soft classroom lighting',
      },
      {
        label: 'cool city boy',
        prompt:
          'a cool anime boy with messy black hair and sharp red eyes, dressed in a dark leather jacket, arms crossed, standing confidently under city lights',
      },
      {
        label: 'cozy tea girl',
        prompt:
          'a gentle anime girl with short pink hair and green eyes, wearing a cozy oversized hoodie, holding a warm cup of tea, relaxed and content expression',
      },
      {
        label: 'graceful dancer',
        prompt:
          'a graceful anime girl with long silver hair tied in twintails, violet eyes, wearing an elegant frilled dress, twirling gracefully with a soft smile',
      },
      {
        label: 'determined fighter',
        prompt:
          'a determined anime boy with spiky orange hair and golden eyes, wearing a torn martial arts uniform, clenching his fists with a fierce grin',
      },
      {
        label: 'serene kimono',
        prompt:
          'a serene anime girl with dark hair in a neat bun, amber eyes, wearing a floral kimono, holding a paper fan, calm and elegant demeanor',
      },
      {
        label: 'dreamy gothic',
        prompt:
          'a dreamy anime girl with wavy lavender hair and pink eyes, wearing a gothic lolita dress, gazing softly into the distance among blooming roses',
      },
      {
        label: 'lively runner',
        prompt:
          'a lively anime girl with a mint-green ponytail and orange eyes, wearing a sporty track uniform, mid-stride with an energetic bright smile',
      },
      {
        label: 'mysterious cloak',
        prompt:
          'a mysterious anime boy with long white hair and glowing blue eyes, wearing a flowing dark cloak, a faint smirk playing on his lips',
      },
      {
        label: 'bright idol',
        prompt:
          'a bright anime idol girl with gradient pink twintails and cyan eyes, wearing a sparkling stage costume, winking cheerfully under stage lights',
      },
      {
        label: 'curious alchemist',
        prompt:
          'a curious anime girl with chestnut waves and green eyes, wearing a leather apron and goggles, examining a glowing potion with wide fascinated eyes',
      },
      {
        label: 'calm reader',
        prompt:
          'a calm anime boy with neat brown hair and hazel eyes, wearing a casual cardigan, reading a book quietly by a sunlit window',
      },
    ],
  },
  {
    id: 'anime-genre-hooks',
    label: 'Anime Genre Hooks',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '장르별 anime 주인공 hook',
    modes: ['anime'],
    options: [
      {
        label: 'shonen fighter',
        prompt:
          '1boy, shonen protagonist, spiky black hair, fiery orange eyes, tattered fighting gi, bandaged hands, raised clenched fist, burning fighting-spirit aura, dynamic pose',
      },
      {
        label: 'shojo lead',
        prompt:
          '1girl, shojo heroine, soft pink shoulder-length hair, sparkling brown eyes, cute school cardigan, hand on blushing cheek, floating flower petals, tender romantic mood',
      },
      {
        label: 'isekai hero',
        prompt:
          '1boy, isekai hero, tousled brown hair, blue eyes, adventurer tunic, glowing status-window aura, ornate starter sword, wonder-struck expression, fantasy meadow',
      },
      {
        label: 'cyberpunk runner',
        prompt:
          '1girl, cyberpunk runner, neon-streaked cyan hair, glowing augmented eyes, cropped techwear jacket, cybernetic arm, holographic hud, rain-slick neon alley',
      },
      {
        label: 'everyday student',
        prompt:
          '1girl, slice of life student, neat brown bob, warm hazel eyes, tidy school uniform, satchel over shoulder, gentle everyday smile, sunlit classroom',
      },
      {
        label: 'dark knight',
        prompt:
          '1boy, dark fantasy knight, ashen long hair, cold grey eyes, blackened battle-worn armor, torn cape, greatsword planted in ground, grim stare, foggy battlefield',
      },
      {
        label: 'mahou shoujo',
        prompt:
          '1girl, mahou shoujo, glowing pink twintails, magenta eyes, frilly transformation dress, heart-topped wand, magical girl pose, sparkling ribbons, starry backdrop',
      },
      {
        label: 'sports ace',
        prompt:
          '1boy, sports ace, sweaty tousled navy hair, sharp determined eyes, team jersey, athletic build, mid-action leap, stadium floodlights, intense focus',
      },
      {
        label: 'idol center',
        prompt:
          '1girl, idol center, voluminous gradient blonde hair, bright aqua eyes, glittering frilled stage costume, wireless mic, radiant wink, confetti and spotlights',
      },
      {
        label: 'sharp detective',
        prompt:
          '1boy, anime detective, tidy dark hair, keen violet eyes, long trench coat, gloved hand adjusting collar, thoughtful smirk, dim rainy street, film-noir mood',
      },
      {
        label: 'wasteland survivor',
        prompt:
          '1girl, post-apocalyptic survivor, messy ash-blonde hair, weary green eyes, patched hooded jacket, goggles, worn backpack, guarded expression, ruined cityscape',
      },
      {
        label: 'steampunk engineer',
        prompt:
          '1girl, steampunk engineer, copper-red bun, hazel eyes, brass goggles, leather corset vest, mechanical gauntlet, wrench in hand, confident grin, gear-filled workshop',
      },
      {
        label: 'yokai hunter',
        prompt:
          '1boy, yokai hunter, wild dark hair with red streak, golden slit eyes, dark haori, ofuda talismans, spirit-blade katana, alert stance, moonlit haunted forest',
      },
      {
        label: 'space captain',
        prompt:
          '1girl, space captain, sleek silver hair, glowing blue eyes, sharp futuristic uniform with epaulets, star insignia, confident command pose, starship bridge with viewport',
      },
    ],
  },
  {
    id: 'anime-action-fx',
    label: 'Anime Action FX',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: 'anime 전용 액션/연출 tag',
    modes: ['anime'],
    options: [
      {
        label: 'motion blur dash',
        prompt:
          '1girl, dynamic dash, motion blur, trailing afterimages, wind-swept hair, action pose, speed streaks, energetic expression',
      },
      {
        label: 'speed lines',
        prompt:
          '1boy, charging forward, dramatic speed lines, radial background, clenched fist, determined shout, dynamic foreshortening',
      },
      {
        label: 'energy aura',
        prompt:
          '1boy, powering up, glowing energy aura, crackling lightning, floating hair, cracking ground, fierce expression, radiant light burst',
      },
      {
        label: 'sword trail',
        prompt:
          '1girl, mid-swing, glowing sword trail, neon light streak, wind-blown cloak, sharp focused eyes, flying sparks, dynamic slash',
      },
      {
        label: 'wind-swept',
        prompt:
          '1girl, standing in strong wind, wind-swept long hair, fluttering coat, floating leaves, calm resolute gaze, dramatic backlight',
      },
      {
        label: 'floating debris',
        prompt:
          '1boy, telekinetic power, floating debris, levitating rocks, glowing hands, swirling dust, focused stare, energy distortion',
      },
      {
        label: 'magic circle',
        prompt:
          '1girl, casting spell, glowing magic circle, runic symbols, radiant arcane light, extended palm, flowing robe, concentrated expression',
      },
      {
        label: 'sparkle burst',
        prompt:
          '1girl, magical transformation, sparkles, glittering ribbons, swirling light particles, joyful smile, radiant glow, floating pose',
      },
      {
        label: 'dramatic lighting',
        prompt:
          '1boy, dramatic rim lighting, high-contrast shadows, glowing eyes, tense atmosphere, cinematic backlight, intense stance',
      },
      {
        label: 'flowing cape',
        prompt:
          '1boy, heroic stance, flowing cape billowing, wind gust, confident pose, dramatic sky, sweeping fabric motion',
      },
      {
        label: 'jump kick',
        prompt:
          '1girl, mid-air jump kick, dynamic action pose, extended leg, motion blur, flying hair, impact energy, fierce battle expression',
      },
      {
        label: 'spellcast pose',
        prompt:
          '1boy, spell casting pose, both hands glowing, swirling elemental energy, floating magic runes, wind swirl, focused magical concentration',
      },
    ],
  },
  {
    id: 'wd-pixai-tags',
    label: 'WD / PixAI Tagger Tags',
    href: 'https://huggingface.co/SmilingWolf/wd-swinv2-tagger-v3',
    note: 'anime tagger category 조합',
    modes: ['anime'],
    options: [
      {
        label: 'portrait serafuku',
        prompt:
          '1girl, long hair, blue eyes, hair between eyes, upper body, looking at viewer, school uniform, serafuku, closed mouth, blurry background, depth of field',
      },
      {
        label: 'cowboy shot casual',
        prompt:
          '1girl, short hair, brown hair, green eyes, cowboy shot, hand on hip, casual clothes, jacket, standing, outdoors, day',
      },
      {
        label: 'upper body boy',
        prompt:
          '1boy, spiky hair, black hair, red eyes, upper body, looking at viewer, jacket, collar, serious, indoors, dark background',
      },
      {
        label: 'full body dress',
        prompt:
          '1girl, twintails, pink hair, purple eyes, full body, standing, frilled dress, thighhighs, ribbon, simple background, from side',
      },
      {
        label: 'portrait from above',
        prompt:
          '1girl, medium hair, silver hair, aqua eyes, portrait, looking at viewer, from above, choker, off-shoulder, soft lighting, blush',
      },
      {
        label: 'sporty dynamic',
        prompt:
          '1girl, ponytail, orange hair, yellow eyes, upper body, sportswear, sweat, open mouth, dynamic angle, motion lines, gym background',
      },
      {
        label: 'winter cowboy shot',
        prompt:
          '1boy, long hair, white hair, blue eyes, cowboy shot, coat, scarf, hand in pocket, snow, cool expression, depth of field',
      },
      {
        label: 'gothic full body',
        prompt:
          '1girl, wavy hair, purple hair, red eyes, full body, gothic dress, lace, holding umbrella, standing, night, moonlight',
      },
      {
        label: 'hoodie upper body',
        prompt:
          '1girl, bob cut, teal hair, amber eyes, upper body, looking at viewer, hoodie, hood down, headphones, blush, indoor lighting',
      },
      {
        label: 'idol close-up',
        prompt:
          '1girl, long hair, blonde hair, blue eyes, portrait, close-up, idol costume, gloves, wink, stage, colorful lighting, bokeh',
      },
      {
        label: 'mage cowboy shot',
        prompt:
          '1boy, medium hair, green hair, gold eyes, cowboy shot, robe, hood, holding staff, glowing, magic, fantasy background',
      },
      {
        label: 'kimono side view',
        prompt:
          '1girl, long hair, black hair, brown eyes, upper body, kimono, floral print, hair ornament, gentle smile, from side, autumn leaves',
      },
    ],
  },
  {
    id: 'adult-editorial-glamour',
    label: 'Adult Editorial Glamour',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '성인 화보/글래머 스타일',
    modes: ['realistic'],
    options: [
      {
        label: 'Evening Gown',
        prompt:
          'adult woman, 25+ years old, floor-length silk evening gown, plunging open back, elegant editorial glamour, studio lighting, sophisticated refined pose',
      },
      {
        label: 'Silk Blazer',
        prompt:
          'adult woman, 25+ years old, tailored silk blazer worn open over bare skin, deep lapel neckline, sleek fashion editorial, confident stance, high-end styling',
      },
      {
        label: 'Lounge Suit',
        prompt:
          'adult woman, 25+ years old, relaxed satin lounge suit, unbuttoned collar, sensual loungewear editorial, warm ambient light, poised and alluring',
      },
      {
        label: 'Beauty Editorial',
        prompt:
          'adult woman, 25+ years old, close-up beauty editorial, glossy makeup, bare shoulders, dramatic catchlights, glamour portrait, magazine cover styling',
      },
      {
        label: 'Athleisure Glam',
        prompt:
          'adult woman, 25+ years old, form-fitting athleisure set, cropped top and high-waist leggings, toned figure, glossy sport-glam editorial, dynamic pose',
      },
      {
        label: 'Resort Cover-up',
        prompt:
          'adult woman, 25+ years old, sheer resort cover-up over swimwear, sun-kissed skin, breezy poolside editorial, relaxed sensual mood, golden light',
      },
      {
        label: 'Red Carpet Slit',
        prompt:
          'adult woman, 25+ years old, red carpet gown with high thigh slit, sleek silhouette, flashbulb glamour, confident runway pose, luxe fashion editorial',
      },
      {
        label: 'Noir Portrait',
        prompt:
          'adult woman, 25+ years old, film-noir portrait, low-key chiaroscuro lighting, silk slip, mysterious sultry mood, black and white editorial glamour',
      },
      {
        label: 'Wet-Look Hair',
        prompt:
          'adult woman, 25+ years old, wet-look slicked hair fashion editorial, dewy skin, structured swimwear, glossy high-fashion beauty shot, cool studio light',
      },
      {
        label: 'Latex Fashion',
        prompt:
          'adult woman, 25+ years old, glossy latex fashion editorial dress, sculpted silhouette, avant-garde high-fashion styling, dramatic runway lighting',
      },
      {
        label: 'Satin Robe Morning',
        prompt:
          'adult woman, 25+ years old, loosely tied satin robe, soft morning window light, intimate boudoir editorial, relaxed sensual elegance, bare shoulder',
      },
      {
        label: 'Corset Couture',
        prompt:
          'adult woman, 25+ years old, structured corset couture bodice, cinched waist, dramatic ball skirt, opulent editorial glamour, regal poised stance',
      },
    ],
  },
  {
    id: 'adult-realistic-body',
    label: '19+ Body Shape',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '성인 몸매/실루엣',
    modes: ['realistic'],
    options: [
      {
        label: 'Slender Glam',
        prompt:
          'adult woman, 25+ years old, slender graceful figure, long limbs, elongated elegant silhouette, poised glamour posture, fitted attire, refined lines',
      },
      {
        label: 'Curvy Hourglass',
        prompt:
          'adult woman, 25+ years old, curvy hourglass figure, defined waist, balanced feminine silhouette, confident glamour stance, flattering fitted styling',
      },
      {
        label: 'Toned Athletic',
        prompt:
          'adult woman, 25+ years old, toned athletic build, subtle muscle definition, sculpted shoulders, sporty glamour posture, healthy fit silhouette',
      },
      {
        label: 'Petite Sensual',
        prompt:
          'adult woman, 25+ years old, petite delicate frame, soft feminine curves, graceful compact silhouette, intimate sensual posture, tasteful styling',
      },
      {
        label: 'Tall Model',
        prompt:
          'adult woman, 25+ years old, tall statuesque model figure, long legs, striking runway silhouette, poised elongated posture, high-fashion presence',
      },
      {
        label: 'Soft Voluptuous',
        prompt:
          'mature adult woman, late 20s, soft voluptuous figure, full curves, warm feminine silhouette, relaxed sensual posture, flattering drape',
      },
      {
        label: 'Fit Muscular',
        prompt:
          'adult woman, 25+ years old, fit muscular physique, sculpted core and arms, strong athletic silhouette, powerful confident stance, glamour lighting',
      },
      {
        label: 'Pear Silhouette',
        prompt:
          'adult woman, 25+ years old, pear-shaped silhouette, fuller hips, gentle waist, graceful feminine curves, poised elegant posture, tasteful styling',
      },
      {
        label: 'Statuesque',
        prompt:
          'adult woman, 25+ years old, statuesque commanding figure, broad graceful shoulders, regal silhouette, upright confident posture, editorial glamour',
      },
      {
        label: 'Lithe Dancer',
        prompt:
          'adult woman, 25+ years old, lithe dancer physique, supple limbs, elegant lengthened lines, expressive graceful posture, poised balance',
      },
      {
        label: 'Plus-Size Glamour',
        prompt:
          'mature adult woman, late 20s, plus-size glamour figure, full confident curves, radiant feminine silhouette, empowered posture, flattering styling',
      },
      {
        label: 'Swimmer Build',
        prompt:
          'adult woman, 25+ years old, swimmer build, broad shoulders tapering to waist, toned lean muscle, streamlined athletic silhouette, poised stance',
      },
    ],
  },
  {
    id: 'adult-realistic-exposure',
    label: '19+ Exposure Level',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '노출 강도 단계 (은은→아트누드)',
    modes: ['realistic'],
    options: [
      {
        label: 'L1 Fitted',
        prompt:
          'adult woman, 25+ years old, level 1 body-conscious fitted dress, fully covered, sleek form-hugging silhouette, tasteful elegant glamour',
      },
      {
        label: 'L2 Bare Shoulders',
        prompt:
          'adult woman, 25+ years old, level 2 off-shoulder top, bare shoulders and collarbone, softly revealing, elegant tasteful editorial',
      },
      {
        label: 'L3 Deep Neckline',
        prompt:
          'adult woman, 25+ years old, level 3 deep plunging neckline, hint of cleavage, refined glamour styling, tasteful editorial exposure',
      },
      {
        label: 'L4 Swimwear',
        prompt:
          'adult woman, 25+ years old, level 4 one-piece swimwear, poolside editorial, sun-lit skin, sporty glamour, tasteful framing',
      },
      {
        label: 'L5 Lingerie Set',
        prompt:
          'adult woman, 25+ years old, level 5 matching lingerie set, lace bra and briefs, soft boudoir lighting, tasteful sensual editorial',
      },
      {
        label: 'L6 Sheer Bodysuit',
        prompt:
          'adult woman, 25+ years old, level 6 sheer mesh bodysuit, softly translucent fabric, artful shadow, tasteful boudoir editorial',
      },
      {
        label: 'L7 Open Robe',
        prompt:
          'adult woman, 25+ years old, level 7 open robe over lingerie, fabric parting softly, intimate boudoir mood, tasteful',
      },
      {
        label: 'L8 Wet Sheer',
        prompt:
          'adult woman, 25+ years old, level 8 wet sheer fabric clinging, artful water droplets, soft shadow concealment, tasteful fine-art editorial',
      },
      {
        label: 'L9 Under Sheet',
        prompt:
          'adult woman, 25+ years old, level 9 covered boudoir under silk sheet, draped fabric over body, intimate soft light, tasteful',
      },
      {
        label: 'L10 Implied Nude',
        prompt:
          'adult woman, 25+ years old, level 10 artistic implied nude, limbs and shadow covering, no explicit detail, fine-art boudoir',
      },
      {
        label: 'L11 Topless Art',
        prompt:
          'adult woman, 25+ years old, level 11 tasteful topless art nude, arms and hair covering chest, no explicit detail, fine-art',
      },
      {
        label: 'L12 Back Nude',
        prompt:
          'adult woman, 25+ years old, level 12 fine-art back nude, viewed from behind, soft rim light, elegant artistic study',
      },
      {
        label: 'L13 Silhouette Nude',
        prompt:
          'adult woman, 25+ years old, level 13 silhouette nude against bright window, body as dark outline, no explicit detail, artistic',
      },
      {
        label: 'L14 Classical Nude',
        prompt:
          'adult woman, 25+ years old, level 14 classical art nude, soft chiaroscuro shadow, painterly fine-art study, tasteful and timeless',
      },
    ],
  },
  {
    id: 'adult-realistic-pose',
    label: '19+ Sensual Poses / Angles',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '성인 관능 포즈·앵글',
    modes: ['realistic'],
    options: [
      {
        label: 'Arched Back Bed',
        prompt:
          'adult woman, 25+ years old, arched back reclining on bed, graceful curve, soft boudoir lighting, sensual tasteful pose, silk sheets',
      },
      {
        label: 'Kneeling Boudoir',
        prompt:
          'adult woman, 25+ years old, kneeling upright boudoir pose, poised posture, intimate warm light, tasteful sensual editorial',
      },
      {
        label: 'Side Glance Back',
        prompt:
          'adult woman, 25+ years old, lying on side glancing back over shoulder, soft curves, alluring gaze, tasteful boudoir framing',
      },
      {
        label: 'Over-Shoulder Back',
        prompt:
          'adult woman, 25+ years old, over-shoulder bare-back angle, exposed shoulder blades, elegant back line, tasteful fine-art framing',
      },
      {
        label: 'Low-Angle Stance',
        prompt:
          'adult woman, 25+ years old, low-angle powerful standing pose, commanding presence, elongated legs, dramatic editorial glamour',
      },
      {
        label: 'Seated Leg Cross',
        prompt:
          'adult woman, 25+ years old, seated with legs crossed teasingly, poised elegant posture, subtle allure, refined editorial',
      },
      {
        label: 'Stretching Sensual',
        prompt:
          'adult woman, 25+ years old, stretching arms overhead sensually, elongated torso, graceful morning light, tasteful boudoir',
      },
      {
        label: 'Hands In Hair',
        prompt:
          'adult woman, 25+ years old, reclining with hands in hair, relaxed sultry expression, soft ambient glow, tasteful glamour',
      },
      {
        label: 'Window Silhouette',
        prompt:
          'adult woman, 25+ years old, standing silhouette by window, backlit soft outline, contemplative mood, artistic tasteful framing',
      },
      {
        label: 'Shower Editorial',
        prompt:
          'adult woman, 25+ years old, wet-look shower editorial, water on skin, steam and soft glass haze, tastefully framed sensual',
      },
      {
        label: 'Mirror Reflection',
        prompt:
          'adult woman, 25+ years old, mirror reflection boudoir pose, layered reflection composition, intimate soft light, tasteful editorial',
      },
      {
        label: 'From-Above Bed',
        prompt:
          'adult woman, 25+ years old, from-above bed angle, relaxed sprawl on sheets, overhead camera, tasteful sensual composition',
      },
      {
        label: 'Hip-Tilt Contrapposto',
        prompt:
          'adult woman, 25+ years old, hip-tilt contrapposto standing pose, sculptural weight shift, elegant curves, classical glamour',
      },
      {
        label: 'Crawling Forward',
        prompt:
          'adult woman, 25+ years old, crawling forward editorial pose, feline grace, direct gaze, dramatic low light, tasteful sensual',
      },
    ],
  },
  {
    id: 'adult-realistic-wardrobe',
    label: '19+ Lingerie / Wardrobe',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '성인 란제리·의상',
    modes: ['realistic'],
    options: [
      {
        label: 'Lace Balconette',
        prompt:
          'adult woman, 25+ years old, lace balconette bra and matching briefs, delicate floral lace, soft boudoir light, tasteful lingerie editorial',
      },
      {
        label: 'Silk Chemise',
        prompt:
          'adult woman, 25+ years old, short silk chemise, thin straps, fluid draping fabric, intimate soft glow, tasteful loungewear',
      },
      {
        label: 'Garter Bodysuit',
        prompt:
          'adult woman, 25+ years old, bodysuit with attached garters and stockings, sculpted lines, refined boudoir editorial, tasteful',
      },
      {
        label: 'Sheer Robe',
        prompt:
          'adult woman, 25+ years old, sheer chiffon robe over lingerie, translucent flowing fabric, soft backlight, tasteful sensual mood',
      },
      {
        label: 'Satin Slip',
        prompt:
          'adult woman, 25+ years old, bias-cut satin slip dress, liquid sheen fabric, elegant draping, intimate glamour, tasteful',
      },
      {
        label: 'Corset Stockings',
        prompt:
          'adult woman, 25+ years old, laced corset with sheer stockings, cinched waist, vintage boudoir styling, tasteful editorial',
      },
      {
        label: 'Bralette High-Waist',
        prompt:
          'adult woman, 25+ years old, soft bralette and high-waist briefs, minimalist lingerie, natural light, tasteful modern editorial',
      },
      {
        label: 'Micro Bikini',
        prompt:
          'adult woman, 25+ years old, micro bikini editorial, sun-lit beach glamour, string ties, toned figure, tasteful resort shoot',
      },
      {
        label: 'Latex Bodysuit',
        prompt:
          'adult woman, 25+ years old, glossy latex bodysuit, sculpted second-skin fit, avant-garde fashion editorial, dramatic lighting, tasteful',
      },
      {
        label: 'Fishnet Layered',
        prompt:
          'adult woman, 25+ years old, layered fishnet over lingerie, textured mesh, edgy fashion editorial, moody light, tasteful',
      },
      {
        label: 'Babydoll',
        prompt:
          'adult woman, 25+ years old, sheer babydoll nightie, flared hem, delicate trim, soft boudoir glow, tasteful lingerie',
      },
      {
        label: 'Cami Shorts',
        prompt:
          'adult woman, 25+ years old, silky cami and shorts loungewear set, relaxed morning styling, cozy sensual mood, tasteful',
      },
      {
        label: 'Harness Fashion',
        prompt:
          'adult woman, 25+ years old, minimalist strap harness fashion over bodysuit, geometric lines, edgy editorial styling, tasteful',
      },
      {
        label: 'Kimono Robe',
        prompt:
          'adult woman, 25+ years old, silk kimono robe loosely tied, flowing sleeves, bare shoulder, serene intimate mood, tasteful',
      },
    ],
  },
  {
    id: 'adult-anime-glamour',
    label: 'Adult Anime Glamour',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: '성인 anime 글래머',
    modes: ['anime'],
    options: [
      {
        label: 'Evening Dress',
        prompt:
          'mature adult anime woman, 25+ years old, elegant backless evening dress, jewelry, glamorous, detailed anime illustration, tasteful',
      },
      {
        label: 'Bunny Suit',
        prompt:
          'mature adult anime woman, 25+ years old, playboy bunny suit, bunny ears, detached collar, pantyhose, glamorous pose, tasteful anime art',
      },
      {
        label: 'China Dress',
        prompt:
          'mature adult anime woman, 25+ years old, china dress, cheongsam, thigh slit, side bun, elegant, detailed anime illustration, tasteful',
      },
      {
        label: 'Glamour Swimsuit',
        prompt:
          'mature adult anime woman, 25+ years old, competition swimsuit, poolside, wet skin, glamorous figure, tasteful anime illustration',
      },
      {
        label: 'Office Lady',
        prompt:
          'mature adult anime woman, 25+ years old, office lady, pencil skirt, unbuttoned blouse, glasses, mature charm, tasteful anime art',
      },
      {
        label: 'Elegant Kimono',
        prompt:
          'mature adult anime woman, 25+ years old, elegant furisode kimono, obi, hair ornament, refined pose, detailed anime illustration, tasteful',
      },
      {
        label: 'Cocktail Glam',
        prompt:
          'mature adult anime woman, 25+ years old, cocktail dress, high heels, wine glass, sultry expression, glamorous anime art, tasteful',
      },
      {
        label: 'Bunny Large Bust',
        prompt:
          'mature adult anime woman, 25+ years old, mature female, large breasts, formal bunny suit, curvy figure, glamorous, tasteful anime illustration',
      },
      {
        label: 'Nurse Outfit',
        prompt:
          'mature adult anime woman, 25+ years old, fitted nurse outfit, stethoscope, mature charm, glamorous pose, tasteful anime art',
      },
      {
        label: 'Santa Glam',
        prompt:
          'mature adult anime woman, 25+ years old, santa costume, fur trim, thigh-highs, festive glamour, tasteful anime illustration',
      },
      {
        label: 'Modern Qipao',
        prompt:
          'mature adult anime woman, 25+ years old, modern qipao, embroidered silk, elegant curves, sultry gaze, detailed anime art, tasteful',
      },
      {
        label: 'Gown Diva',
        prompt:
          'mature adult anime woman, 25+ years old, mermaid gown, opera gloves, tiara, diva glamour pose, detailed anime illustration, tasteful',
      },
    ],
  },
  {
    id: 'adult-anime-body',
    label: '19+ Anime Body',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: '성인 anime 몸매 태그',
    modes: ['anime'],
    options: [
      {
        label: 'Slender Anime',
        prompt:
          'mature adult anime woman, 25+ years old, slender figure, long legs, graceful silhouette, elegant proportions, detailed anime illustration, tasteful',
      },
      {
        label: 'Curvy Hourglass',
        prompt:
          'mature adult anime woman, 25+ years old, curvy hourglass figure, wide hips, narrow waist, mature female, tasteful anime art',
      },
      {
        label: 'Toned Athletic',
        prompt:
          'mature adult anime woman, 25+ years old, toned athletic body, abs, fit figure, sporty silhouette, detailed anime illustration, tasteful',
      },
      {
        label: 'Petite Frame',
        prompt:
          'mature adult anime woman, 25+ years old, petite frame, delicate figure, graceful posture, mature female, tasteful anime art',
      },
      {
        label: 'Tall Model',
        prompt:
          'mature adult anime woman, 25+ years old, tall figure, long legs, statuesque silhouette, model proportions, detailed anime illustration, tasteful',
      },
      {
        label: 'Voluptuous',
        prompt:
          'mature adult anime woman, 25+ years old, mature female, large breasts, voluptuous figure, soft curves, tasteful anime illustration',
      },
      {
        label: 'Muscular Fit',
        prompt:
          'mature adult anime woman, 25+ years old, muscular, toned abs, strong arms, fit physique, powerful pose, tasteful anime art',
      },
      {
        label: 'Wide Hips',
        prompt:
          'mature adult anime woman, 25+ years old, wide hips, thick thighs, pear silhouette, mature female, detailed anime illustration, tasteful',
      },
      {
        label: 'Statuesque',
        prompt:
          'mature adult anime woman, 25+ years old, statuesque tall figure, regal proportions, commanding silhouette, elegant, tasteful anime art',
      },
      {
        label: 'Slim Dancer',
        prompt:
          'mature adult anime woman, 25+ years old, slim dancer build, flexible pose, elongated limbs, graceful lines, tasteful anime illustration',
      },
      {
        label: 'Plus Curves',
        prompt:
          'mature adult anime woman, 25+ years old, plus-size figure, full soft curves, confident pose, mature female, tasteful anime art',
      },
      {
        label: 'Swimmer Build',
        prompt:
          'mature adult anime woman, 25+ years old, broad shoulders, toned lean body, swimmer build, athletic silhouette, tasteful anime illustration',
      },
    ],
  },
  {
    id: 'adult-anime-exposure',
    label: '19+ Anime Exposure',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: 'anime 노출 단계 (은은→아트누드)',
    modes: ['anime'],
    options: [
      {
        label: 'L1 Fitted',
        prompt:
          'mature adult anime woman, 25+ years old, level 1 fitted bodycon dress, fully clothed, form-fitting silhouette, tasteful anime illustration',
      },
      {
        label: 'L2 Bare Shoulders',
        prompt:
          'mature adult anime woman, 25+ years old, level 2 off-shoulder outfit, bare shoulders, collarbone, tasteful anime art',
      },
      {
        label: 'L3 Cleavage',
        prompt:
          'mature adult anime woman, 25+ years old, level 3 plunging neckline, cleavage, elegant styling, tasteful anime illustration',
      },
      {
        label: 'L4 Swimsuit',
        prompt:
          'mature adult anime woman, 25+ years old, level 4 swimsuit, poolside, exposed midriff, tasteful anime art',
      },
      {
        label: 'L5 Lingerie',
        prompt:
          'mature adult anime woman, 25+ years old, level 5 lingerie set, bra and panties, boudoir, tasteful anime illustration',
      },
      {
        label: 'L6 Sheer Bodysuit',
        prompt:
          'mature adult anime woman, 25+ years old, level 6 sheer bodysuit, see-through fabric, soft shadow, tasteful anime art',
      },
      {
        label: 'L7 Open Robe',
        prompt:
          'mature adult anime woman, 25+ years old, level 7 open robe over lingerie, parted fabric, intimate mood, tasteful anime illustration',
      },
      {
        label: 'L8 Wet Sheer',
        prompt:
          'mature adult anime woman, 25+ years old, level 8 wet see-through clothes, clinging fabric, water droplets, soft shadow, tasteful anime art',
      },
      {
        label: 'L9 Under Sheet',
        prompt:
          'mature adult anime woman, 25+ years old, level 9 covered under bed sheet, draped fabric, intimate light, tasteful anime illustration',
      },
      {
        label: 'L10 Implied Nude',
        prompt:
          'mature adult anime woman, 25+ years old, level 10 implied nude, convenient censoring by limbs and shadow, no explicit detail, tasteful anime art',
      },
      {
        label: 'L11 Topless Art',
        prompt:
          'mature adult anime woman, 25+ years old, level 11 tasteful topless art, arms and hair covering chest, no explicit detail, anime illustration',
      },
      {
        label: 'L12 Back Nude',
        prompt:
          'mature adult anime woman, 25+ years old, level 12 back nude, viewed from behind, bare back, soft rim light, tasteful anime art',
      },
      {
        label: 'L13 Silhouette',
        prompt:
          'mature adult anime woman, 25+ years old, level 13 silhouette nude against light, dark outline, no explicit detail, tasteful anime illustration',
      },
      {
        label: 'L14 Classical Nude',
        prompt:
          'mature adult anime woman, 25+ years old, level 14 classical art nude, soft shadow, painterly anime study, tasteful and timeless',
      },
    ],
  },
  {
    id: 'adult-anime-pose',
    label: '19+ Anime Sensual Poses',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: '성인 anime 관능 포즈·앵글',
    modes: ['anime'],
    options: [
      {
        label: 'Arched Back',
        prompt:
          'mature adult anime woman, 25+ years old, arched back on bed, graceful curve, soft light, sensual tasteful pose, detailed anime illustration',
      },
      {
        label: 'Kneeling',
        prompt:
          'mature adult anime woman, 25+ years old, kneeling pose, hands on thighs, upright posture, intimate light, tasteful anime art',
      },
      {
        label: 'Side Glance',
        prompt:
          'mature adult anime woman, 25+ years old, lying on side, looking back over shoulder, alluring gaze, tasteful anime illustration',
      },
      {
        label: 'Bare Back Angle',
        prompt:
          'mature adult anime woman, 25+ years old, from behind, bare back, looking over shoulder, elegant back line, tasteful anime art',
      },
      {
        label: 'Low Angle',
        prompt:
          'mature adult anime woman, 25+ years old, low angle view, powerful standing pose, long legs, dramatic composition, tasteful anime illustration',
      },
      {
        label: 'Leg Cross Seated',
        prompt:
          'mature adult anime woman, 25+ years old, sitting, crossed legs, teasing pose, poised posture, tasteful anime art',
      },
      {
        label: 'Stretching',
        prompt:
          'mature adult anime woman, 25+ years old, arms up stretching, arched torso, morning light, sensual tasteful anime illustration',
      },
      {
        label: 'Hands In Hair',
        prompt:
          'mature adult anime woman, 25+ years old, reclining, hands in hair, sultry expression, soft glow, tasteful anime art',
      },
      {
        label: 'Window Silhouette',
        prompt:
          'mature adult anime woman, 25+ years old, standing by window, backlit silhouette, contemplative mood, tasteful anime illustration',
      },
      {
        label: 'Wet Shower',
        prompt:
          'mature adult anime woman, 25+ years old, shower scene, wet skin, steam, tastefully framed, sensual anime art',
      },
      {
        label: 'Mirror Pose',
        prompt:
          'mature adult anime woman, 25+ years old, mirror reflection, layered composition, boudoir, intimate light, tasteful anime illustration',
      },
      {
        label: 'From Above',
        prompt:
          'mature adult anime woman, 25+ years old, from above, lying on bed, overhead view, relaxed sprawl, tasteful anime art',
      },
      {
        label: 'Hip Tilt',
        prompt:
          'mature adult anime woman, 25+ years old, hip tilt, contrapposto stance, curvy silhouette, elegant pose, tasteful anime illustration',
      },
      {
        label: 'Crawling',
        prompt:
          'mature adult anime woman, 25+ years old, crawling forward, feline grace, direct gaze, dramatic light, sensual tasteful anime art',
      },
    ],
  },
  {
    id: 'adult-anime-wardrobe',
    label: '19+ Anime Lingerie / Wardrobe',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: '성인 anime 란제리·의상',
    modes: ['anime'],
    options: [
      {
        label: 'Lace Lingerie',
        prompt:
          'mature adult anime woman, 25+ years old, lace bra and panties, delicate lace trim, boudoir, tasteful anime illustration',
      },
      {
        label: 'Bunny Suit',
        prompt:
          'mature adult anime woman, 25+ years old, bunny suit, bunny ears, detached collar, pantyhose, tasteful anime art',
      },
      {
        label: 'Competition Swimsuit',
        prompt:
          'mature adult anime woman, 25+ years old, competition swimsuit, high leg cut, sporty, tasteful anime illustration',
      },
      {
        label: 'China Dress',
        prompt:
          'mature adult anime woman, 25+ years old, china dress, cheongsam, thigh slit, elegant, tasteful anime art',
      },
      {
        label: 'Garter Set',
        prompt:
          'mature adult anime woman, 25+ years old, garter belt, thigh-highs, lingerie, boudoir styling, tasteful anime illustration',
      },
      {
        label: 'Latex Bodysuit',
        prompt:
          'mature adult anime woman, 25+ years old, glossy latex bodysuit, skin-tight, sculpted, tasteful anime art',
      },
      {
        label: 'Loose Kimono',
        prompt:
          'mature adult anime woman, 25+ years old, loose kimono, bare shoulder, sash undone, serene mood, tasteful anime illustration',
      },
      {
        label: 'Babydoll',
        prompt:
          'mature adult anime woman, 25+ years old, sheer babydoll, flared hem, ribbon trim, soft glow, tasteful anime art',
      },
      {
        label: 'Chemise Slip',
        prompt:
          'mature adult anime woman, 25+ years old, satin chemise slip, thin straps, fluid fabric, intimate, tasteful anime illustration',
      },
      {
        label: 'Micro Bikini',
        prompt:
          'mature adult anime woman, 25+ years old, micro bikini, string ties, beach glamour, tasteful anime art',
      },
      {
        label: 'Fishnet Layer',
        prompt:
          'mature adult anime woman, 25+ years old, fishnet bodystocking over lingerie, mesh texture, edgy, tasteful anime illustration',
      },
      {
        label: 'Corset Set',
        prompt:
          'mature adult anime woman, 25+ years old, corset, cinched waist, garters, vintage boudoir, tasteful anime art',
      },
      {
        label: 'Cami Shorts',
        prompt:
          'mature adult anime woman, 25+ years old, camisole and shorts, loungewear, cozy sensual mood, tasteful anime illustration',
      },
      {
        label: 'Strap Harness',
        prompt:
          'mature adult anime woman, 25+ years old, strap harness over bodysuit, geometric straps, edgy fashion, tasteful anime art',
      },
    ],
  },
  {
    id: 'adult-realistic-boudoir',
    label: '19+ Boudoir',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '성인 부두아르/침실 무드',
    modes: ['realistic'],
    options: [
      {
        label: 'Silk Sheets Recline',
        prompt:
          'adult woman, 25+ years old, reclining on silk sheets, draped sheet across the body, soft morning light, serene sensual boudoir editorial',
      },
      {
        label: 'Morning Robe',
        prompt:
          'adult woman, 25+ years old, silk robe slipping off one shoulder, bedside morning light, intimate relaxed boudoir mood',
      },
      {
        label: 'Prone Glance',
        prompt:
          'adult woman, 25+ years old, lying prone on the bed on elbows, looking back over the shoulder, bare back, tasteful boudoir',
      },
      {
        label: 'Window Lingerie',
        prompt:
          'adult woman, 25+ years old, sitting on the bed edge in lace lingerie, soft window light, contemplative sensual mood',
      },
      {
        label: 'Sheet Wrap',
        prompt:
          'adult woman, 25+ years old, standing wrapped loosely in a bedsheet, bare shoulders and legs, warm intimate light',
      },
      {
        label: 'Pillow Embrace',
        prompt:
          'adult woman, 25+ years old, lying among pillows hugging one, soft smile, cozy sensual boudoir',
      },
      {
        label: 'Candlelit Bed',
        prompt:
          'adult woman, 25+ years old, kneeling on the bed in candlelight, warm glow on the skin, intimate boudoir editorial',
      },
      {
        label: 'Headboard Lean',
        prompt:
          'adult woman, 25+ years old, leaning against the headboard, one knee up, silk chemise, relaxed alluring pose',
      },
      {
        label: 'Backlit Slip',
        prompt:
          'adult woman, 25+ years old, standing by a sheer bedroom curtain, backlit silhouette in a satin slip, dreamy mood',
      },
      {
        label: 'Satin Reverie',
        prompt:
          'adult woman, 25+ years old, reclining in a satin robe, eyes closed, soft sensual serenity, warm light',
      },
    ],
  },
  {
    id: 'adult-realistic-artnude',
    label: '19+ Art Nude',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '아트 누드 (전략적 가림/실루엣, 노골X)',
    modes: ['realistic'],
    options: [
      {
        label: 'Backlit Silhouette',
        prompt:
          'adult woman, 25+ years old, fine-art nude silhouette against a bright window, body as a dark outline, no explicit detail',
      },
      {
        label: 'Body Paint Art',
        prompt:
          'adult woman, 25+ years old, artistic body paint covering the torso, fine-art studio, creative sensual composition',
      },
      {
        label: 'Wet Skin Study',
        prompt:
          'adult woman, 25+ years old, wet glistening-skin studio art nude, arm and shadow covering, fine-art lighting',
      },
      {
        label: 'Shadow Play',
        prompt:
          'adult woman, 25+ years old, chiaroscuro art nude, deep shadows covering, sculptural light on the curves, no explicit detail',
      },
      {
        label: 'Classical Drape',
        prompt:
          'adult woman, 25+ years old, classical fine-art nude with a sheer drape, painterly soft light, timeless study',
      },
      {
        label: 'Side Profile',
        prompt:
          'adult woman, 25+ years old, side-profile art nude, elegant curve of the back and hip, soft rim light',
      },
      {
        label: 'Rim-light Contour',
        prompt:
          'adult woman, 25+ years old, rim-lit contour nude, glowing edges, form suggested in shadow, artistic',
      },
      {
        label: 'Hands Cover',
        prompt:
          'adult woman, 25+ years old, standing art nude with hands and hair covering, no explicit detail, tasteful',
      },
      {
        label: 'Veiled Sheet',
        prompt:
          'adult woman, 25+ years old, nude behind a translucent sheet, form softly veiled, artistic fine-art',
      },
      {
        label: 'Monochrome Nude',
        prompt:
          'adult woman, 25+ years old, black-and-white fine-art nude study, film grain, soft shadow, gallery style',
      },
    ],
  },
  {
    id: 'adult-realistic-fetish-fashion',
    label: '19+ Fetish Fashion',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '라텍스·레더·하네스 패션 화보',
    modes: ['realistic'],
    options: [
      {
        label: 'Latex Catsuit',
        prompt:
          'adult woman, 25+ years old, glossy latex catsuit, sculpted second-skin fit, avant-garde fashion editorial, dramatic lighting',
      },
      {
        label: 'Leather Corset',
        prompt:
          'adult woman, 25+ years old, leather corset and long gloves, edgy high-fashion editorial, confident stance',
      },
      {
        label: 'Strap Harness',
        prompt:
          'adult woman, 25+ years old, minimalist leather strap harness over a bodysuit, geometric lines, fashion editorial',
      },
      {
        label: 'Bodystocking',
        prompt:
          'adult woman, 25+ years old, sheer fishnet bodystocking, full-body mesh, moody fashion editorial',
      },
      {
        label: 'Latex Gown',
        prompt:
          'adult woman, 25+ years old, latex opera gloves with a high-slit gown, glossy high-fashion glamour',
      },
      {
        label: 'Vinyl Bodysuit',
        prompt:
          'adult woman, 25+ years old, shiny vinyl bodysuit, cyber-fashion editorial, neon rim light',
      },
      {
        label: 'Chain Harness',
        prompt:
          'adult woman, 25+ years old, delicate chain-body harness over lingerie, jewelry-like styling, boudoir editorial',
      },
      {
        label: 'Leather Set',
        prompt:
          'adult woman, 25+ years old, leather bralette and mini skirt with studded accents, edgy glamour editorial',
      },
      {
        label: 'Sleek Noir Latex',
        prompt:
          'adult woman, 25+ years old, sleek black latex two-piece, noir fashion editorial, high contrast light',
      },
      {
        label: 'Lace Catsuit',
        prompt:
          'adult woman, 25+ years old, sheer floral-lace catsuit, full-body lace, elegant fashion editorial',
      },
    ],
  },
  {
    id: 'adult-realistic-shower-wet',
    label: '19+ Shower / Wet',
    href: 'https://huggingface.co/datasets/detection-datasets/fashionpedia',
    note: '샤워·웻룩 관능 화보',
    modes: ['realistic'],
    options: [
      {
        label: 'Steamy Glass',
        prompt:
          'adult woman, 25+ years old, behind steamy shower glass, soft silhouette, water droplets, tastefully framed sensual',
      },
      {
        label: 'Wet White Shirt',
        prompt:
          'adult woman, 25+ years old, wet white shirt clinging, rain-soaked, sensual editorial, tasteful',
      },
      {
        label: 'Shower Stream',
        prompt:
          'adult woman, 25+ years old, standing under a shower stream, wet hair and skin, arm covering, tasteful sensual',
      },
      {
        label: 'Pool Emerge',
        prompt:
          'adult woman, 25+ years old, emerging from a pool, water sheeting off the skin, swimwear, glamour editorial',
      },
      {
        label: 'Rain Slip',
        prompt:
          'adult woman, 25+ years old, rain-soaked in a slip dress, wet fabric clinging, moody sensual mood',
      },
      {
        label: 'Foam Bath',
        prompt:
          'adult woman, 25+ years old, reclining in a foam bath, bubbles covering, relaxed spa sensual, tasteful',
      },
      {
        label: 'Wet Hair Studio',
        prompt:
          'adult woman, 25+ years old, wet-look slicked hair, dripping, glossy beauty editorial, bare shoulders',
      },
      {
        label: 'Frosted Silhouette',
        prompt:
          'adult woman, 25+ years old, wet silhouette behind frosted glass, soft veiled form, artistic',
      },
      {
        label: 'Waterfall Mist',
        prompt:
          'adult woman, 25+ years old, standing in waterfall mist, wet skin, swimwear, cinematic sensual',
      },
      {
        label: 'Wet Sheer Robe',
        prompt:
          'adult woman, 25+ years old, wet sheer robe clinging, water droplets, boudoir editorial, tasteful',
      },
    ],
  },
  {
    id: 'adult-anime-boudoir',
    label: '19+ Anime Boudoir',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: '성인 anime 부두아르',
    modes: ['anime'],
    options: [
      {
        label: 'Silk Sheets',
        prompt:
          'mature adult anime woman, 25+ years old, reclining on silk sheets, draped sheet, soft morning light, sensual boudoir, tasteful anime illustration',
      },
      {
        label: 'Morning Robe',
        prompt:
          'mature adult anime woman, 25+ years old, silk robe off one shoulder, morning light, intimate boudoir mood, tasteful anime art',
      },
      {
        label: 'Prone Glance',
        prompt:
          'mature adult anime woman, 25+ years old, lying prone on bed, looking back over shoulder, bare back, tasteful anime illustration',
      },
      {
        label: 'Window Lingerie',
        prompt:
          'mature adult anime woman, 25+ years old, on bed edge in lace lingerie, soft window light, sensual mood, tasteful anime art',
      },
      {
        label: 'Sheet Wrap',
        prompt:
          'mature adult anime woman, 25+ years old, wrapped loosely in a bedsheet, bare shoulders, warm light, tasteful anime illustration',
      },
      {
        label: 'Candlelit Bed',
        prompt:
          'mature adult anime woman, 25+ years old, kneeling on bed in candlelight, warm glow, intimate boudoir, tasteful anime art',
      },
      {
        label: 'Headboard Lean',
        prompt:
          'mature adult anime woman, 25+ years old, leaning on headboard, one knee up, silk chemise, alluring, tasteful anime illustration',
      },
      {
        label: 'Backlit Slip',
        prompt:
          'mature adult anime woman, 25+ years old, by a sheer curtain, backlit silhouette in a satin slip, dreamy, tasteful anime art',
      },
      {
        label: 'Pillow Embrace',
        prompt:
          'mature adult anime woman, 25+ years old, among pillows hugging one, soft smile, cozy boudoir, tasteful anime illustration',
      },
      {
        label: 'Satin Reverie',
        prompt:
          'mature adult anime woman, 25+ years old, reclining in a satin robe, eyes closed, sensual serenity, tasteful anime art',
      },
    ],
  },
  {
    id: 'adult-anime-artnude',
    label: '19+ Anime Art Nude',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: 'anime 아트 누드 (가림/실루엣, 노골X)',
    modes: ['anime'],
    options: [
      {
        label: 'Backlit Silhouette',
        prompt:
          'mature adult anime woman, 25+ years old, art nude silhouette against bright light, dark outline, no explicit detail, tasteful anime illustration',
      },
      {
        label: 'Body Paint',
        prompt:
          'mature adult anime woman, 25+ years old, artistic body paint over the torso, creative sensual, tasteful anime art',
      },
      {
        label: 'Wet Skin Study',
        prompt:
          'mature adult anime woman, 25+ years old, wet glistening skin, arm and shadow covering, fine-art, tasteful anime illustration',
      },
      {
        label: 'Shadow Play',
        prompt:
          'mature adult anime woman, 25+ years old, chiaroscuro art nude, shadows covering, sculptural light, no explicit detail, tasteful anime art',
      },
      {
        label: 'Classical Drape',
        prompt:
          'mature adult anime woman, 25+ years old, classical art nude with sheer drape, painterly soft light, tasteful anime illustration',
      },
      {
        label: 'Side Profile',
        prompt:
          'mature adult anime woman, 25+ years old, side-profile art nude, elegant back and hip curve, soft rim light, tasteful anime art',
      },
      {
        label: 'Hands Cover',
        prompt:
          'mature adult anime woman, 25+ years old, art nude with hands and hair covering, no explicit detail, tasteful anime illustration',
      },
      {
        label: 'Veiled Sheet',
        prompt:
          'mature adult anime woman, 25+ years old, nude behind a translucent sheet, softly veiled form, artistic, tasteful anime art',
      },
      {
        label: 'Rim-light Contour',
        prompt:
          'mature adult anime woman, 25+ years old, rim-lit contour nude, glowing edges, form in shadow, tasteful anime illustration',
      },
      {
        label: 'Monochrome',
        prompt:
          'mature adult anime woman, 25+ years old, monochrome fine-art nude study, soft shadow, gallery style, tasteful anime art',
      },
    ],
  },
  {
    id: 'adult-anime-fetish-fashion',
    label: '19+ Anime Fetish Fashion',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: '성인 anime 라텍스·레더 패션',
    modes: ['anime'],
    options: [
      {
        label: 'Latex Catsuit',
        prompt:
          'mature adult anime woman, 25+ years old, glossy latex catsuit, second-skin fit, avant-garde fashion, tasteful anime illustration',
      },
      {
        label: 'Leather Corset',
        prompt:
          'mature adult anime woman, 25+ years old, leather corset and gloves, edgy high-fashion, confident stance, tasteful anime art',
      },
      {
        label: 'Strap Harness',
        prompt:
          'mature adult anime woman, 25+ years old, leather strap harness over bodysuit, geometric lines, fashion editorial, tasteful anime illustration',
      },
      {
        label: 'Bodystocking',
        prompt:
          'mature adult anime woman, 25+ years old, sheer fishnet bodystocking, full-body mesh, moody fashion, tasteful anime art',
      },
      {
        label: 'Latex Gown',
        prompt:
          'mature adult anime woman, 25+ years old, latex opera gloves with a high-slit gown, glossy glamour, tasteful anime illustration',
      },
      {
        label: 'Vinyl Bodysuit',
        prompt:
          'mature adult anime woman, 25+ years old, shiny vinyl bodysuit, cyber-fashion, neon rim light, tasteful anime art',
      },
      {
        label: 'Chain Harness',
        prompt:
          'mature adult anime woman, 25+ years old, delicate chain-body harness over lingerie, jewelry styling, tasteful anime illustration',
      },
      {
        label: 'Leather Set',
        prompt:
          'mature adult anime woman, 25+ years old, leather bralette and mini skirt, studded accents, edgy glamour, tasteful anime art',
      },
      {
        label: 'Sleek Noir Latex',
        prompt:
          'mature adult anime woman, 25+ years old, sleek black latex two-piece, noir fashion, high contrast light, tasteful anime illustration',
      },
      {
        label: 'Lace Catsuit',
        prompt:
          'mature adult anime woman, 25+ years old, sheer floral-lace catsuit, elegant fashion editorial, tasteful anime art',
      },
    ],
  },
  {
    id: 'adult-anime-shower-wet',
    label: '19+ Anime Shower / Wet',
    href: 'https://huggingface.co/datasets/deepghs/danbooru2023-sfw',
    note: '성인 anime 샤워·웻룩',
    modes: ['anime'],
    options: [
      {
        label: 'Steamy Glass',
        prompt:
          'mature adult anime woman, 25+ years old, behind steamy shower glass, soft silhouette, water droplets, tastefully framed, anime illustration',
      },
      {
        label: 'Wet White Shirt',
        prompt:
          'mature adult anime woman, 25+ years old, wet white shirt clinging, rain-soaked, sensual, tasteful anime art',
      },
      {
        label: 'Shower Stream',
        prompt:
          'mature adult anime woman, 25+ years old, under a shower stream, wet hair and skin, arm covering, tasteful anime illustration',
      },
      {
        label: 'Pool Emerge',
        prompt:
          'mature adult anime woman, 25+ years old, emerging from a pool, water on skin, swimsuit, glamour, tasteful anime art',
      },
      {
        label: 'Rain Slip',
        prompt:
          'mature adult anime woman, 25+ years old, rain-soaked in a slip dress, wet fabric clinging, moody, tasteful anime illustration',
      },
      {
        label: 'Foam Bath',
        prompt:
          'mature adult anime woman, 25+ years old, reclining in a foam bath, bubbles covering, relaxed spa mood, tasteful anime art',
      },
      {
        label: 'Wet Hair',
        prompt:
          'mature adult anime woman, 25+ years old, wet-look slicked hair, dripping, glossy beauty, bare shoulders, tasteful anime illustration',
      },
      {
        label: 'Frosted Silhouette',
        prompt:
          'mature adult anime woman, 25+ years old, wet silhouette behind frosted glass, veiled form, artistic, tasteful anime art',
      },
      {
        label: 'Waterfall Mist',
        prompt:
          'mature adult anime woman, 25+ years old, in waterfall mist, wet skin, swimsuit, cinematic sensual, tasteful anime illustration',
      },
      {
        label: 'Wet Sheer Robe',
        prompt:
          'mature adult anime woman, 25+ years old, wet sheer robe clinging, water droplets, boudoir, tasteful anime art',
      },
    ],
  },
  {
    id: 'outfit-girly',
    label: '의상·걸리 (Girly)',
    href: 'https://arca.live/b/aiart/172954099',
    note: '사랑스런 소녀 컨셉 아웃핏 (채씨 프리셋)',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Mono Frill Harness',
        prompt:
          'white short-sleeve button-up blouse, cropped white shirt, ruffled front button placket, black suspender straps, black harness-style suspenders, black high-waist pleated skirt, black skater skirt, black choker collar, small bow tie detail, white frilly ankle socks, black chunky boots, cream platform sole',
      },
      {
        label: 'Rose Knit Lace-up',
        prompt:
          'light pink cropped button-up shirt, tied-front crop shirt, front tie straps, pink mini skirt, light pink bodycon mini skirt, white side-tie thong, white knee-high socks, pale pink platform mary jane shoes, layered pearl choker, pearl collar necklace',
      },
      {
        label: 'White Ribbed Check Skirt',
        prompt:
          'white fitted tank top, white ribbed sleeveless crop tank, scoop neck tank top, small white bow detail, pink plaid pleated mini skirt, pink black white tartan skirt, gray paisley bandana, black and gray bandana print',
      },
      {
        label: 'Mint Heart Lounge',
        prompt:
          'white cropped long-sleeve lounge top, white microprint crop top, pale blue tiny bow pattern, open-front tie crop top, front tie closure, three-quarter sleeves, matching white microprint ruffle mini shorts, low-rise ruffled shorts, scalloped lettuce hem shorts',
      },
      {
        label: 'Mint Shirt Preppy',
        prompt:
          'llight blue oversized button-up shirt, rolled-up sleeves, open shirt layer, dark navy plaid pleated mini skirt, high-waist pleated skirt, white lace bralette, floral lace bra, white semi-sheer lace lingerie top, scalloped lace trim, white ankle socks',
      },
      {
        label: 'Pink Hotel Party',
        prompt:
          'pink crushed velvet mini dress, pink velvet bodycon dress, off-shoulder, white cloudy pattern, pink velvet choker scarf, matching pink neck scarf, black platform mule heels, silver heel detail',
      },
      {
        label: 'Soft Prep Casual',
        prompt:
          'white short-sleeve ribbed knit polo top, fitted cream knit top, small button placket, gray pleated mini skirt, blue satin panties, white knee-high socks, black and white canvas sneakers, oversized black square glasses',
      },
      {
        label: 'Aqua Tulle Pop',
        prompt:
          'aqua sheer babydoll dress, mint tulle ruffle overlay, translucent mesh fabric, pastel floral camisole bodice, soft lace trim, glitter beige panties, pink and black striped over-the-knee socks, hot pink platform lace-up sneakers, thick white sole, blue stud earrings',
      },
      {
        label: 'Soft Study Girly',
        prompt:
          'white off-shoulder ribbed long-sleeve top, scalloped neckline, fitted cropped top, llight blue high-waisted denim shorts, frayed hem shorts, white low-top sneakers, iridescent holographic mini bag, transparent rainbow crossbody bag, black shoulder strap',
      },
      {
        label: 'Black Ribbon Girly',
        prompt:
          'black short-sleeve fitted top, black lace-trim neckline, small front button detail, black ribbon strap accents, white ankle socks, black and white chunky trail sneakers',
      },
      {
        label: 'Soft White Date',
        prompt:
          'pale pink blazer worn open, tailored blazer jacket, white mini dress, white flowy skater dress, sleeveless dress, soft chiffon-like fabric, layered asymmetric hem, nude clear strap block heels, transparent mule heels, small hoop earrings, delicate silver necklace',
      },
      {
        label: 'Tartan School Siren',
        prompt:
          'grey cropped cardigan, charcoal button-up cardigan, red ribbed camisole, red spaghetti strap top, dark green tartan pleated mini skirt, black and green plaid skirt, black thigh-high stockings, sheer black stockings, black stiletto heels, pointed toe pumps, red bra strap, high-waist plaid skirt',
      },
      {
        label: 'Soft Paisley Muse',
        prompt:
          'white cropped denim jacket, white button-front jacket, silver button details, pale lavender paisley mini skirt, ruched side mini skirt, side slit detail, matching lavender crop top, nude ankle-strap high heel sandals, stiletto heels, gold chain shoulder bag',
      },
      {
        label: 'Sweet Summer',
        prompt:
          'pink floral sundress, pale pink daisy-print dress, spaghetti-strap summer dress, tie-shoulder straps, sweetheart neckline, fitted bodice, flared skirt, soft A-line mini dress, cream white underwear, cream high-cut panties, delicate feminine dress',
      },
      {
        label: 'Fair Isle Lounge',
        prompt:
          'fair isle long-sleeve fitted top, red gray white nordic pattern, soft stretch knit fabric, black graphic shorts, bold white lettering print, cream ribbed over-the-knee socks, slouchy knit leg warmers, black rectangular glasses, small silver hoop earrings, delicate chain necklace',
      },
      {
        label: 'Blush Ribbed Loungewear',
        prompt:
          'cream ribbed floral bodysuit, soft cotton loungewear romper, tiny pink floral print, ribbed knit texture, white lace trim, side cutout detail, cozy bedroom loungewear, silver hoop earrings, delicate silver bracelet',
      },
      {
        label: 'Soft Check Girly',
        prompt:
          'black mock-neck short-sleeve top, fitted black knit tee, soft stretch jersey fabric, white plaid mini skirt, white grid check skirt, pleated micro mini skirt, black ankle boots, pointed-toe ankle boots, side-zip boots',
      },
      {
        label: 'Floral Sneaker Girly',
        prompt:
          'fitted lace bodice, soft pink waistband detail, llight blue denim mini skirt, ruffled denim mini skirt, layered frill hem, distressed denim patches, black floral sneakers, black low-top sneakers, pink flower print sneakers',
      },
      {
        label: 'Flower Crown Denim',
        prompt:
          'pink floral blouse, soft gathered neckline, puff sleeve floral top, lightweight crinkled fabric, rose print pattern, llight blue denim micro shorts, faded wash denim, frayed hem detail, low-rise denim shorts, black flat flip-flop sandals, black shoulder bag with silver chain strap, white flower crown headband, ribbon choker necklace',
      },
      {
        label: 'Aqua Tulle Resort',
        prompt:
          'aqua blue strapless bustier crop top, white piping detail, small white bow accent, fitted corset-style bodice, sheer pale blue long-sleeve shrug cardigan, translucent mesh fabric, bell sleeves, white high-waist mini skirt, structured cotton skirt, llight blue thigh-high stockings, white platform Mary Jane heels, aqua satin choker necklace, silver butterfly pendant',
      },
      {
        label: 'White Garden Slip',
        prompt:
          'white floral camisole mini dress, tiny pink floral print, delicate lace trim, ruffled neckline, thin shoulder straps, low open back detail, loose white cover-up shirt, lightweight fabric, translucent beaded bracelet, pearl choker necklace',
      },
      {
        label: 'Powder Blue Girly',
        prompt:
          'llight blue short-sleeve shirt dress, pastel blue button-front one-piece dress, partially unbuttoned front, open collar neckline, white button detail, white inner camisole, white floral print bra, cream floral print panties, black crossbody bag, white ankle socks, white Mary Jane shoes',
      },
      {
        label: 'Cute Cow Bikini',
        prompt:
          'white cow-print micro bikini set, triangle halter bikini top, side-tie string bikini bottoms, cow-ear headband, cow-print bow choker, black wrist cuff, cute silver heart stud earrings, delicate silver charm necklace',
      },
      {
        label: 'Blush Satin',
        prompt:
          'soft pink long-sleeve ruched blouse, drawstring front detail, lightweight textured knit fabric, delicate ruffle trim, white satin flared mini skirt, skater skirt silhouette, small silver stud earrings',
      },
      {
        label: 'Sweet Glasses Casual',
        prompt:
          'pink-and-white vertical striped cropped tie-front blouse, short sleeve pajama-style collared shirt, front knot detail, white fitted camisole inner top, white skinny jeans, pastel pink belt, silver square buckle, pink velvet choker, silver heart pendant necklace, black oversized glasses',
      },
      {
        label: 'Lavender Daisy Cottage',
        prompt:
          'lavender daisy print pinafore mini dress, white eyelet puff-sleeve blouse, square neckline, front tie detail, ruffled skirt hem, white lace trim, white headscarf, white knee-high socks, black mary jane shoes, daisy flower choker',
      },
      {
        label: 'Ribbon Prep Lounge',
        prompt:
          'black short puff sleeves, white ribbed pleated front blouse panel, black contrast side panels, black ribbon bow tie collar, white stand collar, decorative front button detail, fitted knit top, beige plaid mini skirt, tartan check pattern, llight blue lace bra, matching lace panties, scalloped lace trim, white ankle socks',
      },
      {
        label: 'Sky Prep Ballet',
        prompt:
          'pale blue oversized button-up shirt, loose open-collar shirt, soft satin-like cotton fabric, pale blue lace-trim camisole, llight blue pleated tennis skirt, crisp micro-pleat texture, small embroidered logo detail, white shirt cuffs, white ballet flats, delicate stud earrings',
      },
    ],
  },
  {
    id: 'outfit-glam',
    label: '의상·글램 (Glam)',
    href: 'https://arca.live/b/aiart/172954099',
    note: '클럽·파티 컨셉 아웃핏 (채씨 프리셋)',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Pink Neon Fur',
        prompt:
          'hot pink long-sleeve crop top, turquoise low-rise micro shorts, silver chain waist belt, hot pink faux fur jacket, hot pink ankle-strap platform heels, blue choker collar, turquoise heart pendant necklace, fitted stretch fabric, metallic chain detail',
      },
      {
        label: 'Rockstar Pink Beach',
        prompt:
          'metallic hot pink bikini top, metallic pink micro mini skirt, silver chain belt, white spiked choker, red-pink flame sunglasses, black leather biker jacket, ribbed pink leg warmers, white platform slide heels',
      },
      {
        label: 'Checkerboard Pink Club',
        prompt:
          'black and gray checkerboard mesh crop top, sheer checkered long-sleeve crop top, hot pink micro shorts, silver chain belt, pink ankle-strap platform heels, round pink tinted sunglasses, silver chain necklace, pink heart padlock pendant, rings, sheer mesh texture, metallic chain detail',
      },
      {
        label: 'Wine Leather Corset',
        prompt:
          'red fitted mini dress, red bodycon dress, red strappy lace bralette, red lace triangle bra, harness-style bra straps, black velvet choker, black sheer thigh-high stockings, lace waistband detail, white shoulder bag, chain-strap purse',
      },
      {
        label: 'Pink Bikini Denim Shirt',
        prompt:
          'blue and pink plaid button-up shirt, oversized plaid shirt, rolled-up sleeves, hot pink bikini top, hot pink strappy bikini top, llight blue denim micro mini skirt, butterfly patch detail, white thigh-high socks, white chunky sneakers, pink lace choker, pink bracelet, hoop earrings, silver rings',
      },
      {
        label: 'Sheer Noir Bodysuit',
        prompt:
          'black sheer long-sleeve bodysuit, translucent mesh fabric, high-neck bodysuit, fitted black one-piece, black low-top sneakers, white rubber sole',
      },
      {
        label: 'Ice Lounge Glam',
        prompt:
          'llight blue ribbed turtleneck mini dress, fitted knit bodycon dress, long sleeve sweater dress, pale blue ribbed knit texture, white strappy high-heel sandals, open-toe heels, geometric block heel, white ankle strap detail',
      },
      {
        label: 'Noir Sequin Night',
        prompt:
          'black sequin cropped jacket, black rhinestone embellished bolero jacket, sheer taupe mesh long-sleeve top, translucent mesh fabric, fitted taupe mini skirt, satin stretch fabric, bodycon mini skirt, sheer black stockings, black pointed-toe ankle strap heels',
      },
      {
        label: 'Powder Blue Glam',
        prompt:
          'powder blue moto jacket worn open, llight blue biker jacket, silver zipper detail, pale blue bodycon mini dress, ruched bust detail, keyhole front detail, fitted stretch fabric, black ankle strap stiletto sandals, black open-toe heels, small hoop earrings',
      },
      {
        label: 'City Sport Siren',
        prompt:
          'white cropped moto jacket, white biker jacket, silver zipper detail, red sleeveless bodycon mini dress, high neck cutout dress, fitted club dress, large silver hoop earrings, black ankle boots',
      },
      {
        label: 'Midnight Tulle Glam',
        prompt:
          'black strapless mini dress, black lace bodice detail, black sheer mesh skirt overlay, black tulle skirt, scalloped lace texture, fitted party dress, silver ankle strap rhinestone heels, glitter embellished high heels, delicate ankle bracelet',
      },
      {
        label: 'Y2K Moto Clubwear',
        prompt:
          'mustard yellow faux leather biker jacket, asymmetrical zipper, silver zipper details, snap button lapels, shoulder epaulettes, sleeve belt strap detail, black fitted mini dress, black bodycon dress, yellow high heels, gold hoop earrings, long gold tassel earrings, gold cuff bracelet',
      },
      {
        label: 'Red Leather Night',
        prompt:
          'red cropped leather moto jacket, asymmetric zipper jacket, silver snap buttons, black spaghetti strap tank top, black fitted camisole, black leather pants, high-waist skinny pants, black ankle boots, red high heel shoes, nose ring',
      },
      {
        label: 'Glossy Cherry Knit',
        prompt:
          'white fitted long-sleeve top, white mock-neck top, white belted jacket, white cropped jacket, snap button details, red satin shorts, red boxer-style shorts, red high-waist shorts, white thigh-high socks, white underwear, white choker collar, pale pink choker, black shoulder bag, chain-strap bag',
      },
      {
        label: 'Cherry Denim Doll',
        prompt:
          'llight blue denim jacket, cropped denim jacket, pink cherry-pattern crop top, red spaghetti straps, pale wash denim pants, embroidered cherry pattern jeans, red grommet belt, red studded belt, white string thong, white choker collar, red round sunglasses, gold hoop earrings, white sneakers',
      },
      {
        label: 'Sheer Noir Club',
        prompt:
          'black sheer off-shoulder mini dress, black ruched bodycon dress, semi-sheer mesh fabric, fitted long sheer sleeves, soft draped texture, asymmetric off-shoulder neckline, short black club dress, clear mule heels, transparent open-toe heels, lucite heel detail, delicate gold pendant necklace, gold beaded bracelet, slim ring',
      },
      {
        label: 'Iridescent Pool Siren',
        prompt:
          'iridescent blue-purple triangle bikini top, shimmering satin-like fabric, thin string halter straps, center ring connector detail, side string tie detail, delicate silver bracelet',
      },
      {
        label: 'Noir Slit Glam',
        prompt:
          'black sleeveless mock-neck maxi dress, fitted turtleneck column dress, high side-slit dress, smooth stretch knit fabric, bodycon silhouette, elegant black evening dress, black ankle-strap stiletto heels, pointed-toe high heels, slim black bracelet, minimalist black wristwatch, small silver hoop earrings, delicate silver ring',
      },
      {
        label: 'Floral Keyhole Dress',
        prompt:
          'black sleeveless embroidered mini dress, fitted bodycon dress, sheer mesh upper panel, high mock neckline, keyhole cutout neckline, red and pink floral embroidery, rose embroidery detail, semi-sheer fabric, red ankle-strap high heels, red platform sandals, pearl bracelet, small black stud earrings',
      },
      {
        label: 'Denim Heel Muse',
        prompt:
          'black sleeveless sheer mesh tank top, black fitted camisole lining, scoop neckline, semi-sheer upper panel, charcoal gray denim micro shorts, faded denim wash, low-rise shorts, raw hem detail, side slit hem, black platform stiletto sandals, ankle-strap high heels, silver chain necklace with drop pendant, pearl bracelet',
      },
      {
        label: 'Red Satin Cheongsam',
        prompt:
          'red satin cheongsam dress, sleeveless qipao mini dress, mandarin collar, keyhole neckline, side slit, floral embroidery print, peacock pattern, red piping trim, fitted silhouette, small gold drop earrings, delicate gold bracelet, red ankle-strap heels, gold strappy sandals',
      },
      {
        label: 'Dark Panel Noir',
        prompt:
          'black long-sleeve button-front blouse, fitted black knit top, navy pleated mini skirt, black sheer pantyhose, black ankle-strap heels, black Mary Jane pumps, gold pendant necklace, pearl stud earrings',
      },
      {
        label: 'Leopard Lace Glam',
        prompt:
          'black button-front cardigan, black fitted long-sleeve cardigan, scoop neckline, silver button detail, pink lace camisole, fuchsia lace-trim inner top, leopard print skirt, animal print hem detail, pink lace hem trim, black strappy sandals, black ankle-strap sandals, silver chain sandal detail, oversized dark sunglasses',
      },
      {
        label: 'Teal Saree',
        prompt:
          'turquoise green saree, teal draped sari, gold embroidered border, navy blue blouse, short-sleeve choli blouse, gold sleeve cuffs, gold striped pattern, flowing lightweight fabric, pleated drape texture, delicate gold necklace, small gold stud earrings, silver anklets, metallic strappy sandals',
      },
      {
        label: 'Ripped Denim Glam',
        prompt:
          'mauve fuzzy knit crop top, short-sleeve cropped sweater, semi-sheer loose-knit texture, wide cutout neckline, red underwire bra, balconette bra detail, dark indigo ripped jeans, heavily torn back-cutout denim, distressed denim seams, flared jeans silhouette, visible red thong underwear, black pointed-toe stiletto pumps, bold gold hoop earrings, layered gold pendant necklace',
      },
      {
        label: 'Blonde Bandana Crop',
        prompt:
          'white button-up shirt, open-collar blouse, partially unbuttoned shirt, tie-front shirt hem, knotted blouse, black high-waisted mini skirt, fitted office skirt, black thigh-high stockings, sheer patterned stockings, black pointed-toe pumps, delicate layered silver necklace, small silver hoop earrings, slim silver bracelet',
      },
      {
        label: 'Yellow Poolside Bikini',
        prompt:
          'yellow triangle bikini top, pastel yellow floral bikini, white flower strap detail, matching yellow bikini bottoms, low-rise tie-side bikini bottoms, poolside resort swimwear, layered gold necklaces, turquoise pendant necklace, delicate gold bracelet, small pearl stud earrings, white strappy sandals',
      },
      {
        label: 'Hayloft Folk Dress',
        prompt:
          'black dirndl dress, Bavarian folk costume, black patterned bodice, lace-up front corset detail, white lace short-sleeve blouse, scalloped lace trim, black apron skirt, gingham fabric accent, silver rhinestone open-toe heels, silver ankle-strap sandals, black drop earrings, gold nameplate necklace, large rhinestone statement ring',
      },
      {
        label: 'Crystal Apartment',
        prompt:
          'ivory halter keyhole corset top, cream fitted bustier top, structured seam detail, semi-sheer mesh side panels, satin-like finish, black low-rise shorts, fitted micro shorts, sheer black pantyhose, rhinestone drop earrings, layered crystal necklace, sparkly pendant necklace, delicate tennis bracelet, stacked silver rings, crystal charm bracelet',
      },
    ],
  },
  {
    id: 'outfit-street',
    label: '의상·스트릿 (Street)',
    href: 'https://arca.live/b/aiart/172954099',
    note: '도시 스트릿 컨셉 아웃핏 (채씨 프리셋)',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Pastel Windbreaker Dolly',
        prompt:
          'light pink zip-up windbreaker jacket, white cropped camisole, pale pink low-rise micro shorts, black studded thigh garters, black leather choker collar, colorful plastic bracelets, white ruffle ankle socks, white floral platform boots, chunky lace-up platform boots',
      },
      {
        label: 'Violet Check Punk',
        prompt:
          'purple plaid cropped polo top, lavender black white tartan crop top, black collar, matching purple plaid mini skirt, white ribbon side ties, black leather opera gloves, buckle strap glove details, black choker collar, black platform sandals, open-toe platform shoes',
      },
      {
        label: 'Black Bandana Sleeveless',
        prompt:
          'black headscarf, black gauze bandana, black sheer scarf headwrap, sleeveless black cropped graphic tank, washed black cut-off crop top, llight blue baggy denim pants, front drawstring tie, loose low-rise jeans, back patch pockets',
      },
      {
        label: 'Black Goth Frill',
        prompt:
          'black gothic lace bodysuit, black lace long-sleeve top, sheer floral lace sleeves, deep plunge neckline, black ribbon choker, black O-ring choker, black ruffled mini skirt, black frill-hem skirt, black fishnet tights, black net pantyhose, black chunky lace-up shoes, black platform derby shoes, lace texture, fishnet pattern',
      },
      {
        label: 'Burgundy Sheer Denim',
        prompt:
          'burgundy sheer mesh crop top, dark red see-through crop blouse, off-shoulder mesh top, short puff sleeves, ruffled neckline, small floral embroidery pattern, sheer floral tulle fabric, llight blue denim micro shorts, frayed hem denim shorts, black thigh-high stockings, small hoop earrings, delicate pendant necklace, thin bracelet',
      },
      {
        label: 'Pastel Candy Rave',
        prompt:
          'white strapless bandeau crop top, white tube top, sheer white cropped shrug, translucent long-sleeve bolero, pastel rainbow striped cuffs, mint green micro mini skirt, orange net pantyhose, white chunky platform sandals, gold studded choker, gold chain waist belt, round purple tinted sunglasses, rainbow bracelet stack',
      },
      {
        label: 'Rose Mesh Street',
        prompt:
          'black sheer floral mesh long-sleeve top, translucent mesh fabric, pink rose print, black fitted camisole underlayer, high-waisted charcoal gray ripped jeans, distressed knee cutouts, wide-leg denim, black and white canvas sneakers, small black stud earrings',
      },
      {
        label: 'Houndstooth Night Street',
        prompt:
          'white puffer jacket worn open, cropped padded jacket, black fitted turtleneck crop top, long-sleeve crop top, multicolor houndstooth mini skirt, tweed texture, beige black red check pattern, black thigh-high socks, large silver hoop earrings, layered silver chain necklaces',
      },
      {
        label: 'Frosty Pleat Pop',
        prompt:
          'shiny patent puffer jacket, white ribbed turtleneck crop top, fitted long-sleeve crop sweater, gray high-waist pleated mini skirt, structured pleated skirt, silver layered necklace, large silver hoop earrings, beige furry ankle boots, white fur-trim boots',
      },
      {
        label: 'Night Lace Casual',
        prompt:
          'black faux leather jacket worn open, black moto jacket, black lace bodysuit, black sheer lace camisole top, scalloped lace neckline, spaghetti straps, llight blue high-waist skinny jeans, distressed ripped jeans, black ankle boots, black heeled ankle boots, delicate gold necklace',
      },
      {
        label: 'Windy Punk Casual',
        prompt:
          'black rectangular glasses, black faux leather jacket worn open, black graphic sweatshirt, white text print, dark skinny jeans, black low-top sneakers, black and white skate shoes, septum ring, small hoop earrings, multiple ear piercings',
      },
      {
        label: 'Urban Knit Rider',
        prompt:
          'black faux leather jacket worn open, open-front moto jacket, black ribbed knit mini dress, black knit one-piece dress, red stripe chest detail, pleated hem detail, long sleeves, small silver hoop earrings, white low-top sneakers',
      },
      {
        label: 'Checker Pop Punk',
        prompt:
          'black and white checkerboard tank top, sleeveless checkered crop top, yellow trim, yellow low-rise shorts, bright yellow hot pants, white chain belt, chunky plastic chain belt, white thigh garter strap, white leather choker collar, silver heart O-ring collar, rainbow striped ankle socks, black platform mary jane shoes, white bracelet, rainbow bracelet stack',
      },
      {
        label: 'Leather Noir Street',
        prompt:
          'black leather jacket, cropped leather jacket, fitted black blazer-style jacket, black bodycon mini skirt, black mini dress, black lace camisole, black lace-trim top, black shoulder bag, gold chain strap, silver pendant necklace, black pumps, black peep-toe heels',
      },
      {
        label: 'Stripe Grunge Street',
        prompt:
          'white oversized knit sweater, black stripe sleeve detail, dark ribbed mock neck, ribbed cuffs, small chest logo patch, black bra, dark blue skinny jeans, fitted denim pants, black ankle boots, small silver stud earrings',
      },
      {
        label: 'Layered Denim Noir',
        prompt:
          'black fitted short-sleeve top, sleek minimalist t-shirt, blue asymmetrical layered denim mini skirt, wrap-style overlap panel, offset waistband, side flap detail, washed denim texture, white lace bra with black lace applique, scalloped lace trim, black patent mary jane heels',
      },
      {
        label: 'Gothic Lace Street',
        prompt:
          'black gothic lace top, sheer floral lace long sleeves, flared lace cuffs, black ribbon tie neckline, black O-ring choker, black ruffled mini skirt, layered hem skirt, smocked waistband, black fishnet tights, back-seam bow detail tights, black lace-up oxford shoes, gold bracelet watch, black ring',
      },
      {
        label: 'Lavender White Muse',
        prompt:
          'black padded winter jacket, black puffer jacket, black scarf, black turtleneck top, purple ribbed cardigan, white denim mini skirt, sheer black pantyhose, black knee-high boots, black leather shoulder bag, pearl stud earrings, white satin lingerie set, black lace trim bra, grey floral embroidery bra cups, black mesh lace panels, white satin panties, llight blue ribbon bow detail',
      },
    ],
  },
  {
    id: 'outfit-sporty',
    label: '의상·스포티 (Sporty)',
    href: 'https://arca.live/b/aiart/172954099',
    note: '스포츠 컨셉 아웃핏 (채씨 프리셋)',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Mint Zip-up Sporty',
        prompt:
          'llight blue sleeveless zip-up crop top, mock-neck quarter-zip top, white side stripe detail, matching llight blue biker shorts, high-waist fitted cycling shorts, white platform high heels, silver layered choker necklace, rhinestone choker, small pendant necklace, belly button piercing',
      },
      {
        label: 'Clean Sporty Basic',
        prompt:
          'white cropped zip-front tank top, white sleeveless crop top, white racerback crop tank, front zipper detail, black high-waist leggings, white logo waistband, fitted activewear pants, white sneakers, white low-top sneakers, pale pink scrunchie, small stud earrings, ribbed stretch fabric',
      },
      {
        label: 'Sporty Lake Cutie',
        prompt:
          'red cropped football jersey, red mesh sports jersey, white number print, V-neck jersey top, short-sleeve athletic crop top, black leather shorts, black high-waist shorts, side lace-up detail, black choker, thin silver chain necklace, white crew socks, white low-top sneakers',
      },
      {
        label: 'Candy Athleisure Pop',
        prompt:
          'black varsity jacket worn open, lavender sleeves, striped ribbed cuffs, snap button front, white lace bra top, scalloped lace trim, floral lace texture, hot pink high-waist leggings, bright fuchsia fitted leggings, large silver hoop earrings, delicate gold necklace, turquoise blue beaded bracelet',
      },
      {
        label: 'Sunny Sporty Fit',
        prompt:
          'yellow translucent sunglasses, small hoop earrings, llight blue ribbed crop top, pale blue spaghetti strap camisole, fitted cropped tank top, ribbed knit texture, dark gray high-waist leggings, charcoal gray fitted leggings, white chunky sneakers, yellow beaded bracelet',
      },
      {
        label: 'Pink Lounge Sport',
        prompt:
          'gray sheer lace-trim camisole, pink lace neckline, small white bow accents, pink dolphin shorts, white contrast piping, drawstring waist, soft athletic shorts, pink and white leg warmers, gray buckle strap platform sandals, silver bracelet stack, delicate chain necklace',
      },
      {
        label: 'White Racer Sport',
        prompt:
          'white lightweight bomber jacket, white zip-up jacket, reflective sleeve stripe detail, red cropped tank top, red racerback crop top, llight blue denim mini skirt, white thigh-high athletic socks, white platform sneakers, white chunky sneakers, large silver hoop earrings, white leather backpack',
      },
      {
        label: 'Road Trip Sporty',
        prompt:
          'white sleeveless graphic crop top, white cut-out tank top, distressed white jersey fabric, red blue black graphic print, star print detail, studded perforated neckline, llight blue denim shorts, faded wash denim shorts, high-waisted denim shorts, raw hem cut-off shorts, frayed denim texture, white leather sneakers, chunky white sneakers, low-top lace-up sneakers',
      },
      {
        label: 'Gray Biker Lounge',
        prompt:
          'gray oversized cropped sports t-shirt, charcoal gray boxy tee, printed graphic, athletic slogan print, black contrast trim neckline, black contrast sleeve cuffs, loose short sleeves, dark gray high-waisted biker shorts, fitted stretch cycling shorts, matte jersey fabric, silver chain bracelet',
      },
      {
        label: 'Sporty Zip Mini',
        prompt:
          'black fitted zip-front sporty mini dress, short-sleeve athletic dress, white double side stripes, polo collar, deep zip neckline, bodycon silhouette, smooth stretch jersey fabric, white chunky platform sneakers, delicate gold heart pendant necklace, small stud earrings',
      },
      {
        label: 'Riding Crop Chic',
        prompt:
          'white sleeveless riding shirt, partially unbuttoned front, collared button-up blouse, white sheer corset bra, lace-trim bustier, black fitted riding pants, black knee-high riding boots, layered gold necklaces, pearl stud earrings',
      },
      {
        label: 'Cream Yoga Street',
        prompt:
          'cream cable-knit wrap cardigan, ivory chunky knit cardigan, cropped cardigan hem, ribbed trim, gold button detail, soft knit texture, dusty rose leggings, fitted pink leggings, white chunky sneakers, beige shoulder tote bag',
      },
      {
        label: 'Beige Bedroom Sporty',
        prompt:
          'beige cropped hoodie, oversized hooded sweatshirt, white long sleeve layered cuffs, soft fleece fabric, drawstring hood, cropped raw hem, black high-waisted panties, black brief underwear, white thigh-high socks, black double stripe detail, sporty striped socks',
      },
      {
        label: 'Mesh Trail Street',
        prompt:
          'black sheer mesh crop top, black sports bra, sleeveless athletic top, yellow high-waisted biker shorts, fitted cycling shorts, black low-top sneakers, black ankle socks',
      },
    ],
  },
  {
    id: 'outfit-office',
    label: '의상·오피스 (Office)',
    href: 'https://arca.live/b/aiart/172954099',
    note: '직장 여성 컨셉 아웃핏 (채씨 프리셋)',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Gray Lounge Secretary',
        prompt:
          'gray long-sleeve knit mini dress, fitted sweater dress, heather gray knit texture, deep v-neckline, long pearl necklace, pearl choker necklace, gold statement earrings, black clutch bag, beige thigh-high stockings, nude sheer stockings, black pointed-toe pumps',
      },
      {
        label: 'Noir Bow Muse',
        prompt:
          'black fitted bodycon dress, black midi dress, short-sleeve dress, white contrast piping, white trim neckline, oversized bow shoulder detail, black knit fabric, black quilted chain bag, black satin gloves, sheer black pantyhose, black seamed stockings, black thigh-high stockings, black pointed-toe pumps, black patent pumps, black ankle-strap heels, vintage noir dress',
      },
      {
        label: 'Pearl Noir Office',
        prompt:
          'semi-sheer white button-up blouse, subtly translucent white shirt, open-collar blouse, black ruffle-trim bra top, black balconette bra, black spaghetti straps, black underbust corset, black waist cincher, black mini skirt, black fishnet stockings, black garter-style hosiery, pearl necklace, leopard print pumps, leopard print high heels',
      },
      {
        label: 'Navy Prep Blazer',
        prompt:
          'grey cropped blazer, grey fitted blazer, navy trim blazer, white ruffled blouse, white button-up blouse, navy high-waist mini skirt, navy pleated mini skirt, black thigh-high socks, opaque black over-knee socks, black chain shoulder bag, gold chain strap, black mary jane heels, black patent pumps, black block heel loafers',
      },
      {
        label: 'Office Noir Chic',
        prompt:
          'gray sleeveless draped cowl-neck blouse, loose gray satin blouse, soft gray sleeveless top, black fitted pencil skirt, black bodycon mini skirt, black sheer panel skirt, horizontal sheer stripe detail, black strappy high heels, black ankle-strap sandals, beige leather tote bag, cream shoulder bag, black bracelet',
      },
      {
        label: 'Beige Office Minimal',
        prompt:
          'white short-sleeve blouse, soft gathered shoulder detail, airy lightweight fabric, high-waisted beige pencil mini skirt, fitted office skirt, back zipper detail, black pointed pumps, small dangling earrings',
      },
      {
        label: 'Soft Lace Office',
        prompt:
          'black knee-length knit dress, fitted black sweater dress, soft matte knit fabric, three-quarter sleeves, scoop neckline, gray floral lace trim neckline, delicate embroidered collar detail, subtle scalloped lace edge, slim black choker necklace, small purple gemstone stud earrings',
      },
      {
        label: 'Red Executive Suit',
        prompt:
          'red tailored blazer, red belted blazer jacket, matching red suit trousers, red two-piece pantsuit, structured office suit set, fitted silhouette, sharp lapels, silver grommet belt detail, black pointed-toe pumps, sleek high heels, gold drop earrings',
      },
      {
        label: 'Navy City Office',
        prompt:
          'white sleeveless halter blouse, open-back halter top, crisp cotton fabric, subtle button-front detail, fitted black pencil skirt, high-waisted black office skirt, sheer black stockings, opaque thigh-high stocking effect, black pointed-toe pumps, slim ankle-strap heels, delicate silver stud earrings, thin silver bracelet, black rectangular glasses',
      },
      {
        label: 'Beige Office Muse',
        prompt:
          'light beige fitted wrap mini dress, soft knit fabric, short sleeves, deep v-neckline, body-hugging silhouette, subtle side ruching, black sheer pantyhose, black pointed pumps, small stud earrings, delicate pendant necklace, slim bracelet',
      },
      {
        label: 'Leopard Blazer Office',
        prompt:
          'black fitted blazer, flared cuff blazer sleeves, leopard-print lining detail, crisp white button-up blouse, open collar shirt, black high-waisted micro shorts, black studded belt, sheer nude pantyhose, black patent pointed pumps, cream canvas shoulder handbag with black trim',
      },
      {
        label: 'Pink Knit Secretary',
        prompt:
          'pale pink ribbed knit cardigan, cropped fitted cardigan, white collared blouse, soft layered office knitwear, llight blue pencil mini skirt, sheer nude pantyhose, black pointed stiletto pumps, delicate pendant necklace, small stud earrings',
      },
      {
        label: 'Plaid Pencil Chic',
        prompt:
          'gray fitted turtleneck sweater, long-sleeve ribbed knit top, high-neck slim silhouette, gray plaid pencil skirt, knee-length office skirt, subtle check pattern, back slit detail, gray pointed pumps, small hoop earrings',
      },
      {
        label: 'Orange Office Tease',
        prompt:
          'bright orange off-shoulder blouse, one-shoulder slouch top, black lace-trim inner camisole slightly exposed, black bra strap detail, loose long-sleeve blouse, black high-waisted pencil mini skirt, fitted office skirt, black sheer thigh-high stockings, lace-top stocking bands, black pointed pumps',
      },
      {
        label: 'Black Suit Secretary',
        prompt:
          'black fitted blazer, white collared dress shirt, black bodycon mini skirt, tailored office suit set, slim black belt, black sheer pantyhose, black pointed stiletto pumps, amber-tinted aviator glasses, long dangling white earrings, gold wristwatch',
      },
      {
        label: 'Executive Noir Office',
        prompt:
          'black long-sleeve wrap blouse, loose satin blouse, deep V-neckline, front zipper detail, tied back waist ribbon, beige high-waist pencil skirt, taupe faux leather skirt, fitted knee-length skirt, black pointed-toe stiletto heels, delicate gold necklace, thin gold bracelet',
      },
      {
        label: 'White Blouse Seduction',
        prompt:
          'light gray fitted button-up blouse, deep V-neck blouse, twisted front detail, black high-waist pencil skirt, black sheer thigh-high stockings, wide black lace stocking tops, black garter straps, white thong underwear, black patent leather peep-toe platform heels, pearl stud earrings',
      },
      {
        label: 'Office Lace Noir',
        prompt:
          'black tailored blazer, white button-up blouse, blouse tucked into skirt, open-collar blouse, unbuttoned blouse, black lace bra, exposed lingerie detail, high-waisted black skirt, fitted mini skirt, sheer black pantyhose, striped sheer tights, silver pendant necklace, small silver drop earrings, delicate silver bracelet, silver ring',
      },
      {
        label: 'Chic Desk Muse',
        prompt:
          'white button-up shirt, open-collar shirt, unbuttoned shirt, white pocket shirt, black high-waisted mini skirt, black bra, visible bra detail, skin-tone sheer pantyhose, nude stockings, black strappy high heels, wraparound ankle-strap heels, small pearl stud earrings, delicate gold necklace, slim gold bracelet, elegant wristwatch, minimal gold ring',
      },
    ],
  },
  {
    id: 'outfit-uniform',
    label: '의상·유니폼 (Uniform)',
    href: 'https://arca.live/b/aiart/172954099',
    note: '교복·제복 컨셉 아웃핏 (채씨 프리셋)',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Twilight Cheer Schoolgirl',
        prompt:
          'white long-sleeve turtleneck crop top, purple cheerleader mini skirt, pleated purple skirt, white double stripe hem, white thigh-high socks, black choker collar, yellow mini backpack, black-and-pink sneakers',
      },
      {
        label: 'Navy Rider School',
        prompt:
          'black faux leather biker jacket, cropped leather jacket, snap button lapels, shoulder epaulettes, white collared button-up shirt, dark navy sleeveless sweater vest, red and navy plaid necktie, navy pleated skirt, black sheer tights, black platform lace-up shoes, black leather backpack',
      },
      {
        label: 'Retro Uniform Muse',
        prompt:
          'black boater hat, floral hat trim, red white and blue flower detail, grey fitted coatdress, grey structured jacket bodice, black lapel trim, puff shoulders, white collared shirt, red necktie, red cravat, royal blue skirt panel, red mary jane heels, red low pumps, sheer nude pantyhose',
      },
      {
        label: 'Alpine Maid Costume',
        prompt:
          'off-shoulder white peasant blouse, lace-trim neckline, black and white gingham corset bodice, black folk mini skirt, red apron panel, floral embroidered trim, white ruffle underskirt, large red back bow, red satin thong underwear, white lace wrist cuffs, white platform ankle-strap heels',
      },
      {
        label: 'Velvet Collar Uniform',
        prompt:
          'black velvet long-sleeve mini dress, white Peter Pan collar, fitted bodycon silhouette, soft velour texture, white over-the-knee socks, double black stripe detail, black chunky Mary Jane shoes',
      },
      {
        label: 'Woodland School Muse',
        prompt:
          'gray cable-knit V-neck sweater, white button-up collared shirt, dark plaid necktie, black hooded coat with faux fur trim, black pleated mini skirt, black backpack, oversized black round glasses, sheer black knee-high socks, black lace-up school shoes',
      },
      {
        label: 'Scarlet Cabin Uniform',
        prompt:
          'red flight attendant pillbox hat, red fitted blazer, white blouse, red and blue neck scarf, navy pencil skirt, beige thigh-high stockings, nude lace-top stockings, black pointed-toe heels, small silver stud earrings',
      },
      {
        label: 'Classic Maid Doll',
        prompt:
          'black satin maid dress, black and white maid costume, white lace trim, white ruffle hem, white puff sleeves, off-shoulder sleeves, black faux leather bodice, lace-trim bra cups, white ribbon lace-up front, white waist apron panel, matching maid headpiece, black peep-toe platform pumps, black stiletto heels, sheer nude pantyhose',
      },
      {
        label: 'White Ribbon Uniform',
        prompt:
          'white oversized button-up shirt, loose long-sleeve dress shirt, open lower buttons, crisp cotton fabric, gray striped ribbon bow tie, school-uniform neck bow, minimal collar detail',
      },
      {
        label: 'Navy Authority Uniform',
        prompt:
          'navy uniform blazer, white collared dress shirt, black necktie, navy pencil skirt, structured tailored suit, silver button detail, shoulder epaulettes, name badge detail, black lace panties, black lace garter stockings, sheer black thigh-high stockings, black patent leather pointed-toe heels',
      },
      {
        label: 'Red Academy Classic',
        prompt:
          'red cropped cardigan, white collared dress shirt, black necktie, red and gray plaid pleated mini skirt, white knee-high socks, black Mary Jane loafers, black headband, small silver bow earrings',
      },
      {
        label: 'Twin Braid Schoolgirl',
        prompt:
          'burgundy school blazer, white collared dress shirt, striped burgundy necktie, charcoal gray pleated mini skirt, white ankle socks, casual indoor shoes, delicate bracelet',
      },
      {
        label: 'Lavender School Boudoir',
        prompt:
          'white short-sleeve school blouse, half-open front, partially unbuttoned blouse, black name tag detail, gray plaid pleated mini skirt, lavender floral lace bra, lavender sheer lace panties, white knee-high socks, black pointed-toe pumps',
      },
      {
        label: 'Beige Pinafore Uniform',
        prompt:
          'llight blue short-sleeve collared school shirt, blue red striped necktie, beige pinafore pleated mini dress, khaki suspender school dress, white thigh-high socks, metallic silver Mary Jane flats, blue stud earrings, white underwear',
      },
      {
        label: 'Navy Dorm Uniform',
        prompt:
          'navy oversized school sweater, llight blue collared shirt, blue striped necktie, navy pleated mini skirt, black sheer pantyhose, black garter straps, black patent leather platform stiletto heels, red sole detail, pearl stud earrings',
      },
      {
        label: 'Dark Officer Uniform',
        prompt:
          'navy military dress uniform, structured uniform blazer, matching navy pencil skirt, llight blue collared dress shirt, military insignia cap, embroidered silver cap badge, epaulettes, name tag, ribbon bar pins, nude sheer pantyhose, pale yellow lace panties, black ankle-strap pumps, pearl stud earrings',
      },
      {
        label: 'Midnight Stewardess',
        prompt:
          'black tailored blazer, black uniform mini skirt, white collared dress shirt, blue gray black patterned neck scarf, flight attendant uniform style, black sheer pantyhose, purple satin panties, black pointed-toe pumps, small pearl stud earrings, delicate gold ring',
      },
      {
        label: 'Soft Clerk Uniform',
        prompt:
          'black short-sleeve knit cardigan, white contrast trim, open-front cardigan design, white pocket accent detail, black scoop-neck inner top, black sleeveless mini dress, sleek fitted silhouette, soft knit fabric, black ankle socks, small gold floral stud earrings',
      },
      {
        label: 'Woodland Field Utility',
        prompt:
          'beige utility jumpsuit, cropped button-up utility jacket, elastic cropped waist, flap chest pockets, metal snap buttons, beige high-waisted cargo jogger pants, tapered ankle cuffs, side cargo pocket, soft twill fabric, white sporty sneakers, white ankle socks, black smartwatch',
      },
    ],
  },
  {
    id: 'outfit-casual',
    label: '의상·캐주얼 (Casual)',
    href: 'https://arca.live/b/aiart/172954099',
    note: '편안한 일상 컨셉 아웃핏 (채씨 프리셋)',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Pink Ribbed City',
        prompt:
          'pale pink cropped sweater, cropped turtleneck sweater, soft knit texture, pale pink low-rise shorts, silver chain belt, white ankle boots, pink collar choker, pink leather collar',
      },
      {
        label: 'Olive Bomber',
        prompt:
          'olive green bomber jacket, zip-up flight jacket, ribbed cuffs, white cropped tank top, white bandeau crop top, llight blue skinny jeans, white thong, black backpack, satin-like bomber texture',
      },
      {
        label: 'Denim Jacket Slim',
        prompt:
          'black cropped denim jacket, white contrast stitching, silver zipper details, snap button lapels, white ribbed crop tank top, black high-waist hot pants, black micro shorts, white pointed mule heels, small hoop earrings, delicate pendant necklace, ring',
      },
      {
        label: 'Red Mini Padded',
        prompt:
          'black zip-up jacket, black bomber jacket, red fitted mini dress, red bodycon mini dress, scoop neck red dress, large silver hoop earrings, black strappy high heels, black open-toe heels',
      },
      {
        label: 'Towel Wrap Spa',
        prompt:
          'white bath towel wrap, strapless white towel dress, white terry cloth towel, front twist towel wrap, delicate pendant necklace, silver ring',
      },
      {
        label: 'Ivory Varsity Denim',
        prompt:
          'white lightweight bomber jacket, reflective sleeve stripe detail, red cropped tank top, red racerback crop top, llight blue denim mini skirt, white thigh-high athletic socks, white platform sneakers, large silver hoop earrings, white leather backpack',
      },
      {
        label: 'Black Oversized Graphic Tee',
        prompt:
          'oversized black graphic T-shirt, washed black band T-shirt, loose short-sleeve tee, purple and white graphic print, red string thong, red high-cut thong, thin side straps',
      },
      {
        label: 'Cow Print Slim',
        prompt:
          'white cropped baby tee, cow print crop top, black and white cow pattern shirt, black high-cut thong, black strappy underwear, silver strap adjusters, minimal black lingerie bottom',
      },
      {
        label: 'White Cami Denim Shorts',
        prompt:
          'white cropped camisole top, white ribbed crop cami, thin spaghetti straps, llight blue denim shorts, high-waist denim shorts, white thigh-high socks, pearl drop earrings, delicate pearl necklace, silver chain necklace',
      },
      {
        label: 'Sky Denim Overalls',
        prompt:
          'blue denim shortalls, bib overalls, front bib pocket, side button detail, silver overall buckle clasps, white cropped peasant blouse, puff sleeves, elastic neckline, ruffled hem',
      },
      {
        label: 'Forest Knit Overalls',
        prompt:
          'dark blue denim short overalls, rolled hem shorts, silver overall buckle clasps, forest green ribbed long-sleeve top, scoop neckline, white knee-high socks, black chunky platform mary jane shoes, delicate silver pendant necklace',
      },
      {
        label: 'Sunset Kitty Casual',
        prompt:
          'white oversized graphic T-shirt, white cartoon print tee, soft cotton fabric, black cropped capri pants, black mid-calf fitted pants, smooth stretch fabric, white low-top sneakers, clean casual sneakers',
      },
      {
        label: 'Sheer Lace Casual',
        prompt:
          'sheer white long-sleeve blouse, translucent chiffon fabric, soft draped wrap front, white lace-trim camisole, cream lace bralette detail, black ribbed leggings, black knee-high socks, black and white platform sneakers, clear oversized square glasses',
      },
      {
        label: 'Campus Grunge Casual',
        prompt:
          'oversized black hooded jacket, black zip-up windbreaker, cream button-up shirt, burgundy striped necktie, loose blue straight-leg jeans, light wash denim, cuffed jeans hem, white ankle socks, black and white lace-up sneakers, black rectangular sunglasses',
      },
      {
        label: 'Arctic Cozy Casual',
        prompt:
          'fur-trim hood, white hooded winter parka, long padded coat, faux fur hood trim, high collar, zipper pocket detail, black chunky knit turtleneck crop sweater, ribbed knit texture, llight blue skinny jeans, cropped denim pants, tan lace-up winter boots, small gold hoop earrings',
      },
      {
        label: 'Denim Utility Casual',
        prompt:
          'navy blue denim short-sleeve jumpsuit, dark blue button-front romper, collared denim shirt dress style, rolled sleeve cuffs, front chest pocket, cinched waist seam, tapered cropped pants, dark denim fabric, small gold necklace, thin bracelet, black wristwatch, black low-top sneakers, white sneaker soles',
      },
      {
        label: 'Soft Denim Casual',
        prompt:
          'gray cropped t-shirt, heather gray crop top, short sleeve cropped tee, raw hem detail, small rhinestone logo detail, llight blue high-waist jeans, loose straight-leg denim pants, distressed ripped knees, gold hoop earrings, turquoise blue beaded bracelet, white low-top sneakers, white platform sneakers',
      },
      {
        label: 'Mustard Mini Casual',
        prompt:
          'mustard yellow long-sleeve crop top, fitted cropped shirt, boat neckline, soft cotton fabric, white high-waist mini skirt, bodycon mini skirt, side slit detail, white ankle strap sandals, flat sandals, silver rings',
      },
      {
        label: 'Berry Lounge Casual',
        prompt:
          'maroon long-sleeve top, berry knit top, lattice neckline detail, crisscross neckline, knotted hem top, side-tie hem, loose-fit top, black short shorts, black lounge shorts, white wedge sandals, white platform wedge sandals, white strappy sandals',
      },
      {
        label: 'Cozy Cardigan Casual',
        prompt:
          'cream knee-length hooded cardigan, ivory open-knit long cardigan, loose crochet cardigan, open-front cardigan, chunky knit texture, large button detail, patch pockets, light lavender crew-neck t-shirt, soft cotton t-shirt, black panties, purple platform sandals',
      },
      {
        label: 'Rust Knit Casual',
        prompt:
          'rust ribbed knit bodycon mini dress, long-sleeve sweater dress, soft stretch knit fabric, fine ribbed texture, fitted jersey knit, high neckline, warm terracotta color, mini dress hem, black oval pendant necklace, silver chain necklace, black platform pumps, black high heels',
      },
      {
        label: 'Clean City Casual',
        prompt:
          'white fitted baby tee, white short-sleeve T-shirt, black trim crew neck, black sleeve trim, light gray baggy jeans, loose wide-leg denim pants, low-rise jeans, washed gray denim, black studded shoulder bag, thin silver necklace, black open-toe platform sandals',
      },
      {
        label: 'Studio Shirt Muse',
        prompt:
          'oversized white button-up shirt, loose white blouse, lightweight cotton fabric, relaxed fit, deep V neckline, black short shorts, minimalist dark bottoms, white ankle socks, silver wristwatch, delicate ring',
      },
      {
        label: 'Ditsy Knit Casual',
        prompt:
          'light gray knit sweater, heather gray long-sleeve pullover, black and white ditsy print mini skirt, flowy skirt, white lace panties, white low-top sneakers, white ankle socks',
      },
      {
        label: 'Lemon Denim Patio',
        prompt:
          'pale yellow cropped tank top, soft yellow sleeveless crop top, llight blue denim cutoff shorts, distressed frayed denim shorts, low-rise denim shorts, silver belly piercing, white strappy sandals, flat ankle-strap sandals',
      },
      {
        label: 'Mint Lounge Casual',
        prompt:
          'mint blue cropped jacket, off-shoulder draped jacket, raw hem cropped jacket, loose long sleeves, white ribbed tank top, scoop neck sleeveless top, cream high-waisted shorts, beige casual shorts, purple floral lace bra, sheer lace embroidery, navy purple lace panties, thin strap underwear, small drop earrings',
      },
      {
        label: 'White Bathrobe Lounge',
        prompt:
          'white bathrobe, open-front robe, plush terry fabric, relaxed lounge robe, bare legs, barefoot',
      },
    ],
  },
  {
    id: 'outfit-boudoir',
    label: '19+ 부두아르 의상 (Boudoir)',
    href: 'https://arca.live/b/aiart/172954099',
    note: '침실·란제리 컨셉 아웃핏 (채씨 프리셋)',
    modes: ['realistic', 'anime'],
    options: [
      {
        label: 'Burgundy Robe',
        prompt:
          'burgundy satin robe, silky wrap robe, open-front satin robe, black lace bra, black scalloped lace bralette, black strappy garter belt, black lace panties, black thong underwear, black sheer thigh-high stockings, black high heels, layered gold chain necklace, scalloped lace detail',
      },
      {
        label: 'Black Lace Couture',
        prompt:
          'black sheer mesh long-sleeve crop top, open chest cutout top, black lace bralette, black strappy harness bralette, black ruffled lace panties, black strappy side-tie thong, all-black lingerie set',
      },
      {
        label: 'Lace Corset Bodysuit',
        prompt:
          'black lace corset bodysuit, sheer floral lace panels, black satin trim, gold front hook closures, structured bustier cups, black lace fingerless gloves, layered pearl necklace, black choker with bow detail, nude garter stockings, black bow garter accents, black thigh-high suede boots, pointed toe stiletto boots',
      },
      {
        label: 'Stripe Knit Boudoir',
        prompt:
          'pink and black striped cropped knit sweater, chunky ribbed knit texture, oversized long sleeves, black ribbed cuffs, high-neck collar, pale pink striped bikini-style panties, thin side straps, small front bow detail, black stud earrings',
      },
      {
        label: 'Soft Floral Lingerie',
        prompt:
          'black spaghetti-strap camisole, fitted black cami top, pale pink floral thong underwear, delicate floral print, black semi-sheer knee-high socks, sheer nylon texture',
      },
      {
        label: 'Embroidered Garter Noir',
        prompt:
          'black strappy lingerie set, black strappy bra, sheer mesh bra cups, orange floral embroidery, embroidered lace lingerie, black strap harness detail, matching strappy panties, black garter belt, suspender straps, strappy thong back, sheer nude stockings, back-seam stockings, black patent platform pumps',
      },
      {
        label: 'Trench Teal Bodysuit',
        prompt:
          'beige trench coat, camel belted trench coat, black contrast trim, open-front trench coat, teal satin bodysuit, teal satin lingerie bodysuit, underwire cup detail, side cutout bodysuit, lace side panel, delicate gold necklace, sheer nude pantyhose, black pointed-toe pumps, black stiletto heels',
      },
      {
        label: 'Midnight Teal Lace',
        prompt:
          'dark teal velvet lingerie set, teal velvet bra, thin spaghetti straps, sheer teal lace panties, high-cut panties, velvet thong, matching teal lingerie, black patent stiletto heels, black ankle bracelet, soft velvet texture, sheer lace panels',
      },
      {
        label: 'White Shirt Siren',
        prompt:
          'oversized white button-up shirt, white long-sleeve dress shirt, loose open shirt, lightweight cotton fabric, red lace thong, red lace panties, sheer floral lace panels, thin red side straps',
      },
      {
        label: 'White Floral Mesh',
        prompt:
          'white sheer mesh lingerie set, white embroidered mesh bra, floral embroidery detail, white strappy thong panties, sheer mesh fabric, thin elastic straps, small silver ring hardware, pink beaded bracelet, silver ring',
      },
      {
        label: 'Black Cami Boudoir',
        prompt:
          'black spaghetti strap camisole, black low-cut cami top, soft stretch fabric, black fitted mini skirt, black bodycon skirt, purple beaded bracelet',
      },
      {
        label: 'Soft Lounge Basic',
        prompt:
          'light gray sleeveless tank dress, gray ribbed bodycon tank dress, scoop neck, knee-length fitted lounge dress, soft cotton ribbed texture, gray lace-trim underwear, floral print bikini panties, light gray scalloped waistband',
      },
      {
        label: 'Rose Satin Boudoir',
        prompt:
          'pale pink satin lingerie set, floral embroidery camisole, pink rose embroidery, cream lace trim, scalloped lace neckline, thin shoulder straps, sheer white satin overlay, delicate ribbon bow detail, matching floral embroidered panties, pink lace panties',
      },
      {
        label: 'Chrome Collar Swim',
        prompt:
          'black bikini bottoms, black cheeky brief bottoms, bold pink typography print, minimalist black swimwear, silver metallic collar choker, polished chrome collar, matching silver wrist cuffs',
      },
      {
        label: 'Lace Maid Boudoir',
        prompt:
          'white lace maid-style lingerie apron, sheer white lace mini apron, black lace bralette, black strappy bra top, multiple thin side straps, black lace trim, scalloped lace edging, black thong underwear, sheer white ruffle waist detail, white wrist cuffs, delicate ruffled cuffs',
      },
      {
        label: 'Pink Strap Noir',
        prompt:
          'black patent leather mini dress, fitted bustier-style mini dress, black shiny bodycon dress, thin pink shoulder straps, exposed neon pink strappy lingerie, visible pink strappy thong underwear, pink garter-style strap details, side-tie ribbon straps, rhinestone choker necklace, silver crystal collar, silver bracelet, black pointed-toe slingback pumps, black kitten heels',
      },
      {
        label: 'Neon Strap Lingerie',
        prompt:
          'neon pink strappy lingerie set, hot pink bra top, thin adjustable shoulder straps, pink strappy thong underwear, side-tie ribbon straps, garter-style waist straps, cut-out lingerie strap details, playful ribbon accents, silver wristwatch',
      },
      {
        label: 'Pink Varsity Boudoir',
        prompt:
          'pink varsity jacket, white faux leather sleeves, satin bomber jacket finish, black and white striped ribbed cuffs, cropped white v-neck crop top, white fitted tee, pink lace panties, floral lace texture, scalloped lace trim, small silver hoop earrings, gold heart pendant necklace',
      },
      {
        label: 'Emerald Cage Boudoir',
        prompt:
          'emerald green strappy cage bodysuit, structured bra top, intricate crisscross harness design, gold chain front detail, jewel pendant accent, matching green choker strap, black sheer thigh-high stockings, wide black stocking bands, gold high heels',
      },
      {
        label: 'Ivory Pajama Muse',
        prompt:
          'cream pajama blouse, ivory satin pajama top, black piping trim, black button detail, loose sleepwear shirt, off-shoulder drape, white floral print panties, soft cotton underwear, small pearl earrings',
      },
      {
        label: 'Torn Lace Maid',
        prompt:
          'bblack-and-white lace maid bodysuit, black corset-style bodice, white lace trim, white ribbon lace-up front, frilled shoulder straps, white apron detail, black sheer torn stockings, ripped thigh-high stockings, cutout stocking detail',
      },
      {
        label: 'Pinstripe Lounge Boudoir',
        prompt:
          'white pinstripe short-sleeve button-up shirt, unbuttoned shirt, cropped tie-front shirt hem, high-waisted black mini skirt, black panties, sheer black pantyhose, silky sheer tights, elegant pearl drop earrings, delicate gold bracelet, slim gold ring, black pointed-toe pumps, black ankle-strap heels',
      },
      {
        label: 'Cat Noir Boudoir',
        prompt:
          'black sheer mesh bra, black lingerie set, black choker collar, black cat-ear headband, black faux fur cat ears, black faux fur tail accessory, red and black fishnet thigh-high stockings, diamond fishnet pattern, black stocking cuffs, black patent stiletto heels',
      },
      {
        label: 'Snowflake Boudoir',
        prompt:
          'white puffer jacket, fully unzipped puffer jacket, oversized open jacket, black strappy bra, black minimal lingerie top, black strappy thong bottoms, delicate silver snowflake drop earrings, fine silver pendant necklace, slim silver bracelet',
      },
      {
        label: 'Leather Harness Boudoir',
        prompt:
          'black leather belt harness, wide black waist belt, double-buckle leather belt, black leather thigh garters, adjustable strap details, silver metal ring hardware, silver buckle accents, silver belly piercing, gold ring',
      },
      {
        label: 'Blue Floral Lingerie',
        prompt:
          'sky blue lace bra, sheer mesh cups, pink and lavender floral embroidery, 3D floral applique strap detail, scalloped lace trim, crisscross front detail, jewel charm center detail, matching sky blue lace panties, floral embroidered thong panties, double strap waistband, delicate sheer mesh fabric, minimal silver stud earrings, thin silver chain necklace, dainty silver bracelet',
      },
      {
        label: 'Plaid Noir Boudoir',
        prompt:
          'gray plaid cropped button-up shirt, unbuttoned front shirt, long sleeves, black strappy lingerie bra, black cutout bra, pleated collar choker detail, black high-waisted faux leather pants, unbuttoned waistband, black thong panties, white lace-top thigh-high stockings',
      },
    ],
  },
  {
    id: 'persona-eyebrows',
    label: '외형·눈썹 (Eyebrows)',
    href: 'https://huggingface.co/datasets/tpremoli/CelebA-attrs',
    note: '눈썹 모양',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Arched', prompt: 'elegantly arched eyebrows' },
      { label: 'Straight', prompt: 'straight flat eyebrows' },
      { label: 'Soft Arch', prompt: 'soft rounded eyebrows' },
      { label: 'Angular', prompt: 'sharp angular eyebrows' },
      { label: 'Thick Bold', prompt: 'thick bold eyebrows' },
      { label: 'Thin Refined', prompt: 'thin refined eyebrows' },
      { label: 'Feathered', prompt: 'feathered laminated natural eyebrows' },
      { label: 'Tapered', prompt: 'tapered eyebrows with a defined tail' },
      { label: 'High Arch', prompt: 'high dramatic arched eyebrows' },
      { label: 'Low Set', prompt: 'low-set relaxed straight eyebrows' },
      { label: 'Downturned', prompt: 'gently downturned soft eyebrows' },
      { label: 'Bushy Natural', prompt: 'full natural bushy eyebrows' },
    ],
  },
  {
    id: 'persona-eye-shape',
    label: '외형·눈매 (Eye Shape)',
    href: 'https://huggingface.co/datasets/tpremoli/CelebA-attrs',
    note: '눈 모양',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Almond', prompt: 'almond-shaped eyes' },
      { label: 'Round', prompt: 'large round eyes' },
      { label: 'Upturned', prompt: 'upturned cat-like eyes' },
      { label: 'Downturned', prompt: 'downturned puppy eyes' },
      { label: 'Hooded', prompt: 'hooded eyes' },
      { label: 'Monolid', prompt: 'monolid eyes' },
      { label: 'Double Eyelid', prompt: 'defined double-eyelid eyes' },
      { label: 'Wide Set', prompt: 'wide-set eyes' },
      { label: 'Deep Set', prompt: 'deep-set eyes' },
      { label: 'Narrow Fox', prompt: 'narrow sharp fox eyes' },
      { label: 'Big Doe', prompt: 'big round doe eyes with aegyo-sal' },
      { label: 'Sharp Corner', prompt: 'sharp almond eyes with defined corners' },
    ],
  },
  {
    id: 'persona-eye-color',
    label: '외형·눈동자색 (Eye Color)',
    href: 'https://huggingface.co/datasets/tpremoli/CelebA-attrs',
    note: '눈동자 색',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Dark Brown', prompt: 'dark brown eyes' },
      { label: 'Warm Brown', prompt: 'warm brown eyes' },
      { label: 'Hazel', prompt: 'hazel eyes' },
      { label: 'Amber', prompt: 'amber golden eyes' },
      { label: 'Honey', prompt: 'light honey-brown eyes' },
      { label: 'Emerald', prompt: 'emerald green eyes' },
      { label: 'Blue', prompt: 'clear blue eyes' },
      { label: 'Gray', prompt: 'cool gray eyes' },
      { label: 'Violet', prompt: 'violet eyes' },
      { label: 'Aqua', prompt: 'aqua teal eyes' },
      { label: 'Black', prompt: 'deep black eyes' },
      { label: 'Heterochromia', prompt: 'heterochromia, one brown and one blue eye' },
    ],
  },
  {
    id: 'persona-lip-shape',
    label: '외형·입술 모양 (Lip Shape)',
    href: 'https://huggingface.co/datasets/tpremoli/CelebA-attrs',
    note: '입술 모양',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Full', prompt: 'full plump lips' },
      { label: 'Thin', prompt: 'thin delicate lips' },
      { label: 'Cupid Bow', prompt: "defined cupid's bow lips" },
      { label: 'Wide', prompt: 'wide expressive lips' },
      { label: 'Heart', prompt: 'small heart-shaped lips' },
      { label: 'Rounded', prompt: 'soft rounded lips' },
      { label: 'Bee Stung', prompt: 'bee-stung pouty lips' },
      { label: 'Downturned', prompt: 'subtly downturned lips' },
      { label: 'Balanced', prompt: 'balanced even lips' },
      { label: 'Defined Line', prompt: 'sharply defined lip line' },
    ],
  },
  {
    id: 'persona-lip-color',
    label: '외형·입술색 (Lip Color)',
    href: 'https://huggingface.co/datasets/tpremoli/CelebA-attrs',
    note: '입술 색',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Natural Nude', prompt: 'natural nude lips' },
      { label: 'Rose', prompt: 'soft rose lips' },
      { label: 'Coral', prompt: 'coral pink lips' },
      { label: 'Peach', prompt: 'peach nude lips' },
      { label: 'Cherry Red', prompt: 'cherry red lips' },
      { label: 'Classic Red', prompt: 'deep classic red lips' },
      { label: 'Berry', prompt: 'berry mauve lips' },
      { label: 'Brick', prompt: 'brick brown lips' },
      { label: 'Pink Gloss', prompt: 'glossy pink lips' },
      { label: 'Mauve', prompt: 'muted mauve lips' },
      { label: 'Wine', prompt: 'dark wine lips' },
      { label: 'Bare Pale', prompt: 'bare pale lips' },
    ],
  },
  {
    id: 'persona-hairstyle',
    label: '외형·헤어스타일 (Hairstyle)',
    href: 'https://huggingface.co/datasets/tpremoli/CelebA-attrs',
    note: '헤어스타일',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Long Straight', prompt: 'long straight hair' },
      { label: 'Long Wavy', prompt: 'long wavy hair' },
      { label: 'Loose Curls', prompt: 'long loose curls' },
      { label: 'Bob', prompt: 'chin-length bob haircut' },
      { label: 'Lob', prompt: 'shoulder-length lob' },
      { label: 'Pixie', prompt: 'short pixie cut' },
      { label: 'High Ponytail', prompt: 'high ponytail' },
      { label: 'Low Bun', prompt: 'low chignon bun' },
      { label: 'Half Up', prompt: 'half-up half-down hairstyle' },
      { label: 'Twin Braids', prompt: 'twin braided pigtails' },
      { label: 'Blunt Bangs', prompt: 'blunt straight bangs' },
      { label: 'Curtain Bangs', prompt: 'curtain bangs' },
      { label: 'See-through Bangs', prompt: 'wispy see-through bangs' },
      { label: 'Slicked Back', prompt: 'sleek slicked-back hair' },
      { label: 'Space Buns', prompt: 'double space buns' },
      { label: 'Layered Shag', prompt: 'layered shag cut' },
    ],
  },
  {
    id: 'persona-hair-color',
    label: '외형·머리색 (Hair Color)',
    href: 'https://huggingface.co/datasets/tpremoli/CelebA-attrs',
    note: '머리 색',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Jet Black', prompt: 'jet black hair' },
      { label: 'Dark Brown', prompt: 'dark brown hair' },
      { label: 'Chestnut', prompt: 'chestnut brown hair' },
      { label: 'Chocolate', prompt: 'warm chocolate brown hair' },
      { label: 'Ash Brown', prompt: 'ash brown hair' },
      { label: 'Honey Blonde', prompt: 'honey blonde hair' },
      { label: 'Platinum', prompt: 'platinum blonde hair' },
      { label: 'Ash Gray', prompt: 'ash gray hair' },
      { label: 'Auburn', prompt: 'auburn red hair' },
      { label: 'Copper', prompt: 'copper orange hair' },
      { label: 'Rose Pink', prompt: 'pastel rose-pink hair' },
      { label: 'Lavender', prompt: 'soft lavender hair' },
      { label: 'Mint', prompt: 'pastel mint hair' },
      { label: 'Blue Black', prompt: 'blue-black hair' },
      { label: 'Burgundy', prompt: 'burgundy wine hair' },
      { label: 'Ombre', prompt: 'dark-to-light ombre hair' },
    ],
  },
  {
    id: 'persona-skin-tone',
    label: '외형·피부톤 (Skin Tone)',
    href: 'https://huggingface.co/datasets/tpremoli/CelebA-attrs',
    note: '피부 톤',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Porcelain', prompt: 'porcelain fair skin' },
      { label: 'Fair Cool', prompt: 'fair skin with cool undertone' },
      { label: 'Light Warm', prompt: 'light skin with warm undertone' },
      { label: 'Ivory', prompt: 'ivory skin' },
      { label: 'Beige', prompt: 'neutral beige skin' },
      { label: 'Olive', prompt: 'olive skin' },
      { label: 'Golden Tan', prompt: 'golden tan skin' },
      { label: 'Honey', prompt: 'warm honey skin' },
      { label: 'Caramel', prompt: 'caramel skin' },
      { label: 'Bronze', prompt: 'bronze skin' },
      { label: 'Deep', prompt: 'rich deep skin' },
      { label: 'Espresso', prompt: 'deep espresso skin' },
    ],
  },
  {
    id: 'persona-figure',
    label: '외형·몸매 (Figure)',
    href: 'https://huggingface.co/datasets/tpremoli/CelebA-attrs',
    note: '체형',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Slim', prompt: 'slim slender figure' },
      { label: 'Petite', prompt: 'petite small frame' },
      { label: 'Athletic', prompt: 'toned athletic figure' },
      { label: 'Lean Fit', prompt: 'lean fit physique' },
      { label: 'Hourglass', prompt: 'hourglass figure' },
      { label: 'Curvy', prompt: 'curvy full figure' },
      { label: 'Tall Model', prompt: 'tall model proportions' },
      { label: 'Pear', prompt: 'pear-shaped figure' },
      { label: 'Broad Shoulder', prompt: 'broad-shouldered figure' },
      { label: 'Petite Curvy', prompt: 'petite curvy figure' },
      { label: 'Willowy', prompt: 'willowy tall slender figure' },
      { label: 'Soft Natural', prompt: 'soft natural figure' },
    ],
  },
  {
    id: 'pose-standing',
    label: '포즈·서있기 (Standing)',
    href: 'https://huggingface.co/datasets/raw-vitor/poses',
    note: '서있는 포즈',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Contrapposto', prompt: 'standing in contrapposto, weight on one leg' },
      { label: 'Hands on Hips', prompt: 'standing with hands on hips' },
      { label: 'Arms Crossed', prompt: 'standing with arms crossed' },
      { label: 'Hand in Pocket', prompt: 'standing with one hand in a pocket' },
      { label: 'Wall Lean', prompt: 'leaning back against a wall' },
      { label: 'Over-shoulder Glance', prompt: 'standing, glancing back over the shoulder' },
      { label: 'Hair Touch', prompt: 'standing, one hand touching the hair' },
      { label: 'Stretching', prompt: 'standing and stretching both arms upward' },
      { label: 'Hands Behind Back', prompt: 'standing with hands clasped behind the back' },
      { label: 'Walking Toward', prompt: 'walking toward the camera' },
      { label: 'Arms Raised', prompt: 'standing with both arms raised' },
      { label: 'Side Profile Stand', prompt: 'standing in a side profile pose' },
      { label: 'Back Turned', prompt: 'standing with back to camera, head turned' },
      { label: 'Casual Slouch', prompt: 'standing in a relaxed casual slouch' },
      { label: 'Power Stance', prompt: 'confident wide power stance' },
      { label: 'Tiptoe', prompt: 'standing on tiptoes, playful' },
    ],
  },
  {
    id: 'pose-sitting',
    label: '포즈·앉기·눕기 (Sitting)',
    href: 'https://huggingface.co/datasets/raw-vitor/poses',
    note: '앉은·눕는 포즈',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Cross-legged Floor', prompt: 'sitting cross-legged on the floor' },
      { label: 'Knees Up', prompt: 'sitting on the floor with knees drawn up' },
      { label: 'Chair Relaxed', prompt: 'sitting relaxed on a chair' },
      { label: 'Chair Straddle', prompt: 'sitting straddling a chair backwards' },
      { label: 'Perched Stool', prompt: 'perched on a high stool' },
      { label: 'Kneeling', prompt: 'kneeling upright on both knees' },
      { label: 'Crouching', prompt: 'crouching low, balanced on the toes' },
      { label: 'Lying Prone', prompt: 'lying prone, propped up on the elbows' },
      { label: 'Lying Back', prompt: 'lying on the back, relaxed' },
      { label: 'Side Recline', prompt: 'reclining on one side' },
      { label: 'Legs Crossed Sit', prompt: 'sitting with legs crossed' },
      { label: 'Curled Up', prompt: 'curled up sitting, hugging the knees' },
      { label: 'Sitting Edge', prompt: 'sitting on the edge of a surface, legs dangling' },
      { label: 'Squat', prompt: 'squatting playfully' },
      { label: 'Kneeling Lean', prompt: 'kneeling and leaning back on the hands' },
      { label: 'Floor Sprawl', prompt: 'sitting sprawled on the floor casually' },
    ],
  },
  {
    id: 'pose-action',
    label: '포즈·동작 (Action)',
    href: 'https://huggingface.co/datasets/raw-vitor/poses',
    note: '역동적 동작',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Running', prompt: 'running mid-stride' },
      { label: 'Jumping', prompt: 'jumping in mid-air' },
      { label: 'Spinning', prompt: 'spinning with motion' },
      { label: 'Dancing', prompt: 'dancing dynamically' },
      { label: 'Mid-stride Walk', prompt: 'captured mid-stride while walking' },
      { label: 'Reaching Up', prompt: 'reaching upward dynamically' },
      { label: 'High Kick', prompt: 'high kick in motion' },
      { label: 'Twirl Dress', prompt: 'twirling with the dress flaring out' },
      { label: 'Hair Flip', prompt: 'dynamic hair flip in motion' },
      { label: 'Leaping', prompt: 'leaping forward energetically' },
      { label: 'Throwing', prompt: 'throwing motion, arm extended' },
      { label: 'Falling Back', prompt: 'falling backward dramatically' },
      { label: 'Wind Blown', prompt: 'standing as wind blows the hair and clothes' },
      { label: 'Skipping', prompt: 'skipping joyfully' },
      { label: 'Fighting Stance', prompt: 'dynamic martial-arts stance' },
      { label: 'Splash Motion', prompt: 'action motion with a water splash' },
    ],
  },
  {
    id: 'pose-hands',
    label: '포즈·손·제스처 (Gesture)',
    href: 'https://huggingface.co/datasets/raw-vitor/poses',
    note: '손·제스처',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Peace Sign', prompt: 'making a peace sign near the face' },
      { label: 'Waving', prompt: 'waving hello' },
      { label: 'Pointing', prompt: 'pointing toward the camera' },
      { label: 'Hand on Chin', prompt: 'resting the chin on a hand thoughtfully' },
      { label: 'Blowing Kiss', prompt: 'blowing a kiss' },
      { label: 'Finger Heart', prompt: 'making a finger heart' },
      { label: 'Framing Face', prompt: 'both hands framing the face' },
      { label: 'Adjusting Glasses', prompt: 'adjusting glasses with one hand' },
      { label: 'Hair Behind Ear', prompt: 'tucking hair behind the ear' },
      { label: 'Shush', prompt: 'one finger to the lips, shushing' },
      { label: 'Cheek Hands', prompt: 'both hands resting on the cheeks' },
      { label: 'Thumbs Up', prompt: 'giving a thumbs up' },
      { label: 'Hand Greeting', prompt: 'one hand raised in greeting' },
      { label: 'Covering Mouth', prompt: 'a hand covering the mouth, surprised' },
    ],
  },
  {
    id: 'camera-angle',
    label: '앵글·시점 (Angle)',
    href: 'https://huggingface.co/datasets/raw-vitor/poses',
    note: '카메라 앵글',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Eye Level', prompt: 'eye-level shot' },
      { label: 'High Angle', prompt: 'high-angle shot looking down' },
      { label: 'Low Angle', prompt: 'low-angle shot looking up' },
      { label: 'Birds Eye', prompt: "bird's-eye view from directly above" },
      { label: 'Worms Eye', prompt: "worm's-eye view from the ground" },
      { label: 'Dutch Tilt', prompt: 'dutch tilt with a tilted horizon' },
      { label: 'Over Shoulder', prompt: 'over-the-shoulder shot' },
      { label: 'Top Down', prompt: 'top-down overhead shot' },
      { label: 'Three Quarter', prompt: 'three-quarter angle view' },
      { label: 'Front Facing', prompt: 'straight front-facing shot' },
      { label: 'Profile Side', prompt: 'side profile shot' },
      { label: 'Back View', prompt: 'shot from behind, back view' },
      { label: 'Slight High', prompt: 'slightly elevated camera angle' },
      { label: 'Slight Low', prompt: 'slightly low camera angle' },
      { label: 'Hip Level', prompt: 'hip-level camera angle' },
      { label: 'Ground Level', prompt: 'ground-level camera angle' },
    ],
  },
  {
    id: 'camera-shot',
    label: '앵글·샷 사이즈 (Shot)',
    href: 'https://huggingface.co/datasets/raw-vitor/poses',
    note: '샷 사이즈·구도',
    modes: ['realistic', 'anime'],
    options: [
      { label: 'Extreme Close-up', prompt: 'extreme close-up shot of the face' },
      { label: 'Close-up', prompt: 'close-up shot' },
      { label: 'Medium Close-up', prompt: 'medium close-up shot' },
      { label: 'Bust Shot', prompt: 'bust shot, head and shoulders' },
      { label: 'Waist Up', prompt: 'waist-up shot' },
      { label: 'Cowboy Shot', prompt: 'cowboy shot from mid-thigh up' },
      { label: 'Medium Full', prompt: 'medium full shot from the knees up' },
      { label: 'Full Body', prompt: 'full body shot' },
      { label: 'Wide Shot', prompt: 'wide establishing shot' },
      { label: 'Extreme Wide', prompt: 'extreme wide shot with a small subject' },
      { label: 'Centered', prompt: 'centered symmetrical composition' },
      { label: 'Rule of Thirds', prompt: 'rule-of-thirds composition, off-center' },
      { label: 'Negative Space', prompt: 'composition with lots of negative space' },
      { label: 'Tight Crop', prompt: 'tight detail crop' },
    ],
  },
  {
    id: 'camera-lens',
    label: '앵글·렌즈 연출 (Lens)',
    href: 'https://huggingface.co/datasets/raw-vitor/poses',
    note: '렌즈·연출',
    modes: ['realistic', 'anime'],
    options: [
      { label: '85mm Portrait', prompt: 'shot on an 85mm portrait lens, shallow depth of field' },
      { label: '35mm', prompt: 'shot on a 35mm lens' },
      { label: '50mm', prompt: 'shot on a 50mm lens, natural perspective' },
      { label: 'Wide Angle', prompt: 'wide-angle lens with slight distortion' },
      { label: 'Fisheye', prompt: 'fisheye lens with strong distortion' },
      { label: 'Telephoto', prompt: 'telephoto lens, compressed background' },
      { label: 'Macro', prompt: 'macro lens, extreme detail' },
      { label: 'Tilt-shift', prompt: 'tilt-shift lens, miniature effect' },
      { label: 'Bokeh', prompt: 'creamy bokeh background, wide aperture' },
      { label: 'Motion Blur', prompt: 'motion blur conveying movement' },
      { label: 'Soft Focus', prompt: 'dreamy soft-focus lens' },
      { label: 'Anamorphic', prompt: 'anamorphic lens flare, cinematic' },
    ],
  },
]

// visual recipe source 를 카테고리로 묶어 탭으로 보여주기 위한 메타.
const RECIPE_CATEGORIES: { id: string; label: string; Icon: typeof UserRound }[] = [
  { id: 'persona', label: '인물·외형', Icon: UserRound },
  { id: 'character', label: '완성형 캐릭터', Icon: Users },
  { id: 'wardrobe', label: '의상·스타일', Icon: Shirt },
  { id: 'scene', label: '장면·무드', Icon: Clapperboard },
  { id: 'angle', label: '앵글·카메라', Icon: Camera },
  { id: 'pose', label: '포즈·동작', Icon: Move },
  { id: 'props', label: '소품', Icon: Package },
  { id: 'expression', label: '표정', Icon: Smile },
  { id: 'adult', label: '19+ 성인', Icon: Flame },
]

const SOURCE_CATEGORY: Record<string, string> = {
  'synthetic-characters': 'character',
  'complete-persona-realistic': 'character',
  'face-caption': 'persona',
  'reid-attributes': 'wardrobe',
  'human-caption-hq': 'character',
  'pedestrian-attributes': 'wardrobe',
  'anigame-persona': 'character',
  'complete-persona-anime': 'character',
  'danbooru-sfw': 'character',
  'anime-caption-danbooru': 'character',
  'wd-pixai-tags': 'character',
  'fashionpedia-deepfashion': 'wardrobe',
  'subculture-wardrobe': 'wardrobe',
  'cinematic-archetypes': 'scene',
  'scene-energy': 'scene',
  'anime-genre-hooks': 'scene',
  'camera-drama': 'angle',
  'camera-angles': 'angle',
  'motion-beats': 'pose',
  'pose-composition': 'pose',
  'gesture-framing': 'pose',
  'anime-action-fx': 'pose',
  'signature-props': 'props',
  'expression-drama': 'expression',
  'adult-editorial-glamour': 'adult',
  'adult-realistic-body': 'adult',
  'adult-realistic-exposure': 'adult',
  'adult-realistic-pose': 'adult',
  'adult-realistic-wardrobe': 'adult',
  'adult-anime-glamour': 'adult',
  'adult-anime-body': 'adult',
  'adult-anime-exposure': 'adult',
  'adult-anime-pose': 'adult',
  'adult-anime-wardrobe': 'adult',
  'outfit-girly': 'wardrobe',
  'outfit-glam': 'wardrobe',
  'outfit-street': 'wardrobe',
  'outfit-sporty': 'wardrobe',
  'outfit-office': 'wardrobe',
  'outfit-uniform': 'wardrobe',
  'outfit-casual': 'wardrobe',
  'outfit-boudoir': 'adult',
  'persona-eyebrows': 'persona',
  'persona-eye-shape': 'persona',
  'persona-eye-color': 'persona',
  'persona-lip-shape': 'persona',
  'persona-lip-color': 'persona',
  'persona-hairstyle': 'persona',
  'persona-hair-color': 'persona',
  'persona-skin-tone': 'persona',
  'persona-figure': 'persona',
  'pose-standing': 'pose',
  'pose-sitting': 'pose',
  'pose-action': 'pose',
  'pose-hands': 'pose',
  'camera-angle': 'angle',
  'camera-shot': 'angle',
  'camera-lens': 'angle',
}

function sourceCategory(id: string): string {
  return SOURCE_CATEGORY[id] ?? (id.startsWith('adult-') ? 'adult' : 'persona')
}

// 이 카테고리들은 "한 번에 하나만" 선택되도록 배타 처리한다.
// 의상을 바꾸면 이전 의상이 대체돼야 하고(완성형 캐릭터도 하나만), 반면
// 외형 세부(눈썹/눈매/눈동자색/입술/헤어/피부톤/몸매)는 서로 조합돼야 하므로 배타가 아니다.
const EXCLUSIVE_RECIPE_CATEGORIES = new Set(['wardrobe', 'character'])

// recipe 옵션 썸네일 경로. slug 규칙은 배치 생성기(gen_thumbs)와 반드시 동일해야 한다.
function recipeThumb(sourceId: string, label: string): string {
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
  return `/recipes/${sourceId}__${slug}.webp`
}

const VISUAL_RECIPE_FIELDS = {
  genders: ['female', 'male'],
  ages: [
    { label: 'late teen', min: 17, max: 19 },
    { label: 'early 20s', min: 20, max: 24 },
    { label: 'late 20s', min: 25, max: 29 },
    { label: '30s', min: 30, max: 39 },
    { label: '40s', min: 40, max: 49 },
  ],
  faceShapes: [
    'oval face',
    'soft round face',
    'heart-shaped face',
    'defined jawline',
    'gentle cheekbones',
    'narrow face',
    'full cheeks',
    'small pointed chin',
    'square jawline',
    'high cheekbones',
    'soft baby face',
    'long elegant face',
    'wide friendly face',
    'delicate V-line face',
  ],
  skin: [
    'clear fair skin',
    'warm beige skin',
    'light olive skin',
    'natural skin texture',
    'soft matte skin',
    'subtle freckles',
    'dewy skin',
    'light tan skin',
    'porcelain skin',
    'healthy warm undertone',
  ],
  hairColors: [
    'black hair',
    'dark brown hair',
    'ash brown hair',
    'soft chestnut hair',
    'blue-black hair',
    'warm auburn hair',
    'deep espresso hair',
    'matte black hair',
    'honey brown hair',
    'dark copper brown hair',
    'smoky gray-brown hair',
  ],
  hairStyles: [
    'straight bob cut',
    'long layered hair',
    'shoulder-length wavy hair',
    'low ponytail',
    'messy short hair',
    'side-parted medium hair',
    'soft bangs',
    'tidy pixie cut',
    'hime cut',
    'wolf cut',
    'short blunt bob',
    'long straight hair with curtain bangs',
    'half-up hairstyle',
    'loose bun with face-framing strands',
    'medium shag cut',
    'sleek ponytail',
    'short two-block haircut',
    'natural wavy medium hair',
  ],
  eyes: [
    'almond-shaped dark eyes',
    'large expressive eyes',
    'calm monolid eyes',
    'sharp focused eyes',
    'soft smiling eyes',
    'slightly upturned eyes',
    'deep-set dark eyes',
    'gentle hooded eyes',
    'bright round eyes',
    'sleepy relaxed eyes',
    'clear double-lidded eyes',
  ],
  bodyTypes: [
    'slim build',
    'slim-average build',
    'average build',
    'soft athletic build',
    'petite build',
    'tall lean build',
    'curvy build',
    'compact athletic build',
    'broad-shouldered build',
    'delicate narrow build',
    'healthy average build',
  ],
  expressions: [
    'gentle half-smile',
    'confident calm expression',
    'curious bright expression',
    'quiet serious expression',
    'playful smirk',
    'focused thoughtful gaze',
    'warm relaxed smile',
    'slightly shy smile',
    'cool distant gaze',
    'friendly open expression',
    'mischievous grin',
    'soft melancholic expression',
    'determined intense look',
    'surprised cheerful expression',
  ],
  distinguishingFeatures: [
    'small beauty mark under one eye',
    'faint freckles across the nose',
    'slightly asymmetrical smile',
    'dimples when smiling',
    'neat arched eyebrows',
    'straight thick eyebrows',
    'soft natural makeup',
    'clean no-makeup look',
    'subtle glossy lip tint',
    'light under-eye aegyo-sal',
    'small nose bridge',
    'defined cupid bow lips',
    'slightly tousled hair strands',
    'one ear tucked behind hair',
    'distinct side part',
  ],
  colorPalettes: [
    'muted neutral color palette',
    'black and white monochrome palette',
    'soft pastel color accents',
    'earth-tone outfit palette',
    'cool gray and navy palette',
    'warm ivory and brown palette',
    'minimal black outfit palette',
    'fresh spring color palette',
    'deep jewel-tone accent',
    'clean denim blue palette',
  ],
  realisticOutfits: [
    'minimal cream knit sweater and dark trousers',
    'oversized white shirt with tailored black slacks',
    'cropped denim jacket over a plain tee',
    'linen blouse with a muted green skirt',
    'black turtleneck under a charcoal blazer',
    'simple hoodie with a canvas tote bag',
    'soft cardigan over a fitted top',
    'streetwear bomber jacket with wide-leg pants',
    'clean café apron over a white shirt',
    'light trench coat over casual clothes',
    'black leather jacket over a fitted tee',
    'ribbed sleeveless top with high-waist jeans',
    'tailored beige suit with a simple inner top',
    'striped knit top with straight denim',
    'white blouse with a dark suspender skirt',
    'athleisure zip-up jacket with jogger pants',
    'minimal black dress with a cropped jacket',
    'utility overshirt with cargo pants',
    'soft oversized sweater with a pleated skirt',
    'clean lab coat over casual office wear',
    'vintage cardigan with loose denim pants',
    'modern hanbok-inspired jacket with simple trousers',
  ],
  animeOutfits: [
    'modern sailor-style school uniform',
    'oversized hoodie with short pleated skirt',
    'fantasy travel cloak with leather belt',
    'idol stage jacket with ribbon accents',
    'techwear jacket with utility straps',
    'pastel cardigan with bow tie',
    'witch academy robe with gold trim',
    'casual denim jacket with striped shirt',
    'frilled blouse with high-waist skirt',
    'simple tracksuit jacket with sneakers',
    'gothic lolita dress with lace details',
    'cyberpunk cropped jacket with neon trim',
    'fantasy knight light armor with cloak',
    'magical girl dress with star accessories',
    'oversized varsity jacket with headphones',
    'summer yukata with simple floral pattern',
    'detective coat with plaid scarf',
    'visual novel heroine cardigan and skirt',
    'battle academy uniform with utility belt',
    'street fashion layered skirt and boots',
    'idol rehearsal outfit with loose jacket',
    'retro 90s anime jacket with high-waist shorts',
  ],
  accessories: [
    'thin round glasses',
    'small silver earrings',
    'black hair ribbon',
    'canvas shoulder bag',
    'delicate necklace',
    'baseball cap',
    'wireless earbuds',
    'no visible accessories',
    'clear frame glasses',
    'silver hoop earrings',
    'silk scarf',
    'simple wristwatch',
    'layered necklace',
    'hair claw clip',
    'crossbody mini bag',
    'beanie hat',
    'single ear cuff',
    'camera strap',
    'book in hand',
    'iced coffee cup',
  ],
  poses: [
    'front-facing bust portrait',
    'three-quarter view portrait',
    'standing relaxed with one hand in pocket',
    'looking over shoulder',
    'arms loosely crossed',
    'holding a notebook',
    'adjusting glasses',
    'walking pose with natural motion',
    'sitting casually on a stool',
    'leaning against a wall',
    'turning back toward the camera',
    'hands clasped in front',
    'one hand brushing hair aside',
    'holding a phone loosely',
    'mid-step street portrait',
    'resting chin on one hand',
    'confident hand-on-hip fashion pose',
    'soft contrapposto standing pose',
    'seated editorial pose with one knee raised',
    'turning walk pose with fabric motion',
    'close beauty portrait with one hand near cheek',
    'runway stride pose',
    'hands behind back reserved pose',
    'leaning on a desk with relaxed elbows',
    'low-angle confident stance',
    'high-angle expressive portrait',
    'side-profile calm portrait',
    'adjusting collar or lapel',
  ],
  realisticSettings: [
    'plain studio backdrop',
    'quiet city street background',
    'minimal apartment interior',
    'cafe window seat background',
    'office hallway background',
    'soft rooftop evening background',
    'subway platform background',
    'university campus walkway',
    'small bookstore background',
    'neutral fashion lookbook background',
  ],
  lighting: [
    'soft window light',
    'overcast natural light',
    'warm golden-hour rim light',
    'clean studio lighting',
    'cinematic side light',
    'gentle backlight',
    'soft diffused beauty lighting',
    'cool morning light',
    'night street neon fill light',
    'bright editorial studio light',
  ],
  animeEyes: [
    'dark brown eyes',
    'amber eyes',
    'soft blue eyes',
    'green eyes',
    'violet eyes',
    'gray eyes',
    'gold eyes',
    'ruby red eyes',
    'teal eyes',
    'ice blue eyes',
    'heterochromia eyes',
  ],
  animeHairColors: [
    'black hair',
    'brown hair',
    'silver hair',
    'pink hair',
    'navy hair',
    'mint hair',
    'auburn hair',
    'lavender hair',
    'platinum blonde hair',
    'teal hair',
    'wine red hair',
    'two-tone hair',
  ],
  animeMotifs: [
    'clean shoujo character design',
    'modern webtoon character design',
    '90s anime-inspired character design',
    'detailed light novel character design',
    'stylish game character splash art',
    'soft slice-of-life anime design',
    'dramatic fantasy anime design',
    'crisp cel-shaded character design',
    'romantic school anime design',
    'urban anime fashion design',
  ],
}

function randomVisualRecipePrompt(style: Style | undefined, locks: PromptLocks) {
  const anime = style?.kind === 'anime'
  const gender = locks.gender === 'auto' ? pick(VISUAL_RECIPE_FIELDS.genders) : locks.gender
  const lockedAge = normalizePromptAge(locks.age)
  const age = lockedAge
    ? { label: `${lockedAge} years old`, min: lockedAge, max: lockedAge }
    : pick(VISUAL_RECIPE_FIELDS.ages)
  const exactAge = lockedAge ?? randInt(age.min, age.max)
  const face = pick(VISUAL_RECIPE_FIELDS.faceShapes)
  const body = pick(VISUAL_RECIPE_FIELDS.bodyTypes)
  const expression = pick(VISUAL_RECIPE_FIELDS.expressions)
  const pose = pick(VISUAL_RECIPE_FIELDS.poses)
  const accessory = pick(VISUAL_RECIPE_FIELDS.accessories)
  const feature = pick(VISUAL_RECIPE_FIELDS.distinguishingFeatures)
  const palette = pick(VISUAL_RECIPE_FIELDS.colorPalettes)

  if (anime) {
    const hair = `${pick(VISUAL_RECIPE_FIELDS.animeHairColors)}, ${pick(VISUAL_RECIPE_FIELDS.hairStyles)}`
    return `${gender}, ${age.label}, ${body}, ${face}, ${hair}, ${pick(VISUAL_RECIPE_FIELDS.animeEyes)}, ${feature}, ${expression}, ${pick(VISUAL_RECIPE_FIELDS.animeOutfits)}, ${palette}, ${accessory}, ${pose}, ${pick(VISUAL_RECIPE_FIELDS.animeMotifs)}, solo single character, consistent appearance, no other people, no text`
  }

  const hair = `${pick(VISUAL_RECIPE_FIELDS.hairColors)}, ${pick(VISUAL_RECIPE_FIELDS.hairStyles)}`
  return `East Asian ${gender}, ${exactAge} years old, ${body}, ${face}, ${pick(VISUAL_RECIPE_FIELDS.skin)}, ${hair}, ${pick(VISUAL_RECIPE_FIELDS.eyes)}, ${feature}, ${expression}, wearing ${pick(VISUAL_RECIPE_FIELDS.realisticOutfits)}, ${palette}, ${accessory}, ${pose}, ${pick(VISUAL_RECIPE_FIELDS.realisticSettings)}, ${pick(VISUAL_RECIPE_FIELDS.lighting)}, realistic character portrait, solo single character, consistent appearance, no other people, no text`
}

function stripAdultPromptConflicts(prompt: string): string {
  return normalizePromptText(
    prompt
      .replace(/\b(late teen|teenage|teen)\b/gi, '')
      .replace(/\b(1[7-9]) years old\b/gi, '')
      .replace(/\bmodern sailor-style school uniform\b/gi, '')
      .replace(/\bschool uniform\b/gi, '')
      .replace(/\bvisual novel heroine cardigan and skirt\b/gi, ''),
  )
}

// Adult(19+) recipe 는 항상 성인 나이 마커를 유지해야 한다.
// gender lock 만 걸고 age 를 비우면 stripPromptDemographics 가 "adult woman, 25+ years old"
// 를 지워버려 노출/누드 프롬프트에 성인 표기가 사라질 수 있어 이를 방지한다.
function ensureAdultAgeMarker(prompt: string): string {
  if (/\b(?:1[89]|[2-9]\d)\s*\+?\s*years old\b/i.test(prompt)) return prompt
  if (/\bmature adult\b/i.test(prompt)) return prompt
  return normalizePromptParts(['mature adult, 25+ years old', prompt])
}

function applyPromptLocksToPrompt(
  prompt: string,
  locks: PromptLocks,
  mode: 'realistic' | 'anime',
): string {
  const lockPrompt = promptLockPrefix(locks, mode)
  const base = lockPrompt ? stripPromptDemographics(prompt) : prompt
  return ensureSingleCharacterGuard(normalizePromptParts([lockPrompt, base]))
}

// recipe source 는 "그룹별 믹스" 규칙:
//  - 같은 source(그룹) 안에서 다른 chip 을 누르면 그 source 문장만 교체
//  - 다른 source(그룹) chip 을 누르면 기존 선택은 유지한 채 append (섞어쓰기)
// previousSelections 로 직전 문장들을 base 에서 제거하고, nextSelections 전체를 다시 합친다.
function applyFeatureSelectionsToPrompt(
  current: string,
  previousSelections: Record<string, string>,
  nextSelections: Record<string, string>,
  sourceId: string,
  locks: PromptLocks,
  mode: 'realistic' | 'anime',
): string {
  let base = stripFeatureSelections(current, previousSelections)
  if (sourceId.startsWith('adult-')) base = stripAdultPromptConflicts(base)
  const joined = normalizePromptParts([base, ...Object.values(nextSelections)])
  const locked = applyPromptLocksToPrompt(joined, locks, mode)
  const anyAdult = Object.keys(nextSelections).some((id) => id.startsWith('adult-'))
  return anyAdult ? ensureAdultAgeMarker(locked) : locked
}

function stripFeatureSelections(prompt: string, selections: Record<string, string>): string {
  let next = prompt.trim()
  // 프롬프트에 실제로 남는 형태는 lock 여부에 따라 demographics/adult 정규화를 거친다.
  // 원문뿐 아니라 그 정규화 변형까지 지워야 같은 그룹 재선택 시 이전 문장이 남지 않는다.
  const variants = Array.from(
    new Set(
      Object.values(selections)
        .filter(Boolean)
        .flatMap((f) => [
          f,
          stripPromptDemographics(f),
          stripAdultPromptConflicts(f),
          stripPromptDemographics(stripAdultPromptConflicts(f)),
        ]),
    ),
  )
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
  for (const variant of variants) {
    next = next.split(variant).join('')
  }
  return normalizePromptText(next)
}

// 프롬프트 텍스트에서 어떤 visual recipe 옵션이 들어있는지 역추출한다.
// 캐릭터를 다시 선택하거나 생성 이미지를 클릭해 로드했을 때 selection 상태를
// 복원해야 (1) 픽커의 선택 표시가 유지되고 (2) 같은 그룹 재선택 시 교체가 동작한다.
// 저장된 프롬프트는 lock/adult 정규화를 거쳤을 수 있으므로 그 변형들도 함께 대조한다.
function deriveFeatureSelections(subject: string): Record<string, string> {
  const text = subject
  const out: Record<string, string> = {}
  if (!text.trim()) return out
  for (const source of VISUAL_FEATURE_SOURCES) {
    let bestPrompt = ''
    let bestLen = 0
    for (const option of source.options) {
      const f = option.prompt
      const variants = Array.from(
        new Set([
          f,
          stripPromptDemographics(f),
          stripAdultPromptConflicts(f),
          stripPromptDemographics(stripAdultPromptConflicts(f)),
        ]),
      ).filter((v) => v && v.length >= 12)
      for (const v of variants) {
        if (v.length > bestLen && text.includes(v)) {
          bestLen = v.length
          bestPrompt = f
        }
      }
    }
    if (bestPrompt) out[source.id] = bestPrompt
  }
  return out
}

function updatePromptLocksInPrompt(
  prompt: string,
  previousLocks: PromptLocks,
  nextLocks: PromptLocks,
  mode: 'realistic' | 'anime',
): string {
  const previousLockPrompt = promptLockPrefix(previousLocks, mode)
  const nextLockPrompt = promptLockPrefix(nextLocks, mode)
  if (!previousLockPrompt && !nextLockPrompt) return prompt
  const base = stripPromptDemographics(prompt)
  return applyPromptLocksToPrompt(base, nextLocks, mode)
}

function promptLockPrefix(locks: PromptLocks, mode: 'realistic' | 'anime'): string {
  const parts: string[] = []
  if (locks.gender !== 'auto') {
    parts.push(mode === 'realistic' ? `East Asian ${locks.gender}` : locks.gender)
  }
  const age = normalizePromptAge(locks.age)
  if (age) parts.push(`${age} years old`)
  return parts.join(', ')
}

function stripPromptDemographics(prompt: string): string {
  return normalizePromptText(
    prompt
      .replace(/\bEast Asian\s+(?:female|male|woman|man),?\s*/gi, '')
      .replace(
        /\b(?:mature adult anime woman|mature adult anime man|adult woman|adult man),?\s*/gi,
        '',
      )
      .replace(/\b(?:female|male),?\s*(?:late teen|early 20s|late 20s|30s|40s),?\s*/gi, '')
      .replace(/\b\d{1,2}\+?\s*years old,?\s*/gi, ''),
  )
}

function normalizePromptAge(age: string): number | undefined {
  const parsed = Number.parseInt(age.trim(), 10)
  if (!Number.isFinite(parsed)) return undefined
  if (parsed < 10 || parsed > 80) return undefined
  return parsed
}

function normalizePromptParts(parts: string[]): string {
  return normalizePromptText(
    parts
      .map((part) => part.trim())
      .filter(Boolean)
      .join(', '),
  )
}

function normalizePromptText(prompt: string): string {
  return prompt
    .replace(/\s*,\s*,+/g, ', ')
    .replace(/^\s*,\s*/g, '')
    .replace(/\s*,\s*$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

function ensureSingleCharacterGuard(prompt: string): string {
  const lower = prompt.toLowerCase()
  const guards: string[] = []
  if (!lower.includes('solo') && !lower.includes('single character')) {
    guards.push('solo single character')
  }
  if (!lower.includes('no other people') && !lower.includes('no multiple characters')) {
    guards.push('no other people')
  }
  return normalizePromptParts([prompt, ...guards])
}

function pick<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)]
}

function randInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

export default function Characters() {
  const editorRef = useRef<HTMLDivElement | null>(null)
  const [styleId, setStyleId] = useState<string | null>(null)
  const [name, setName] = useState('')
  // Prefill the prompt when opened with ?prompt=… (e.g. Factory story "세부설정").
  const [subject, setSubject] = useState(
    () => new URLSearchParams(window.location.search).get('prompt') ?? '',
  )
  const [aspect, setAspect] = useState<CharacterAspect>('portrait')
  const [seed, setSeed] = useState('')
  const [modelsOpen, setModelsOpen] = useState(true)
  const [modelQuery, setModelQuery] = useState('')
  const [kreaOpen, setKreaOpen] = useState(false)
  const [selectedCharacterId, setSelectedCharacterId] = useState<number | null>(null)
  const [loraQuery, setLoraQuery] = useState('')
  const [loraCategory, setLoraCategory] = useState('all')
  const [loraWithSampleOnly, setLoraWithSampleOnly] = useState(true)
  const [galleryStatusFilter, setGalleryStatusFilter] = useState<'all' | CharacterStatus>('all')
  const [galleryStyleFilter, setGalleryStyleFilter] = useState('all')
  const [galleryModelQuery, setGalleryModelQuery] = useState('')
  const [galleryPageSize, setGalleryPageSize] = useState('24')
  const [galleryPage, setGalleryPage] = useState(1)
  const [promptGender, setPromptGender] = useState<PromptGenderLock>('auto')
  const [promptAge, setPromptAge] = useState('')
  const [featureSelections, setFeatureSelections] = useState<Record<string, string>>({})
  // 생성 이미지 썸네일 전역 숨김(프라이버시). localStorage에 유지.
  const [hideThumbs, setHideThumbs] = useState<boolean>(
    () =>
      typeof window !== 'undefined' && localStorage.getItem('studio.characters.hideThumbs') === '1',
  )
  const toggleHideThumbs = () =>
    setHideThumbs((v) => {
      const next = !v
      try {
        localStorage.setItem('studio.characters.hideThumbs', next ? '1' : '0')
      } catch {
        /* ignore */
      }
      return next
    })
  // 개별 숨기기: 특정 캐릭터를 갤러리에서 감춘다(localStorage에 id 집합 유지).
  const [hiddenIds, setHiddenIds] = useState<Set<number>>(() => {
    if (typeof window === 'undefined') return new Set()
    try {
      const raw = localStorage.getItem('studio.characters.hiddenIds')
      return new Set(raw ? (JSON.parse(raw) as number[]) : [])
    } catch {
      return new Set()
    }
  })
  const [showHidden, setShowHidden] = useState(false)
  const toggleHiddenId = (id: number) =>
    setHiddenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      try {
        localStorage.setItem('studio.characters.hiddenIds', JSON.stringify([...next]))
      } catch {
        /* ignore */
      }
      return next
    })

  const stylesQ = useStyles()
  const kreaLorasQ = useKreaLoras()
  const charactersQ = useCharacters()
  const createM = useCreateCharacter()
  const retryM = useRetryCharacter()
  const deleteM = useDeleteCharacter()
  const updateM = useUpdateCharacter()

  const canSubmit =
    !!styleId && subject.trim().length > 0 && !createM.isPending && !retryM.isPending
  const selectedCharacter = charactersQ.data?.find((c) => c.id === selectedCharacterId)
  const selectedStyle = findStyle(stylesQ.data, styleId ?? '')
  const currentStyleName = styleId ? styleName(stylesQ.data, styleId, kreaLorasQ.data) : ''
  const currentFeatureMode: 'realistic' | 'anime' =
    selectedStyle?.kind === 'anime' ? 'anime' : 'realistic'
  const creating = createM.isPending || retryM.isPending
  const promptLocks = useMemo(
    () => ({ gender: promptGender, age: promptAge }),
    [promptAge, promptGender],
  )
  const applyRandomVisualRecipe = () => {
    setFeatureSelections({})
    setSubject(randomVisualRecipePrompt(selectedStyle, promptLocks))
  }
  const applySubjectFeature = (sourceId: string, feature: string) => {
    const next = feature.trim()
    if (!next) return
    // 같은 그룹(source)은 교체, 다른 그룹은 append 되도록 source별 선택을 누적한다.
    // 단, 배타 카테고리(의상 등)는 같은 카테고리의 다른 소스 선택을 제거해 하나만 유지한다.
    const previousSelections = featureSelections
    const cat = sourceCategory(sourceId)
    const nextSelections = { ...previousSelections }
    if (EXCLUSIVE_RECIPE_CATEGORIES.has(cat)) {
      for (const id of Object.keys(nextSelections)) {
        if (id !== sourceId && sourceCategory(id) === cat) delete nextSelections[id]
      }
    }
    nextSelections[sourceId] = next
    setFeatureSelections(nextSelections)
    setSubject((current) =>
      applyFeatureSelectionsToPrompt(
        current,
        previousSelections,
        nextSelections,
        sourceId,
        promptLocks,
        currentFeatureMode,
      ),
    )
  }
  const clearSubjectFeature = (sourceId: string) => {
    const previousSelections = featureSelections
    if (!(sourceId in previousSelections)) return
    const nextSelections = { ...previousSelections }
    delete nextSelections[sourceId]
    setFeatureSelections(nextSelections)
    setSubject((current) =>
      applyFeatureSelectionsToPrompt(
        current,
        previousSelections,
        nextSelections,
        sourceId,
        promptLocks,
        currentFeatureMode,
      ),
    )
  }
  const clearAllFeatures = () => {
    const previousSelections = featureSelections
    if (Object.keys(previousSelections).length === 0) return
    setFeatureSelections({})
    setSubject((current) =>
      applyFeatureSelectionsToPrompt(
        current,
        previousSelections,
        {},
        '',
        promptLocks,
        currentFeatureMode,
      ),
    )
  }
  const applyPromptLockChange = (nextLocks: PromptLocks) => {
    const previousLocks = promptLocks
    setPromptGender(nextLocks.gender)
    setPromptAge(nextLocks.age)
    setSubject((current) =>
      updatePromptLocksInPrompt(current, previousLocks, nextLocks, currentFeatureMode),
    )
  }
  const filteredStyles = useMemo(() => {
    const q = modelQuery.trim().toLowerCase()
    return (stylesQ.data ?? [])
      .filter((s) => !s.id.startsWith('krea-lora-') && !s.id.startsWith('krea:'))
      .filter((s) => {
        if (!q) return true
        const haystack = [
          s.id,
          s.name,
          s.kind,
          s.checkpoint,
          s.loras,
          s.category,
          s.trigger,
          styleDisplayName(s),
          ...parseLoras(s.loras),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return haystack.includes(q)
      })
  }, [modelQuery, stylesQ.data])
  const filteredCharacters = useMemo(() => {
    const items = charactersQ.data ?? []
    const q = galleryModelQuery.trim().toLowerCase()
    return items.filter((c) => {
      if (!showHidden && hiddenIds.has(c.id)) return false
      if (galleryStatusFilter !== 'all' && c.status !== galleryStatusFilter) return false
      if (galleryStyleFilter !== 'all' && c.style_id !== galleryStyleFilter) return false
      if (q) {
        const style = findStyle(stylesQ.data, c.style_id)
        const kreaStyle = findKreaStyle(kreaLorasQ.data, c.style_id)
        const haystack = [
          c.style_id,
          styleName(stylesQ.data, c.style_id, kreaLorasQ.data),
          style?.id,
          style?.name,
          style?.kind,
          style?.checkpoint,
          style?.loras,
          style?.category,
          style?.trigger,
          style ? styleDisplayName(style) : '',
          ...(style ? parseLoras(style.loras) : []),
          kreaStyle?.slug,
          kreaStyle?.lora,
          kreaStyle?.category,
          kreaStyle?.trigger,
          ...(kreaStyle?.tags ?? []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!haystack.includes(q)) return false
      }
      return true
    })
  }, [
    charactersQ.data,
    galleryModelQuery,
    galleryStatusFilter,
    galleryStyleFilter,
    kreaLorasQ.data,
    stylesQ.data,
    hiddenIds,
    showHidden,
  ])
  const galleryPageSizeNumber = Number(galleryPageSize) || 24
  const galleryPageCount = Math.max(1, Math.ceil(filteredCharacters.length / galleryPageSizeNumber))
  const clampedGalleryPage = Math.min(galleryPage, galleryPageCount)
  const visibleCharacters = filteredCharacters.slice(
    (clampedGalleryPage - 1) * galleryPageSizeNumber,
    clampedGalleryPage * galleryPageSizeNumber,
  )
  const galleryStyleOptions = useMemo(() => {
    const seen = new Set<string>()
    const items = (charactersQ.data ?? [])
      .filter((c) => {
        if (seen.has(c.style_id)) return false
        seen.add(c.style_id)
        return true
      })
      .map((c) => ({
        label: styleName(stylesQ.data, c.style_id, kreaLorasQ.data),
        value: c.style_id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
    return [{ label: '전체 모델 / LoRA', value: 'all' }, ...items]
  }, [charactersQ.data, kreaLorasQ.data, stylesQ.data])

  const submit = (input: {
    style_id: string
    name?: string
    subject_prompt: string
    aspect: CharacterAspect
    seed?: number
  }) => {
    createM.mutate(input)
  }

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!styleId || !subject.trim() || createM.isPending) return
    const parsedSeed = seed.trim() ? Number(seed.trim()) : undefined
    submit({
      style_id: styleId,
      name: name.trim() || undefined,
      subject_prompt: applyPromptLocksToPrompt(subject.trim(), promptLocks, currentFeatureMode),
      aspect,
      seed: Number.isFinite(parsedSeed) ? parsedSeed : undefined,
    })
  }

  const onRegenerate = (c: Character) => {
    if (createM.isPending || retryM.isPending) return
    if (c.status === 'error') {
      retryM.mutate(c.id)
      return
    }
    submit({
      style_id: c.style_id,
      subject_prompt: ensureSingleCharacterGuard(c.subject_prompt),
      aspect: c.aspect,
    })
  }

  const useCharacterSettings = (c: Character, nextSubject = c.subject_prompt) => {
    setStyleId(c.style_id)
    setName(c.name || '')
    setSubject(nextSubject)
    setAspect(c.aspect)
    setSeed('')
    setFeatureSelections(deriveFeatureSelections(nextSubject))
    setModelsOpen(false)
    setKreaOpen(false)
  }

  const onSelectCharacter = (c: Character) => {
    setSelectedCharacterId(c.id)
    useCharacterSettings(c)
  }

  const selectStyleForMobile = (id: string) => {
    setStyleId(id)
    // 모델만 바뀌고 프롬프트는 유지되므로 선택상태도 프롬프트에서 다시 도출해 유지한다.
    setFeatureSelections(deriveFeatureSelections(subject))
    if (typeof window === 'undefined' || window.innerWidth >= 1280) return
    setModelsOpen(false)
    setKreaOpen(false)
    window.setTimeout(() => {
      editorRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }, 80)
  }

  return (
    <div className="space-y-5 pb-24 xl:pb-0">
      <header className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-foreground">Characters</h1>
        <span className="text-sm text-muted-foreground">{charactersQ.data?.length ?? 0}개</span>
      </header>

      <div className="grid gap-5 xl:grid-cols-[minmax(540px,0.9fr)_minmax(0,1.1fr)] 2xl:grid-cols-[minmax(600px,0.9fr)_minmax(0,1.1fr)]">
        <aside className="order-1 flex flex-col gap-4 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:self-start xl:overflow-y-auto xl:pr-1">
          <div ref={editorRef} className="order-3 scroll-mt-3 xl:order-1">
            <CharacterEditorPanel
              selectedCharacter={selectedCharacter}
              style={selectedStyle}
              kreaStyle={findKreaStyle(kreaLorasQ.data, styleId ?? '')}
              styleName={currentStyleName}
              name={name}
              onNameChange={setName}
              subject={subject}
              onSubjectChange={setSubject}
              aspect={aspect}
              onAspectChange={setAspect}
              seed={seed}
              onSeedChange={setSeed}
              onSubmit={onSubmit}
              canSubmit={canSubmit}
              creating={creating}
              createError={
                createM.isError
                  ? errorMessage(createM.error)
                  : retryM.isError
                    ? `재시도 실패: ${errorMessage(retryM.error)}`
                    : undefined
              }
              promptGender={promptGender}
              promptAge={promptAge}
              onPromptGenderChange={(gender) => applyPromptLockChange({ gender, age: promptAge })}
              onPromptAgeChange={(age) => applyPromptLockChange({ gender: promptGender, age })}
              onRandomVisual={applyRandomVisualRecipe}
              featureMode={currentFeatureMode}
              featureSelections={featureSelections}
              onApplyFeature={applySubjectFeature}
              onClearFeature={clearSubjectFeature}
              onClearAllFeatures={clearAllFeatures}
              onRename={(nextName) => {
                if (!selectedCharacter) return
                updateM.mutate({ id: selectedCharacter.id, input: { name: nextName } })
              }}
              renaming={updateM.isPending}
              renameError={updateM.isError ? errorMessage(updateM.error) : undefined}
            />
          </div>

          <div className="order-1 xl:order-2">
            <CollapsibleSection
              title="모델 선택"
              count={filteredStyles.length}
              open={modelsOpen}
              onOpenChange={setModelsOpen}
              summary={
                styleId && !styleId.startsWith('krea:')
                  ? styleName(stylesQ.data, styleId, kreaLorasQ.data)
                  : undefined
              }
            >
              {stylesQ.isLoading ? (
                <p className="text-sm text-muted-foreground">불러오는 중…</p>
              ) : stylesQ.isError ? (
                <p className="text-sm text-destructive">로드 실패: {errorMessage(stylesQ.error)}</p>
              ) : !stylesQ.data || stylesQ.data.length === 0 ? (
                <Card>
                  <EmptyState
                    title="모델이 없어요."
                    description="styles 카탈로그가 비어 있습니다."
                  />
                </Card>
              ) : (
                <Card>
                  <Card.Body className="space-y-3">
                    <Input
                      value={modelQuery}
                      onChange={(e) => setModelQuery(e.target.value)}
                      placeholder="검색: krea2, z-image, anima, pony, animagine, manmaru..."
                      aria-label="모델 검색"
                    />
                    {filteredStyles.length === 0 ? (
                      <EmptyState
                        title="조건에 맞는 모델이 없어요."
                        description="모델명, checkpoint, LoRA 이름으로 다시 검색해보세요."
                      />
                    ) : (
                      <div className="space-y-4">
                        {styleGroups(filteredStyles).map((group) => (
                          <div key={group.title} className="space-y-2">
                            <div className="flex items-center gap-2">
                              <h3 className="text-xs font-semibold text-muted-foreground">
                                {group.title}
                              </h3>
                              <Badge shape="square" variant="muted">
                                {group.items.length}
                              </Badge>
                            </div>
                            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                              {group.items.map((st) => (
                                <StyleCard
                                  key={st.id}
                                  style={st}
                                  selected={styleId === st.id}
                                  onSelect={() => selectStyleForMobile(st.id)}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
            </CollapsibleSection>
          </div>

          <div className="order-2 xl:order-3">
            <KreaLoraBrowser
              loading={kreaLorasQ.isLoading}
              error={kreaLorasQ.isError ? errorMessage(kreaLorasQ.error) : undefined}
              data={kreaLorasQ.data}
              selectedId={styleId ?? undefined}
              onSelect={(style) => selectStyleForMobile(style.id)}
              query={loraQuery}
              onQueryChange={setLoraQuery}
              category={loraCategory}
              onCategoryChange={setLoraCategory}
              withSampleOnly={loraWithSampleOnly}
              onWithSampleOnlyChange={setLoraWithSampleOnly}
              open={kreaOpen}
              onOpenChange={setKreaOpen}
            />
          </div>
        </aside>

        <section className="order-2 min-w-0 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-foreground">생성 갤러리</h2>
              <p className="text-[11px] text-muted-foreground">
                이미지와 사용한 style/checkpoint 정보를 함께 확인합니다.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {hiddenIds.size > 0 ? (
                <Button
                  type="button"
                  variant={showHidden ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setShowHidden((v) => !v)}
                  title={showHidden ? '숨긴 항목 감추기' : '숨긴 항목 보기'}
                >
                  {showHidden ? <Eye size={14} /> : <EyeOff size={14} />}
                  <span className="hidden sm:inline">숨긴 항목 {hiddenIds.size}</span>
                </Button>
              ) : null}
              <Button
                type="button"
                variant={hideThumbs ? 'primary' : 'secondary'}
                size="sm"
                onClick={toggleHideThumbs}
                title={hideThumbs ? '썸네일 다시 표시' : '생성 이미지 썸네일 숨기기'}
              >
                {hideThumbs ? <EyeOff size={14} /> : <Eye size={14} />}
                <span className="hidden sm:inline">
                  {hideThumbs ? '썸네일 숨김' : '썸네일 숨기기'}
                </span>
              </Button>
              <Badge shape="square" variant="muted">
                {filteredCharacters.length}/{charactersQ.data?.length ?? 0}
              </Badge>
            </div>
          </div>
          <div className="grid gap-2 rounded-md border border-border bg-card p-2 sm:grid-cols-[150px_minmax(180px,1fr)_minmax(180px,1fr)_120px_auto]">
            <Select
              value={galleryStatusFilter}
              onValueChange={(v) => {
                setGalleryStatusFilter(v as 'all' | CharacterStatus)
                setGalleryPage(1)
              }}
              options={GALLERY_STATUS_OPTIONS}
              className="h-8 text-xs"
              aria-label="생성 상태 필터"
            />
            <Select
              value={galleryStyleFilter}
              onValueChange={(v) => {
                setGalleryStyleFilter(v)
                setGalleryPage(1)
              }}
              options={galleryStyleOptions}
              className="h-8 text-xs"
              aria-label="모델 또는 LoRA 필터"
            />
            <Input
              value={galleryModelQuery}
              onChange={(e) => {
                setGalleryModelQuery(e.currentTarget.value)
                setGalleryPage(1)
              }}
              placeholder="모델명, checkpoint, LoRA 검색"
              className="h-8 text-xs"
              aria-label="생성 갤러리 모델 검색"
            />
            <Select
              value={galleryPageSize}
              onValueChange={(v) => {
                setGalleryPageSize(v)
                setGalleryPage(1)
              }}
              options={GALLERY_PAGE_SIZE_OPTIONS}
              className="h-8 text-xs"
              aria-label="페이지당 표시 개수"
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setGalleryStatusFilter('all')
                setGalleryStyleFilter('all')
                setGalleryModelQuery('')
                setGalleryPage(1)
              }}
              disabled={
                galleryStatusFilter === 'all' &&
                galleryStyleFilter === 'all' &&
                !galleryModelQuery.trim()
              }
            >
              필터 초기화
            </Button>
          </div>
          {charactersQ.isLoading ? (
            <p className="text-sm text-muted-foreground">불러오는 중…</p>
          ) : charactersQ.isError ? (
            <p className="text-sm text-destructive">로드 실패: {errorMessage(charactersQ.error)}</p>
          ) : !charactersQ.data || charactersQ.data.length === 0 ? (
            <Card>
              <EmptyState
                title="아직 생성된 캐릭터가 없어요."
                description="편집 패널에서 모델 또는 Krea2 LoRA와 프롬프트를 정한 뒤 첫 캐릭터를 만들어보세요."
              />
            </Card>
          ) : filteredCharacters.length === 0 ? (
            <Card>
              <EmptyState
                title="조건에 맞는 캐릭터가 없어요."
                description="상태 또는 모델 필터를 바꿔보세요."
              />
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-6">
                {visibleCharacters.map((c) => (
                  <CharacterCard
                    key={c.id}
                    character={c}
                    styleName={styleName(stylesQ.data, c.style_id, kreaLorasQ.data)}
                    displayName={characterDisplayName(c)}
                    selected={selectedCharacterId === c.id}
                    onSelect={() => onSelectCharacter(c)}
                    onRegenerate={() => onRegenerate(c)}
                    regenerating={createM.isPending || retryM.isPending}
                    deleting={deleteM.isPending}
                    hideImage={hideThumbs}
                    hidden={hiddenIds.has(c.id)}
                    onToggleHidden={() => toggleHiddenId(c.id)}
                    onDelete={() => {
                      const msg =
                        c.status === 'generating'
                          ? '생성 중인 항목을 취소하고 삭제할까요? (멈춘 생성 정리)'
                          : '이 캐릭터를 삭제할까요? 되돌릴 수 없습니다.'
                      if (!window.confirm(msg)) return
                      deleteM.mutate(c.id)
                    }}
                  />
                ))}
              </div>
              {galleryPageCount > 1 ? (
                <Pagination
                  page={clampedGalleryPage}
                  totalPages={galleryPageCount}
                  onChange={setGalleryPage}
                />
              ) : null}
            </>
          )}
        </section>
      </div>
      <MobileCharacterActionBar
        styleName={currentStyleName}
        canSubmit={canSubmit}
        creating={creating}
        onRandomVisual={applyRandomVisualRecipe}
      />
    </div>
  )
}

function CollapsibleSection({
  title,
  count,
  summary,
  open,
  onOpenChange,
  actions,
  children,
}: {
  title: string
  count?: number
  summary?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  actions?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpenChange(!open)}
          className="flex min-w-0 items-center gap-2 text-left"
        >
          <ChevronDown
            size={15}
            className={cn(
              'shrink-0 text-muted-foreground transition-transform',
              open ? '' : '-rotate-90',
            )}
            aria-hidden="true"
          />
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {typeof count === 'number' ? (
            <Badge shape="square" variant="muted">
              {count}
            </Badge>
          ) : null}
          {summary ? (
            <span className="truncate text-[11px] text-muted-foreground">{summary}</span>
          ) : null}
        </button>
        {actions}
      </div>
      {open ? children : null}
    </section>
  )
}

function KreaLoraBrowser({
  loading,
  error,
  data,
  selectedId,
  onSelect,
  query,
  onQueryChange,
  category,
  onCategoryChange,
  withSampleOnly,
  onWithSampleOnlyChange,
  open,
  onOpenChange,
}: {
  loading: boolean
  error?: string
  data?: KreaLoraCatalog
  selectedId?: string
  onSelect: (style: KreaLoraStyle) => void
  query: string
  onQueryChange: (value: string) => void
  category: string
  onCategoryChange: (value: string) => void
  withSampleOnly: boolean
  onWithSampleOnlyChange: (value: boolean) => void
  open: boolean
  onOpenChange: (value: boolean) => void
}) {
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return (data?.styles ?? [])
      .filter((style) => (category === 'all' ? true : style.category === category))
      .filter((style) => (withSampleOnly ? Boolean(style.sample_url) : true))
      .filter((style) => {
        if (!q) return true
        const haystack = [
          style.name,
          style.slug,
          style.category,
          style.trigger,
          style.lora,
          ...(style.tags ?? []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return haystack.includes(q)
      })
  }, [category, data?.styles, query, withSampleOnly])
  const visible = filtered.slice(0, 120)
  const categoryOptions = [
    { label: 'All categories', value: 'all' },
    ...(data?.categories ?? []).map((c) => ({ label: c, value: c })),
  ]

  const selected = selectedId ? data?.styles.find((style) => style.id === selectedId) : undefined

  return (
    <CollapsibleSection
      title="Krea2 LoRA 선택"
      count={data ? filtered.length : undefined}
      open={open}
      onOpenChange={onOpenChange}
      summary={selected ? assetName(selected.lora) : undefined}
      actions={
        data?.source_url ? (
          <a
            href={data.source_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-7 items-center justify-center gap-1 rounded-md border border-border px-2 text-[11px] font-medium text-muted-foreground hover:border-primary/50 hover:text-foreground"
          >
            HF index <ExternalLink size={12} aria-hidden="true" />
          </a>
        ) : null
      }
    >
      <Card>
        <Card.Body className="space-y-3">
          <div className="grid gap-2 lg:grid-cols-[minmax(220px,1fr)_220px_auto]">
            <Input
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="검색: watercolor, manga, cinematic, wetplate..."
            />
            <Select value={category} onValueChange={onCategoryChange} options={categoryOptions} />
            <label className="inline-flex h-9 items-center gap-2 rounded-md border border-border px-3 text-[11px] font-medium text-muted-foreground">
              <input
                type="checkbox"
                checked={withSampleOnly}
                onChange={(e) => onWithSampleOnlyChange(e.target.checked)}
              />
              샘플 있는 항목만
            </label>
          </div>
          {loading ? (
            <p className="text-sm text-muted-foreground">LoRA catalog 불러오는 중...</p>
          ) : error ? (
            <p className="text-sm text-destructive">LoRA catalog 로드 실패: {error}</p>
          ) : !data || filtered.length === 0 ? (
            <EmptyState
              title="조건에 맞는 LoRA가 없어요."
              description="검색어나 카테고리 필터를 바꿔보세요."
            />
          ) : (
            <>
              <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                {visible.map((style) => (
                  <KreaLoraCard
                    key={style.id}
                    style={style}
                    selected={selectedId === style.id}
                    onSelect={() => onSelect(style)}
                  />
                ))}
              </div>
              {filtered.length > visible.length ? (
                <p className="text-center text-[11px] text-muted-foreground">
                  {visible.length}개 표시 중입니다. 검색어나 카테고리로 더 좁히세요.
                </p>
              ) : null}
            </>
          )}
          {data ? (
            <p className="text-[11px] text-muted-foreground">
              Comfy 호환 LoRA {data.count}개를 로컬 catalog로 보여줍니다. HF index는 style row{' '}
              {data.hf_style_count ?? data.count}개이며, original-only{' '}
              {data.original_only_count ?? 0}개는 Comfy 로드 대상에서 제외했습니다. Sample metadata{' '}
              {data.sample_metadata_count}개.
            </p>
          ) : null}
        </Card.Body>
      </Card>
    </CollapsibleSection>
  )
}

function KreaLoraCard({
  style,
  selected,
  onSelect,
}: {
  style: KreaLoraStyle
  selected: boolean
  onSelect: () => void
}) {
  const title = [style.name, assetName(style.lora), style.trigger, style.category]
    .filter(Boolean)
    .join(' · ')
  return (
    <button
      type="button"
      onClick={onSelect}
      title={title}
      className={cn(
        'group flex flex-col overflow-hidden rounded-md border bg-card text-left transition',
        selected ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/50',
      )}
    >
      <div className="relative flex aspect-square w-full items-center justify-center bg-muted">
        <Palette size={16} className="text-muted-foreground" aria-hidden="true" />
        {style.sample_url ? (
          <img
            src={style.sample_url}
            alt={style.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        ) : null}
        {style.category ? (
          <Badge
            shape="square"
            variant="muted"
            className="absolute left-1 top-1 max-w-[calc(100%-0.5rem)] truncate text-[8px] leading-none"
          >
            {style.category}
          </Badge>
        ) : null}
      </div>
      <p className="line-clamp-1 px-1.5 py-1 text-[10px] font-medium leading-tight text-card-foreground [overflow-wrap:anywhere]">
        {style.name}
      </p>
    </button>
  )
}

function StyleCard({
  style,
  selected,
  onSelect,
}: {
  style: Style
  selected: boolean
  onSelect: () => void
}) {
  const displayName = styleDisplayName(style)
  const imageSrc = style.sample_url || style.thumbnail_path || `/styles/${style.id}.png`
  // Caption = human name (the badge already shows the model family, so drop a
  // redundant leading family prefix). Falls back to displayName when unnamed.
  const caption = (style.name || displayName).replace(
    /^(SDXL Anime|Anime|Anima|실사 모델|실사)\s*·\s*/u,
    '',
  )
  const title = [style.name || displayName, style.checkpoint, style.category]
    .filter(Boolean)
    .join(' · ')
  return (
    <button
      type="button"
      onClick={onSelect}
      title={title}
      className={cn(
        'group flex flex-col overflow-hidden rounded-md border bg-card text-left transition',
        selected ? 'border-primary ring-1 ring-primary' : 'border-border hover:border-primary/50',
      )}
    >
      <div className="relative flex aspect-square w-full items-center justify-center bg-muted">
        <Palette size={16} className="text-muted-foreground" aria-hidden="true" />
        <img
          src={imageSrc}
          alt={displayName}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
        <span
          className={cn(
            'absolute left-1 top-1 max-w-[calc(100%-0.5rem)] truncate rounded px-1 py-px text-[8px] font-medium leading-none',
            style.kind === 'realistic'
              ? 'bg-warning-soft text-warning-soft-foreground'
              : 'bg-info-soft text-info-soft-foreground',
          )}
        >
          {styleBadge(style)}
        </span>
      </div>
      <p className="line-clamp-2 px-1 py-1 text-[10px] font-medium leading-tight text-card-foreground [overflow-wrap:anywhere]">
        {caption}
      </p>
    </button>
  )
}

function CharacterCard({
  character,
  styleName,
  displayName,
  selected,
  onSelect,
  onRegenerate,
  onDelete,
  regenerating,
  deleting,
  hideImage,
  hidden,
  onToggleHidden,
}: {
  character: Character
  styleName: string
  displayName: string
  selected: boolean
  onSelect: () => void
  onRegenerate: () => void
  onDelete: () => void
  regenerating: boolean
  deleting: boolean
  hideImage: boolean
  hidden: boolean
  onToggleHidden: () => void
}) {
  const c = character
  const showImage = c.status === 'ready' && !!c.image_path && !hideImage
  const imageContent = (
    <div className={cn('flex items-center justify-center bg-muted', aspectClass(c.aspect))}>
      {showImage ? (
        <img src={c.image_path} alt={c.subject_prompt} className="h-full w-full object-cover" />
      ) : hideImage && c.status === 'ready' ? (
        <div className="flex flex-col items-center gap-1 text-muted-foreground">
          <EyeOff size={22} aria-hidden="true" />
          <span className="text-[10px]">썸네일 숨김</span>
        </div>
      ) : c.status === 'error' ? (
        <div className="flex flex-col items-center gap-1 text-destructive">
          <ImageOff size={22} aria-hidden="true" />
          <span className="text-[10px]">생성 실패</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1 text-muted-foreground">
          <Loader2 size={22} className="animate-spin" aria-hidden="true" />
          <span className="text-[10px]">생성 중…</span>
        </div>
      )}
    </div>
  )
  return (
    <Card
      className={cn(
        'overflow-hidden transition',
        selected ? 'border-primary ring-1 ring-primary' : 'hover:border-primary/50',
        hidden && 'opacity-45',
      )}
    >
      {showImage ? (
        <a
          href={c.image_path}
          target="_blank"
          rel="noreferrer"
          className="block w-full"
          title="원본 이미지 보기"
        >
          {imageContent}
        </a>
      ) : (
        <button
          type="button"
          onClick={onSelect}
          className="block w-full text-left"
          title="생성 설정 보기"
        >
          {imageContent}
        </button>
      )}
      <Card.Body className="space-y-1 px-1.5 py-1.5 sm:space-y-1.5 sm:px-2 sm:py-2">
        <button
          type="button"
          onClick={onSelect}
          className="block w-full space-y-1 text-left sm:space-y-1.5"
        >
          <div className="flex min-w-0 items-center justify-between gap-1">
            <span className="line-clamp-2 min-w-0 break-words text-[11px] font-semibold leading-snug text-card-foreground [overflow-wrap:anywhere]">
              {displayName}
            </span>
            <Badge
              shape="square"
              variant={STATUS_VARIANT[c.status] ?? 'muted'}
              className="hidden shrink-0 text-[9px] sm:inline-flex"
            >
              {c.status}
            </Badge>
          </div>
          <p className="hidden break-words text-[10px] leading-snug text-muted-foreground [overflow-wrap:anywhere] sm:line-clamp-1">
            {styleName}
          </p>
        </button>
        <div className="flex items-center justify-between gap-1 sm:hidden">
          <Badge
            shape="square"
            variant={STATUS_VARIANT[c.status] ?? 'muted'}
            className="shrink-0 text-[9px]"
          >
            {c.status}
          </Badge>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onSelect}
              title="생성 설정 보기"
              className="h-7 px-1.5"
            >
              설정
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRegenerate}
              disabled={regenerating || deleting || c.status === 'generating'}
              aria-label="같은 화풍과 프롬프트로 재생성"
              title="같은 화풍·프롬프트로 재생성"
              className="h-7 px-1.5"
            >
              <RefreshCw size={13} />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onToggleHidden}
              aria-label={hidden ? '숨김 해제' : '갤러리에서 숨기기'}
              title={hidden ? '숨김 해제' : '갤러리에서 숨기기'}
              className="h-7 px-1.5"
            >
              {hidden ? <Eye size={13} /> : <EyeOff size={13} />}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={regenerating || deleting}
              aria-label={c.status === 'generating' ? '생성 취소·삭제' : '캐릭터 삭제'}
              title={c.status === 'generating' ? '생성 취소·삭제 (멈춘 생성 정리)' : '캐릭터 삭제'}
              className="h-7 px-1.5"
            >
              {c.status === 'generating' ? <X size={13} /> : <Trash2 size={13} />}
            </Button>
          </div>
        </div>
        <div className="hidden flex-wrap items-center justify-between gap-1.5 sm:flex">
          <button
            type="button"
            onClick={onSelect}
            className="font-mono text-[10px] text-muted-foreground hover:text-foreground"
            title="생성 설정 보기"
          >
            seed {c.seed} · {formatTime(c.updated_at)}
          </button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRegenerate}
            disabled={regenerating || deleting || c.status === 'generating'}
            title="같은 화풍·프롬프트로 재생성"
          >
            <RefreshCw size={13} /> 재생성
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onToggleHidden}
            title={hidden ? '숨김 해제' : '갤러리에서 숨기기'}
          >
            {hidden ? (
              <>
                <Eye size={13} /> 숨김 해제
              </>
            ) : (
              <>
                <EyeOff size={13} /> 숨기기
              </>
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            disabled={regenerating || deleting}
            title={c.status === 'generating' ? '생성 취소·삭제 (멈춘 생성 정리)' : '캐릭터 삭제'}
          >
            {c.status === 'generating' ? (
              <>
                <X size={13} /> 취소
              </>
            ) : (
              <>
                <Trash2 size={13} /> 삭제
              </>
            )}
          </Button>
        </div>
      </Card.Body>
    </Card>
  )
}

function CharacterEditorPanel({
  selectedCharacter,
  style,
  kreaStyle,
  styleName,
  name,
  onNameChange,
  subject,
  onSubjectChange,
  aspect,
  onAspectChange,
  seed,
  onSeedChange,
  onSubmit,
  canSubmit,
  creating,
  createError,
  promptGender,
  promptAge,
  onPromptGenderChange,
  onPromptAgeChange,
  onRandomVisual,
  featureMode,
  featureSelections,
  onApplyFeature,
  onClearFeature,
  onClearAllFeatures,
  onRename,
  renaming,
  renameError,
}: {
  selectedCharacter?: Character
  style?: Style
  kreaStyle?: KreaLoraStyle
  styleName: string
  name: string
  onNameChange: (value: string) => void
  subject: string
  onSubjectChange: (value: string) => void
  aspect: CharacterAspect
  onAspectChange: (value: CharacterAspect) => void
  seed: string
  onSeedChange: (value: string) => void
  onSubmit: (e: React.FormEvent) => void
  canSubmit: boolean
  creating: boolean
  createError?: string
  promptGender: PromptGenderLock
  promptAge: string
  onPromptGenderChange: (value: PromptGenderLock) => void
  onPromptAgeChange: (value: string) => void
  onRandomVisual: () => void
  featureMode: 'realistic' | 'anime'
  featureSelections: Record<string, string>
  onApplyFeature: (sourceId: string, feature: string) => void
  onClearFeature: (sourceId: string) => void
  onClearAllFeatures: () => void
  onRename: (name: string) => void
  renaming: boolean
  renameError?: string
}) {
  const loras = style ? parseLoras(style.loras) : kreaStyle ? [kreaStyle.lora] : []
  const checkpoint = style?.checkpoint ?? (kreaStyle ? 'krea2_turbo_nvfp4.safetensors' : '')
  const sourceURL = style?.source_url || kreaStyle?.style_url || kreaStyle?.source
  const selectedName = selectedCharacter ? characterDisplayName(selectedCharacter) : ''
  const nameChanged = !!selectedCharacter && name.trim() !== (selectedCharacter.name || '')

  return (
    <form
      id="character-editor-form"
      onSubmit={onSubmit}
      className="rounded-md border border-border bg-card shadow-sm"
    >
      <div className="space-y-3 p-3">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-normal text-muted-foreground">
                {selectedCharacter ? `editing #${selectedCharacter.id}` : 'new character'}
              </p>
              <p className="truncate text-sm font-semibold text-foreground">
                {selectedCharacter ? selectedName : '새 캐릭터 생성'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <FormRow label="성별 고정" stacked className="min-w-0 flex-1">
              <Select
                value={promptGender}
                onValueChange={(v) => onPromptGenderChange(v as PromptGenderLock)}
                options={PROMPT_GENDER_OPTIONS}
              />
            </FormRow>
            <FormRow label="나이 고정" stacked className="w-[76px] shrink-0">
              <Input
                value={promptAge}
                onChange={(e) => onPromptAgeChange(e.target.value)}
                inputMode="numeric"
                placeholder="자동"
              />
            </FormRow>
            <FormRow label="캐릭터 이름" stacked className="min-w-0 flex-1">
              <div className="flex gap-1">
                <Input
                  value={name}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder="예: 민지, 여주인공 A"
                  className="min-w-0 flex-1"
                />
                {selectedCharacter ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    loading={renaming}
                    disabled={renaming || !nameChanged}
                    onClick={() => onRename(name.trim())}
                    className="shrink-0"
                  >
                    저장
                  </Button>
                ) : null}
              </div>
            </FormRow>
            <div className="hidden shrink-0 xl:block">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={creating}
                disabled={!canSubmit}
              >
                <Wand2 size={14} /> {selectedCharacter ? '새로 생성' : '캐릭터 생성'}
              </Button>
            </div>
          </div>
          <Textarea
            value={subject}
            onChange={(e) => onSubjectChange(e.target.value)}
            rows={7}
            placeholder="예: elegant East Asian woman in her late 20s, oval face, soft black wavy hair, warm brown eyes, slim build, modern cafe outfit, gentle smile, standing pose, soft daylight"
            required
            className="min-h-44 font-mono text-[12px] leading-relaxed"
          />
          {createError ? (
            <p className="rounded border border-destructive-soft bg-destructive-soft p-2 text-[11px] text-destructive-soft-foreground">
              {createError}
            </p>
          ) : null}
        </div>

        {/* 생성 설정: 비율/Seed + generation 정보 (recipe sources 위로 이동, 한 섹션으로) */}
        <div className="min-w-0 border-t border-border pt-3">
          <div className="space-y-2 rounded-md border border-border bg-muted/30 p-2">
            <p className="text-[10px] font-semibold uppercase tracking-normal text-muted-foreground">
              생성 설정
            </p>
            <div className="grid grid-cols-2 gap-2">
              <FormRow label="비율" stacked>
                <Select
                  value={aspect}
                  onValueChange={(v) => onAspectChange(v as CharacterAspect)}
                  options={ASPECT_OPTIONS}
                />
              </FormRow>
              <FormRow label="Seed" stacked>
                <Input
                  value={seed}
                  onChange={(e) => onSeedChange(e.target.value)}
                  inputMode="numeric"
                  placeholder="random"
                />
              </FormRow>
            </div>
            <div className="grid grid-cols-2 gap-1 border-t border-border pt-2">
              <CompactDetail label="style" value={styleName || '선택 없음'} />
              <CompactDetail label="checkpoint" value={assetName(checkpoint || '-')} mono />
              <CompactDetail
                label="current seed"
                value={selectedCharacter ? `${selectedCharacter.seed}` : '-'}
                mono
              />
              <CompactDetail label="current aspect" value={selectedCharacter?.aspect ?? aspect} />
            </div>
            {loras.length > 0 ? (
              <div className="max-h-12 overflow-y-auto rounded-md border border-border bg-card p-1">
                <p className="mb-0.5 text-[9px] font-semibold uppercase tracking-normal text-muted-foreground">
                  lora
                </p>
                <div className="flex flex-wrap gap-1">
                  {loras.map((lora) => (
                    <span
                      key={lora}
                      className="rounded border border-border bg-muted/30 px-1 py-0.5 font-mono text-[9px] text-muted-foreground [overflow-wrap:anywhere]"
                    >
                      {lora}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            {kreaStyle?.trigger || style?.trigger ? (
              <p className="max-h-8 overflow-y-auto break-words rounded-md border border-border bg-card px-1.5 py-1 font-mono text-[9px] text-muted-foreground [overflow-wrap:anywhere]">
                trigger: {kreaStyle?.trigger || style?.trigger}
              </p>
            ) : null}
          </div>
          {renameError ? <p className="mt-1 text-[11px] text-destructive">{renameError}</p> : null}
        </div>

        {/* Visual recipe sources (collapsible) — 생성 설정 아래로 이동 */}
        <div className="min-w-0 border-t border-border pt-3">
          <VisualFeaturePicker
            mode={featureMode}
            selections={featureSelections}
            onApplyFeature={onApplyFeature}
            onClearFeature={onClearFeature}
            onClearAllFeatures={onClearAllFeatures}
            onRandomVisual={onRandomVisual}
          />
          {sourceURL ? (
            <a
              href={sourceURL}
              target="_blank"
              rel="noreferrer"
              className="mt-2 hidden h-7 w-fit items-center gap-1 rounded-md border border-border px-2 text-[11px] font-medium text-muted-foreground hover:border-primary/50 hover:text-foreground xl:inline-flex"
            >
              원본 보기 <ExternalLink size={12} aria-hidden="true" />
            </a>
          ) : null}
        </div>
      </div>
    </form>
  )
}

function VisualFeaturePicker({
  mode,
  selections,
  onApplyFeature,
  onClearFeature,
  onClearAllFeatures,
  onRandomVisual,
}: {
  mode: 'realistic' | 'anime'
  selections: Record<string, string>
  onApplyFeature: (sourceId: string, feature: string) => void
  onClearFeature: (sourceId: string) => void
  onClearAllFeatures: () => void
  onRandomVisual: () => void
}) {
  const sources = useMemo(
    () => VISUAL_FEATURE_SOURCES.filter((source) => source.modes.includes(mode)),
    [mode],
  )
  const selectedCount = Object.keys(selections).length
  const availableCategories = useMemo(
    () =>
      RECIPE_CATEGORIES.map((cat) => ({
        ...cat,
        count: sources.filter((s) => sourceCategory(s.id) === cat.id).length,
      })).filter((cat) => cat.count > 0),
    [sources],
  )
  const selectedByCategory = useMemo(() => {
    const map: Record<string, number> = {}
    for (const id of Object.keys(selections)) {
      const cat = sourceCategory(id)
      map[cat] = (map[cat] ?? 0) + 1
    }
    return map
  }, [selections])
  const [activeCat, setActiveCat] = useState(RECIPE_CATEGORIES[0].id)
  const [collapsed, setCollapsed] = useState(false)
  const effectiveCat =
    availableCategories.find((c) => c.id === activeCat)?.id ?? availableCategories[0]?.id
  const visibleSources = sources.filter((s) => sourceCategory(s.id) === effectiveCat)

  return (
    <div className="rounded-md border border-border bg-muted/30 p-2">
      <div className="mb-2 flex items-start justify-between gap-2">
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="flex min-w-0 items-start gap-1.5 text-left"
          aria-expanded={!collapsed}
          title={collapsed ? '펼치기' : '접기'}
        >
          <ChevronDown
            size={14}
            className={cn(
              'mt-0.5 shrink-0 text-muted-foreground transition',
              collapsed && '-rotate-90',
            )}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-normal text-muted-foreground">
              visual recipe sources
              {selectedCount > 0 ? (
                <span className="ml-1 text-primary">· {selectedCount}개 선택</span>
              ) : null}
            </p>
            {!collapsed ? (
              <p className="text-[10px] text-muted-foreground">
                썸네일로 미리보고 고르세요. 같은 그룹은 교체, 다른 그룹은 추가로 섞이고, 선택한
                태그(✓)를 다시 누르면 해제됩니다.
              </p>
            ) : null}
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-1">
          {selectedCount > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClearAllFeatures}
              title="선택 전체 해제"
            >
              <X size={14} /> 초기화
            </Button>
          ) : null}
          <Button type="button" variant="primary" size="sm" onClick={onRandomVisual}>
            <Dice5 size={14} /> 외형 랜덤
          </Button>
        </div>
      </div>

      {!collapsed ? (
        <>
          <div className="mb-2 flex flex-wrap gap-1">
            {availableCategories.map((cat) => {
              const active = cat.id === effectiveCat
              const picked = selectedByCategory[cat.id] ?? 0
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCat(cat.id)}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10px] font-medium transition',
                    active
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border bg-muted/40 text-muted-foreground hover:border-primary/50 hover:text-foreground',
                  )}
                >
                  <cat.Icon size={12} aria-hidden="true" />
                  {cat.label}
                  {picked > 0 ? (
                    <span className="inline-flex h-3.5 min-w-[0.875rem] items-center justify-center rounded-full bg-primary px-1 text-[8px] font-semibold leading-none text-primary-foreground">
                      {picked}
                    </span>
                  ) : (
                    <span
                      className={cn(
                        'rounded-full px-1 text-[9px] leading-none',
                        active ? 'bg-primary/20 text-foreground' : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {cat.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
            {visibleSources.map((source) => {
              const selectedPrompt = selections[source.id]
              return (
                <div
                  key={source.id}
                  className={cn(
                    'rounded-md border p-2',
                    selectedPrompt ? 'border-primary/60 bg-primary/5' : 'border-border bg-card',
                  )}
                >
                  <div className="mb-1.5 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-[11px] font-semibold text-card-foreground">
                        {source.label}
                      </p>
                      <p className="text-[10px] text-muted-foreground">{source.note}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {selectedPrompt ? (
                        <button
                          type="button"
                          onClick={() => onClearFeature(source.id)}
                          title="이 그룹 선택 해제"
                          className="inline-flex h-6 items-center justify-center gap-0.5 rounded-md border border-border px-1.5 text-[10px] font-medium text-muted-foreground hover:border-destructive/50 hover:text-destructive"
                        >
                          <X size={11} aria-hidden="true" /> 지우기
                        </button>
                      ) : null}
                      <a
                        href={source.href}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex h-6 items-center justify-center gap-1 rounded-md border border-border px-1.5 text-[10px] font-medium text-muted-foreground hover:border-primary/50 hover:text-foreground"
                      >
                        원본 <ExternalLink size={10} aria-hidden="true" />
                      </a>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-5 lg:grid-cols-6">
                    {source.options.map((option) => {
                      const active = selectedPrompt === option.prompt
                      return (
                        <button
                          key={`${source.id}:${option.label}`}
                          type="button"
                          onClick={() =>
                            active
                              ? onClearFeature(source.id)
                              : onApplyFeature(source.id, option.prompt)
                          }
                          title={active ? `${option.prompt}\n(다시 누르면 해제)` : option.prompt}
                          aria-pressed={active}
                          className={cn(
                            'group/tile relative flex flex-col overflow-hidden rounded-md border text-left transition',
                            active
                              ? 'border-primary ring-1 ring-primary'
                              : 'border-border hover:border-primary/50',
                          )}
                        >
                          <div className="relative aspect-square w-full bg-muted">
                            <img
                              src={recipeThumb(source.id, option.label)}
                              alt={option.label}
                              loading="lazy"
                              className="absolute inset-0 h-full w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.visibility = 'hidden'
                              }}
                            />
                            {active ? (
                              <span className="absolute right-0.5 top-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground shadow">
                                <Check size={10} aria-hidden="true" />
                              </span>
                            ) : null}
                          </div>
                          <span
                            className={cn(
                              'line-clamp-2 px-1 py-0.5 text-[9px] font-medium leading-tight [overflow-wrap:anywhere]',
                              active ? 'text-primary' : 'text-card-foreground',
                            )}
                          >
                            {option.label}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : null}
    </div>
  )
}

function MobileCharacterActionBar({
  styleName,
  canSubmit,
  creating,
  onRandomVisual,
}: {
  styleName: string
  canSubmit: boolean
  creating: boolean
  onRandomVisual: () => void
}) {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 px-3 pt-2 shadow-[0_-8px_24px_rgba(0,0,0,0.08)] backdrop-blur xl:hidden"
      style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto flex max-w-screen-sm items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-[11px] text-foreground">
            {styleName || '모델 또는 Krea2 LoRA 선택 필요'}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onRandomVisual}
          aria-label="외형 랜덤"
          title="외형 랜덤"
        >
          <Dice5 size={14} />
        </Button>
        <Button
          type="submit"
          form="character-editor-form"
          variant="primary"
          size="sm"
          loading={creating}
          disabled={!canSubmit}
          className="min-w-24 shrink-0"
        >
          <Wand2 size={14} /> 생성
        </Button>
      </div>
    </div>
  )
}

function CompactDetail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="min-w-0 rounded-md border border-border bg-muted/30 px-1.5 py-1">
      <p className="text-[9px] font-semibold uppercase tracking-normal text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          'truncate text-card-foreground',
          mono ? 'font-mono text-[9px]' : 'text-[10px]',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function aspectClass(aspect: CharacterAspect): string {
  switch (aspect) {
    case 'landscape':
      return 'aspect-[4/3]'
    case 'square':
      return 'aspect-square'
    default:
      return 'aspect-[3/4]'
  }
}

function styleName(styles: Style[] | undefined, id: string, kreaLoras?: KreaLoraCatalog): string {
  const style = findStyle(styles, id)
  if (style) return styleDisplayName(style)
  const kreaStyle = findKreaStyle(kreaLoras, id)
  return kreaStyle ? assetName(kreaStyle.lora) : id
}

function findStyle(styles: Style[] | undefined, id: string): Style | undefined {
  return styles?.find((s) => s.id === id)
}

function findKreaStyle(
  kreaLoras: KreaLoraCatalog | undefined,
  id: string,
): KreaLoraStyle | undefined {
  return kreaLoras?.styles.find((s) => s.id === id || `krea:${s.slug}` === id)
}

function characterDisplayName(c: Character): string {
  const name = c.name?.trim()
  return name ? `${name} (#${c.id})` : `#${c.id}`
}

function styleGroups(styles: Style[]) {
  const realistic = styles.filter(
    (s) => s.kind === 'realistic' && !s.id.startsWith('krea-lora-') && !s.id.startsWith('krea:'),
  )
  const anime = styles.filter((s) => s.kind === 'anime')
  return [
    { title: '실사 모델', items: realistic },
    { title: 'Anime 모델', items: anime },
  ].filter((g) => g.items.length > 0)
}

function styleBadge(style: Style): string {
  if (style.id.startsWith('krea-lora-')) return 'Krea LoRA'
  if (isAnimaStyle(style)) return 'Anima'
  if (isPonyStyle(style)) return 'Pony'
  if (isAnimagineStyle(style)) return 'Animagine'
  if (isManmaruStyle(style)) return 'Manmaru'
  if (isZukiStyle(style)) return 'Zuki'
  if (isKokioStyle(style)) return 'Kokio'
  if (isIllustriousStyle(style)) return 'Illustrious'
  if (isSdxlAnimeStyle(style)) return 'SDXL Anime'
  return style.kind === 'realistic' ? '실사' : 'Anime'
}

function styleHaystack(style: Style): string {
  return `${style.id} ${style.name} ${style.checkpoint} ${style.category ?? ''}`.toLowerCase()
}

function isAnimaStyle(style: Style): boolean {
  const haystack = styleHaystack(style)
  return /(^|[\s/_-])anima($|[\s/_.-])/.test(haystack) && !haystack.includes('animagine')
}

function isPonyStyle(style: Style): boolean {
  const haystack = styleHaystack(style)
  return haystack.includes('pony')
}

function isAnimagineStyle(style: Style): boolean {
  const haystack = styleHaystack(style)
  return haystack.includes('animagine')
}

function isManmaruStyle(style: Style): boolean {
  const haystack = styleHaystack(style)
  return haystack.includes('manmaru')
}

function isZukiStyle(style: Style): boolean {
  const haystack = styleHaystack(style)
  return haystack.includes('zuki')
}

function isKokioStyle(style: Style): boolean {
  const haystack = styleHaystack(style)
  return haystack.includes('kokio')
}

function isIllustriousStyle(style: Style): boolean {
  if (style.kind !== 'anime') return false
  const checkpoint = style.checkpoint.toLowerCase()
  return (
    checkpoint.includes('illustrious/') ||
    checkpoint.includes('waiillustrious') ||
    checkpoint.includes('novaanimexl')
  )
}

function isSdxlAnimeStyle(style: Style): boolean {
  if (style.kind !== 'anime') return false
  const checkpoint = style.checkpoint.toLowerCase()
  return (
    style.id.startsWith('sdxl-') ||
    checkpoint.includes('sdxl') ||
    checkpoint.includes('illustrious/') ||
    checkpoint.includes('waiillustrious') ||
    checkpoint.includes('novaanimexl') ||
    checkpoint.includes('pony/') ||
    checkpoint.includes('animagine') ||
    checkpoint.includes('manmaru') ||
    checkpoint.includes('kokio') ||
    checkpoint.includes('zuki')
  )
}

function styleDisplayName(style: Style): string {
  const loras = parseLoras(style.loras)
  if (style.id.startsWith('krea-lora-') && loras[0]) return assetName(loras[0])
  if (style.kind === 'anime') return assetName(style.checkpoint)
  return style.name
}

function assetName(path: string): string {
  const file = path.split('/').pop() ?? path
  return file.replace(/\.safetensors$/i, '')
}

function parseLoras(raw: string): string[] {
  const trimmed = raw.trim()
  if (!trimmed || trimmed === '[]') return []
  try {
    const parsed = JSON.parse(trimmed) as unknown
    if (Array.isArray(parsed)) {
      return parsed.filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    }
    if (typeof parsed === 'string' && parsed.trim()) return [parsed]
  } catch {
    return trimmed
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
  }
  return []
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'unknown error'
}
